import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  PlusIcon, 
  TrashIcon,
  GlobeAltIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  PlayIcon,
  StopIcon,
  PencilIcon,
  ArrowLeftOnRectangleIcon,
  ArrowTopRightOnSquareIcon,
  EllipsisVerticalIcon,
  FolderIcon,
  FolderPlusIcon,
  SparklesIcon,
  Bars3Icon,
  XMarkIcon,
  ClipboardDocumentIcon,
  CalendarIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  ClockIcon,
  DocumentTextIcon,
  LockOpenIcon,
  LockClosedIcon,
  KeyIcon,
  CpuChipIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../services/api';
import GoLoginJobProgressBar from '../components/GoLoginJobProgressBar';
import GroupStatusSidebarFilters, { sumLeafStatusCounts } from '../components/GroupStatusSidebarFilters';
import ModelParentSidebar, { groupsHiddenByModelParents } from '../components/ModelParentSidebar';
import toast from 'react-hot-toast';
import {
  accountPhoneDisplay,
  passesProfileFilter,
  profileFieldMissing
} from '../utils/accountProfileFilters';
import { clampUsernamePrefixInput, exampleUsernamesFromPrefix, USERNAME_PREFIX_HINT } from '../utils/usernameFromPrefix';
import { mergeBulkStartProgress } from '../utils/bulkStartProgress';

/** Matches backend automationWorker globalErrorStatuses — DB can be briefly inconsistent; UI must not show Active + Suspended. */
const BLOCKING_AUTOMATION_STATUSES = new Set([
  'captcha',
  'auth_expired',
  'suspended',
  'login_failed',
  'xchat_key_error'
]);

// Account cards stay mounted across progress ticks. Defining them inside
// Accounts() created a new component type on every socket update, which
// remounted the list and cleared any text selection.
const accountsPageRef = { current: {} };

const DailyLimitTimer = ({ pauseUntil, accountId }) => {
  const [timeRemaining, setTimeRemaining] = useState(null);
  const hasExpiredRef = React.useRef(false); // Track if we've already handled expiration

  useEffect(() => {
    // Reset expiration flag when pauseUntil changes (new pause period)
    hasExpiredRef.current = false;
    
    const updateTimer = () => {
      if (!pauseUntil) {
        setTimeRemaining(null);
        return;
      }

      const now = new Date().getTime();
      const pauseTime = new Date(pauseUntil).getTime();
      const diff = pauseTime - now;

      if (diff <= 0) {
        setTimeRemaining(null);
        // Only check expiry once when timer expires (not on every check)
        if (!hasExpiredRef.current) {
          hasExpiredRef.current = true;
          // Call backend to check and update expired pauseUntil
          api.post(`/accounts/${accountId}/check-pause-expiry`)
            .then(() => {
              // Use the same debounce logic as other socket-triggered fetches
              if (!accountsPageRef.current.isFetchingAccountsRef.current && !accountsPageRef.current.fetchAccountsTimeoutRef.current) {
                accountsPageRef.current.fetchAccountsTimeoutRef.current = setTimeout(() => {
                  accountsPageRef.current.fetchAccounts(false);
                  accountsPageRef.current.fetchAccountsTimeoutRef.current = null;
                }, 2000);
              }
            })
            .catch(error => {
              console.error('Error checking pause expiry:', error);
              // Still refresh accounts even if API call fails
              if (!accountsPageRef.current.isFetchingAccountsRef.current && !accountsPageRef.current.fetchAccountsTimeoutRef.current) {
                accountsPageRef.current.fetchAccountsTimeoutRef.current = setTimeout(() => {
                  accountsPageRef.current.fetchAccounts(false);
                  accountsPageRef.current.fetchAccountsTimeoutRef.current = null;
                }, 2000);
              }
            });
        }
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeRemaining({ hours, minutes, seconds });
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [pauseUntil, accountId]);

  if (!timeRemaining) {
    return null;
  }

  return (
    <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium ml-2" title={`Resumes in ${timeRemaining.hours}h ${timeRemaining.minutes}m ${timeRemaining.seconds}s`}>
      ({String(timeRemaining.hours).padStart(2, '0')}:{String(timeRemaining.minutes).padStart(2, '0')}:{String(timeRemaining.seconds).padStart(2, '0')})
    </span>
  );
};

// OPTIMIZED: Memoize AccountCard to prevent unnecessary re-renders
const AccountCard = React.memo(({ account, clearStatusesKey, selected, models: modelsProp }) => {
  const [loginTesting, setLoginTesting] = useState(false);
  const [loginStatus, setLoginStatus] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showActivityLogModal, setShowActivityLogModal] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);

  const openActivityLog = async () => {
    setShowActivityLogModal(true);
    setActivityLogsLoading(true);
    try {
      const res = await api.get(`/accounts/${account._id}/activity-logs?limit=200`);
      setActivityLogs(Array.isArray(res.data?.logs) ? res.data.logs : []);
    } catch (err) {
      console.error('Activity log fetch failed:', err);
      toast.error(err.response?.data?.error || 'Failed to load activity log');
      setActivityLogs([]);
    } finally {
      setActivityLogsLoading(false);
    }
  };

  const activityCategoryLabel = (cat) => {
    const map = {
      inbox_scan: 'Inbox scan',
      reply_sent: 'Reply sent',
      followback_dm_sent: 'Follow-back · DM sent',
      follow_sent: 'Follow task · Follow sent',
      account_status_http: 'Account status (HTTP)'
    };
    return map[cat] || cat;
  };
  
  // Helper function to format model name with displayName and AI name
  const formatModelName = (model) => {
    if (!model) return null;
    return model.displayName || model.name;
  };
  
  // Get model name for this account
  const getModelName = () => {
    if (!account.modelId) return null;
    
    // Handle both string ID and populated object
    const modelId = typeof account.modelId === 'object' ? account.modelId._id || account.modelId : account.modelId;
    
    const model = modelsProp.find(m => {
      const mId = typeof m._id === 'object' ? m._id.toString() : m._id;
      const aId = typeof modelId === 'object' ? modelId.toString() : modelId;
      return mId === aId || mId === String(aId);
    });
    
    if (!model) return null;
    return formatModelName(model);
  };
  
  const modelName = getModelName();

  // Clear login status when component mounts, account changes, or clearStatusesKey changes
  useEffect(() => {
    setLoginStatus(null);
  }, [account._id, clearStatusesKey]);

  // Function to clear login status
  const clearLoginStatus = () => {
    setLoginStatus(null);
  };

const handleLoginTest = async () => {
  setLoginTesting(true);
  setLoginStatus(null);
  
  try {
    // Increase timeout for login test to 60 seconds
    const response = await api.post(`/accounts/${account._id}/login-test`, {}, {
      timeout: 60000
    });
    
    setLoginStatus(response.data);
    
    // Update account status in parent state if login test succeeded
    if (response.data.success) {
      accountsPageRef.current.setAccounts(prevAccounts => 
        prevAccounts.map(acc => 
          acc._id === account._id 
            ? { ...acc, status: 'active', loginTestFailed: false }
            : acc
        )
      );
    } else {
      // Update loginTestFailed flag on failure
      accountsPageRef.current.setAccounts(prevAccounts => 
        prevAccounts.map(acc => 
          acc._id === account._id 
            ? { ...acc, loginTestFailed: true }
            : acc
        )
      );
    }
  } catch (error) {
    console.error('Login test error:', error);
    console.error('Error response:', error.response?.data);
    
    const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Login test failed';
    
    // Filter out redirect errors - don't show them to user
    if (errorMessage.includes('Maximum number of redirects exceeded')) {
      setLoginStatus({
        success: true,
        message: 'Login test completed (redirect issue resolved)'
      });
      // Update account status even for redirect "success"
      accountsPageRef.current.setAccounts(prevAccounts => 
        prevAccounts.map(acc => 
          acc._id === account._id 
            ? { ...acc, status: 'active', loginTestFailed: false }
            : acc
        )
      );
    } else {
      setLoginStatus({
        success: false,
        message: errorMessage
      });
      // Update loginTestFailed flag on failure
      accountsPageRef.current.setAccounts(prevAccounts => 
        prevAccounts.map(acc => 
          acc._id === account._id 
            ? { ...acc, loginTestFailed: true }
            : acc
        )
      );
    }
  } finally {
    setLoginTesting(false);
  }
};

const handleOpenSession = async () => {
  try {
    const response = await api.post(`/accounts/${account._id}/open-session`, {}, {
      timeout: 30000
    });
    
    if (response.data.success) {
      toast.success(`Chrome session opened for ${account.username}`);
    } else {
      toast.error(response.data.message || 'Failed to open Chrome session');
    }
  } catch (error) {
    toast.error(error.response?.data?.error || 'Failed to open Chrome session');
    console.error('Error opening Chrome session:', error);
  }
};

  return (
    <>
    <div className={`rounded-xl border shadow-sm hover:shadow-md transition-shadow overflow-hidden ${
      selected
        ? 'bg-primary-50 dark:bg-primary-900/10 border-primary-300 dark:border-primary-700'
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
    }`}>
      <div className="p-5">
        <div className="grid grid-cols-12 gap-6">
          {/* LEFT SIDE - Profile Details */}
          <div className="col-span-12 lg:col-span-8">
            <div className="flex items-start space-x-4">
              {/* Checkbox */}
              <div className="flex-shrink-0 pt-1" onClick={(e) => e.stopPropagation()}>
                <label className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      accountsPageRef.current.shiftKeyPressedRef.current = e.shiftKey || e.nativeEvent?.shiftKey || false;
                    }}
                    onChange={() => {
                      accountsPageRef.current.handleSelectAccount(String(account._id));
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                  />
                </label>
              </div>

              {/* Banner and Profile Section - Glassy Style */}
              <div className="flex-1 min-w-0 bg-white/60 dark:bg-gray-800/60 backdrop-blur-md rounded-xl p-3 border border-white/20 dark:border-gray-700/50 shadow-lg">
                {/* Full-width Banner - 25% smaller height */}
                <div className="relative w-full h-24 rounded-lg overflow-visible mb-3 shadow-lg border border-white/30 dark:border-gray-600/50">
                  {/* Gradient fallback */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 rounded-lg" />
                  
                  {account.profileBanner ? (
                    <img 
                      src={account.profileBanner} 
                      alt={`${account.username} banner`}
                      className="absolute inset-0 w-full h-full object-cover z-10 rounded-lg"
                      onError={(e) => {
                        console.error(`[Accounts] Failed to load banner for @${account.username}:`, {
                          url: account.profileBanner,
                          error: e
                        });
                        e.target.style.display = 'none';
                      }}
                      onLoad={() => {
                      }}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm z-10 font-medium rounded-lg">
                      No banner
                    </div>
                  )}

                  {/* Profile Avatar - Large, overlapping banner at bottom-left */}
                  <div className="absolute -bottom-6 left-4 w-24 h-24 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center border-4 border-white dark:border-gray-800 overflow-hidden shadow-xl z-20">
                    {account.profileAvatar ? (
                      <img 
                        src={account.profileAvatar} 
                        alt={`${account.username} avatar`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const parent = e.target.parentElement;
                          if (parent && !parent.querySelector('svg')) {
                            const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                            icon.setAttribute('class', 'h-12 w-12 text-primary-600 dark:text-primary-400');
                            icon.innerHTML = '<path fill="currentColor" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>';
                            parent.appendChild(icon);
                          }
                        }}
                      />
                    ) : (
                      <UserIcon className="h-12 w-12 text-primary-600 dark:text-primary-400" />
                    )}
                  </div>
                </div>

                {/* Profile Information - Inline layout */}
                <div className="pl-4 pt-6">
                  {/* Profile Name, Username, Model - Inline */}
                  <div className="flex items-center flex-wrap gap-2 mb-1.5">
                    {account.profileName ? (
                      <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-tight">
                        {account.profileName}
                      </h3>
                    ) : null}
                    <h4 className={`font-medium text-gray-500 dark:text-gray-400 leading-tight ${account.profileName ? 'text-sm' : 'text-lg font-bold text-gray-900 dark:text-gray-100'}`}>
                      @{account.username}
                    </h4>
                    {modelName && (
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400">
                        Model: {modelName}
                      </p>
                    )}
                  </div>
                  {/* Bio */}
                  {account.profileBio && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed line-clamp-2">
                      {account.profileBio}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - Status and Actions */}
          <div className="col-span-12 lg:col-span-4 flex flex-col">
            <div className="flex flex-col items-end space-y-4 flex-1">
            {/* Status Badges */}
            <div className="flex flex-wrap items-center justify-end gap-2">
              <span className={`badge ${
                account.automation?.enabled && !BLOCKING_AUTOMATION_STATUSES.has(account.status)
                  ? 'badge-success'
                  : 'badge-gray'
              }`}>
                {account.automation?.enabled && !BLOCKING_AUTOMATION_STATUSES.has(account.status)
                  ? 'Active'
                  : 'Inactive'}
              </span>
              <span 
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  account.status === 'active' ? 'badge-success' : 
                  account.status === 'suspended' ? 'badge-danger' : 
                  account.status === 'banned' ? 'badge-danger' :
                  account.status === 'proxy_required' ? 'badge-danger' :
                  account.status === 'proxy_failed' ? 'badge-danger' :
                  account.status === 'proxy_error' ? 'badge-danger' :
                  account.status === 'xchat_key_error' ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800' :
                  (account.status === 'login_failed' || account.loginTestFailed) ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800' :
                  account.status === 'session_extraction_failed' ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800' :
                  account.status === 'auth_expired' ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800' :
                  account.status === 'captcha' ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800' :
                  account.status === 'login_required' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800' :
                  account.status === 'daily_limit' ? 'badge-warning' :
                  'badge-danger'
                }`}
                title={
                  account.status === 'xchat_key_error' ? 'XChat private key is wrong or rotated — restart automation to re-extract'
                    : account.status === 'login_failed' || account.loginTestFailed || account.status === 'session_extraction_failed' || account.status === 'auth_expired' || account.status === 'captcha' || account.status === 'login_required'
                    ? account.status === 'captcha' ? 'Captcha required - use Solve Captcha button' : 
                      account.status === 'login_required' ? 'Account credentials or proxy updated - login required' :
                      'refresh auth / ct0 token'
                    : account.status === 'daily_limit' ? 'Daily limit exceeded - account paused for 24 hours'
                    : undefined
                }
              >
                {account.status === 'active' ? 'Logged-in' :
                 account.status === 'suspended' ? 'Suspended' :
                 account.status === 'banned' ? 'Banned' :
                 account.status === 'proxy_required' ? 'Proxy Required' :
                 account.status === 'proxy_failed' ? 'Proxy Failed' :
                 account.status === 'proxy_error' ? 'Proxy Error' :
                 account.status === 'xchat_key_error' ? 'Key Error' :
                 (account.status === 'login_failed' || account.loginTestFailed) ? 'Login failed' :
                 account.status === 'session_extraction_failed' ? 'Extraction failed' :
                 account.status === 'auth_expired' ? 'Auth expired' :
                 account.status === 'captcha' ? 'Captcha' :
                 account.status === 'login_required' ? 'Login required' :
                 account.status === 'daily_limit' ? 'Daily limit' :
                 'Auth expired'}
              </span>
              {account.hasGraduatedAccess === false && (
                <span
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                  title="X has_graduated_access=false — account still in sandbox"
                >
                  Sandbox
                </span>
              )}
              {account.hasGraduatedAccess === true && (
                <span
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
                  title="X has_graduated_access=true — out of sandbox"
                >
                  Graduated
                </span>
              )}
              {account.status === 'daily_limit' && account.pauseUntil && (
                <DailyLimitTimer pauseUntil={account.pauseUntil} accountId={account._id} />
              )}
              {account.automationStartFailed && (
                <span className="badge badge-danger" title="Automation start failed - last start attempt was unsuccessful">
                  Start Failed
                </span>
              )}
              {/* Account Tags */}
              {(() => {
                if (!account.tags || account.tags.length === 0) return null;
                
                const normalizedTags = [];
                const seenTags = new Set();
                const statusToTagMap = {
                  'login_failed': 'login_failed',
                  'session_extraction_failed': 'session_extraction_failed'
                };
                const statusTag = statusToTagMap[account.status];
                
                account.tags.forEach(tag => {
                  const normalizedTag = tag.toLowerCase().trim();
                  if (seenTags.has(normalizedTag)) return;
                  if (normalizedTag === statusTag) return;
                  if (normalizedTag === 'login_failed' && (account.status === 'login_failed' || account.loginTestFailed)) return;
                  
                  seenTags.add(normalizedTag);
                  normalizedTags.push({ original: tag, normalized: normalizedTag });
                });
                
                if (normalizedTags.length === 0) return null;
                
                return (
                  <>
                    {normalizedTags.map(({ original, normalized }, index) => {
                      let tagColors = 'bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-800';
                      
                      if (normalized === 'scraper') {
                        tagColors = 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-800';
                      } else if (normalized === 'view_checker') {
                        tagColors = 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800';
                      }
                      
                      const tagDisplayNames = {
                        'login_failed': 'Login failed',
                        'session_extraction_failed': 'Extraction failed'
                      };
                      const displayName = tagDisplayNames[normalized] || original;
                      
                      return (
                        <span
                          key={index}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tagColors}`}
                        >
                          {displayName}
                        </span>
                      );
                    })}
                  </>
                );
              })()}
            </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-700/50 rounded-xl p-2 border border-gray-200 dark:border-gray-600">
                <button
                  onClick={() => accountsPageRef.current.navigate('/dashboard/chatbot')}
                  className="p-2.5 text-gray-700 hover:bg-white dark:text-gray-300 dark:hover:bg-gray-600 rounded-lg transition-all hover:scale-105 active:scale-95"
                  title="View Conversations (Chatbot)"
                >
                  <ChatBubbleLeftRightIcon className="h-5 w-5" />
                </button>
                {account.automation?.starting ? (
                  <button
                    disabled
                    className="p-2.5 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-lg transition-colors opacity-50 cursor-not-allowed"
                    title="Automation is initializing..."
                  >
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                  </button>
                ) : account.automation?.enabled &&
                  !BLOCKING_AUTOMATION_STATUSES.has(account.status) ? (
                  <button
                    onClick={() => accountsPageRef.current.handleStopAutomation(account._id)}
                    className="p-2.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all hover:scale-105 active:scale-95"
                    title="Stop automation"
                  >
                    <StopIcon className="h-5 w-5" />
                  </button>
                ) : (
                  <button
                    onClick={() => accountsPageRef.current.handleStartAutomation(account._id)}
                    className="p-2.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all hover:scale-105 active:scale-95"
                    title="Start automation"
                  >
                    <PlayIcon className="h-5 w-5" />
                  </button>
                )}
                <button
                  onClick={() => accountsPageRef.current.openEditModal(account)}
                  className="p-2.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all hover:scale-105 active:scale-95"
                  title="Edit account"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => openActivityLog()}
                  className="p-2.5 text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/20 rounded-lg transition-all hover:scale-105 active:scale-95"
                  title="Activity log"
                >
                  <DocumentTextIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => accountsPageRef.current.handleDeleteAccount(account._id)}
                  className="p-2.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all hover:scale-105 active:scale-95"
                  title="Delete account"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
                <div className="relative">
                  <button
                    onClick={() => setShowMenu(!showMenu)}
                    className="p-2.5 text-gray-700 hover:bg-white dark:text-gray-300 dark:hover:bg-gray-600 rounded-lg transition-all hover:scale-105 active:scale-95"
                    title="More options"
                  >
                    <EllipsisVerticalIcon className="h-5 w-5" />
                  </button>
                {showMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowMenu(false)}
                    ></div>
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                      <button
                        onClick={() => {
                          handleLoginTest();
                          setShowMenu(false);
                        }}
                        disabled={loginTesting}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loginTesting ? (
                          <>
                            <ArrowPathIcon className="h-4 w-4 animate-spin" />
                            <span>Testing...</span>
                          </>
                        ) : (
                          <>
                            <ArrowLeftOnRectangleIcon className="h-4 w-4" />
                            <span>Login</span>
                          </>
                        )}
                      </button>
                    <button
                      onClick={() => {
                        handleOpenSession();
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2"
                    >
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                      <span>Open</span>
                    </button>
                    </div>
                  </>
                )}
              </div>
            </div>

              {/* Proxy Status and DoB + Phone Status - Bottom Right Corner */}
              <div className="flex flex-col items-end space-y-2 w-full mt-auto pt-2">
                {account.proxy && account.proxy.enabled && (
                  <div className="flex items-center space-x-1.5">
                    {account.proxy.proxyError || account.status === 'proxy_error' ? (
                      <>
                        <GlobeAltIcon className="h-3.5 w-3.5 text-red-500" />
                        <span className="text-xs text-red-600 dark:text-red-400 font-medium">Proxy Error</span>
                      </>
                    ) : (
                      <>
                        <GlobeAltIcon className="h-3.5 w-3.5 text-green-500" />
                        <span className="text-xs text-green-600 dark:text-green-400 font-medium">Proxy Enabled</span>
                      </>
                    )}
                  </div>
                )}
                
                {/* DoB + Phone Status with individual icons */}
                <div className="flex flex-col items-end space-y-1.5">
                  {/* Date of Birth */}
                  <div className="flex items-center space-x-1.5">
                    {!profileFieldMissing(account.dateOfBirth) ? (
                      <>
                        <CalendarIcon className="h-4 w-4 text-green-500" />
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          <span className="font-medium">DoB:</span> <span className="text-white dark:text-gray-200">{account.dateOfBirth}</span>
                        </span>
                      </>
                    ) : (
                      <>
                        <CalendarIcon className="h-4 w-4 text-red-500" />
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Missing DoB</span>
                      </>
                    )}
                  </div>
                  
                  {/* Phone Number — email-verified accounts (verificationType==='email') show
                      the clean 'Email Verified' badge instead of a red Missing Phone. Set by
                      the dob_email_check flow module. */}
                  <div className="flex items-center space-x-1.5">
                    {!profileFieldMissing(accountPhoneDisplay(account)) ? (
                      <>
                        <DevicePhoneMobileIcon className="h-4 w-4 text-green-500" />
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          <span className="font-medium">Phone:</span> <span className="text-white dark:text-gray-200">{accountPhoneDisplay(account)}</span>
                        </span>
                      </>
                    ) : account.verificationType === 'email' ? (
                      <>
                        <EnvelopeIcon className="h-4 w-4 text-green-500" />
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Email Verified</span>
                      </>
                    ) : (
                      <>
                        <DevicePhoneMobileIcon className="h-4 w-4 text-red-500" />
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Missing Phone</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Login Test Status */}
        {loginTesting && (
          <div className="mt-3 p-2 rounded-lg text-sm bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-800 mr-2"></div>
              Testing login... This may take up to 30 seconds
            </div>
          </div>
        )}
        {loginStatus && !loginTesting && (
          <div className={`mt-3 p-2 rounded-lg text-sm ${
            loginStatus.success 
              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' 
              : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
          }`}>
            <div className="flex items-center justify-between">
              <span>{loginStatus.message}</span>
              <button
                onClick={clearLoginStatus}
                className="ml-2 text-xs underline hover:no-underline"
                title="Clear status"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Error Messages */}
        {(account.sessionError || account.loginError) && (
          <div className="mt-2 text-xs text-red-600 dark:text-red-400 truncate" title={account.sessionError || account.loginError}>
            {account.sessionError || account.loginError}
          </div>
        )}
      </div>
    </div>

    {showActivityLogModal && (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`activity-log-title-${account._id}`}
        onClick={() => setShowActivityLogModal(false)}
      >
        <div
          className="bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-3xl max-h-[85vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h2 id={`activity-log-title-${account._id}`} className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Activity log · @{account.username}
            </h2>
            <button
              type="button"
              onClick={() => setShowActivityLogModal(false)}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
            {activityLogsLoading ? (
              <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                <ArrowPathIcon className="h-8 w-8 animate-spin mr-2" />
                Loading…
              </div>
            ) : activityLogs.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No log entries yet.</p>
            ) : (
              activityLogs.map((log) => (
                <div
                  key={log._id}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3 space-y-2"
                >
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                      {log.createdAt ? new Date(log.createdAt).toLocaleString() : '—'}
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">
                      {activityCategoryLabel(log.category)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed whitespace-pre-wrap">
                    {log.summary}
                  </p>
                  {log.http && (
                    <pre className="text-xs font-mono bg-gray-900 text-gray-100 dark:bg-black/40 rounded-md p-3 overflow-x-auto max-h-[min(70vh,900px)] overflow-y-auto whitespace-pre-wrap break-words">
                      {JSON.stringify(log.http, null, 2)}
                    </pre>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Only re-render if key fields actually changed (avoids expensive JSON.stringify)
  const a = prevProps.account;
  const b = nextProps.account;
  return a._id === b._id &&
         prevProps.selected === nextProps.selected &&
         prevProps.models === nextProps.models &&
         prevProps.clearStatusesKey === nextProps.clearStatusesKey &&
         a.status === b.status &&
         a.username === b.username &&
         a.profileName === b.profileName &&
         a.profileBio === b.profileBio &&
         a.profileAvatar === b.profileAvatar &&
         a.profileBanner === b.profileBanner &&
         a.pauseUntil === b.pauseUntil &&
         a.group === b.group &&
         a.modelId === b.modelId &&
         a.sessionError === b.sessionError &&
         a.loginError === b.loginError &&
         a.hasValidSession === b.hasValidSession &&
         a.automation?.enabled === b.automation?.enabled &&
         a.automation?.starting === b.automation?.starting &&
         a.hasGraduatedAccess === b.hasGraduatedAccess &&
         a.proxy?.proxyError === b.proxy?.proxyError &&
         a.stats?.conversations24h === b.stats?.conversations24h &&
         a.stats?.totalConversations === b.stats?.totalConversations;
});

const Accounts = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [models, setModels] = useState([]);
  const [modelStats, setModelStats] = useState([]);
  const [modelStatsLoading, setModelStatsLoading] = useState(true);
  const [pinnedModelIds, setPinnedModelIds] = useState([]);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const [pinDraft, setPinDraft] = useState([]);
  const [pinsSaving, setPinsSaving] = useState(false);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  /** True while refetching after group/filter change — avoids empty-state flash. */
  const [listRefreshing, setListRefreshing] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkAddRunning, setBulkAddRunning] = useState(false);
  const [bulkAddProgress, setBulkAddProgress] = useState({
    total: 0,
    index: 0,
    items: [],
    status: null,
    successCount: 0,
    failCount: 0,
    skippedCount: 0,
    currentAccount: null,
    operationId: null
  });
  const bulkAddOperationIdRef = useRef(null);
  const [serviceUrls, setServiceUrls] = useState({});
  const [activeTab, setActiveTab] = useState('accounts');

  // Backup progress poll — sockets can miss updates across replicas; Redis is source of truth
  useEffect(() => {
    if (!bulkAddRunning) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get('/accounts/bulk-add-with-login/progress');
        if (cancelled || !data) return;
        if (!data.active && !data.total) return;

        const expectedOp = bulkAddOperationIdRef.current;
        // Until enqueue returns operationId, ignore stale Redis (esp. prior completed runs)
        if (!expectedOp) return;
        if (data.operationId && data.operationId !== expectedOp) return;

        setBulkAddProgress((prev) => ({
          ...prev,
          operationId: data.operationId || prev.operationId,
          total: data.total != null ? data.total : prev.total,
          index: data.index != null ? data.index : (data.current != null ? data.current : prev.index),
          status: data.status || prev.status,
          successCount: data.successCount != null ? data.successCount : prev.successCount,
          failCount: data.failCount != null ? data.failCount : prev.failCount,
          skippedCount: data.skippedCount != null ? data.skippedCount : prev.skippedCount,
          currentAccount: data.currentAccount != null ? data.currentAccount : prev.currentAccount
        }));
        if (data.status === 'completed' || data.status === 'cancelled') {
          setBulkAddRunning(false);
          setBulkProcessing(false);
          if (data.status === 'completed') {
            const successful = data.successCount || 0;
            const failed = data.failCount || 0;
            const skipped = data.skippedCount || 0;
            const total = data.total || 0;
            setBulkResults({
              successful,
              failed,
              skipped,
              total,
              summary: {
                successful,
                failed,
                skipped,
                total,
                successRate: total > 0 ? Math.round((successful / total) * 100) : 0
              },
              results: []
            });
            toast.success(
              `Bulk addition completed: ${successful}/${total} logged in` +
                (skipped ? `, ${skipped} skipped` : '') +
                (failed ? `, ${failed} failed` : '')
            );
          }
        }
      } catch (_) { /* non-fatal */ }
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [bulkAddRunning]);
  
  // Account filters
  const [filterModelId, setFilterModelId] = useState('all');
  const [filterVpsAgentId, setFilterVpsAgentId] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAutomation, setFilterAutomation] = useState('all');
  /** Account age = time since document creation (createdAt). High = oldest first, Low = newest first */
  const [filterAgeOrder, setFilterAgeOrder] = useState('low');
  /** Profile data filter — missing DoB / phone (works with group tabs + select-all delete). */
  const [filterProfile, setFilterProfile] = useState('all');
  /** Sandbox graduation from UserByScreenName.has_graduated_access (account state, not global status). */
  const [filterSandbox, setFilterSandbox] = useState('all');
  /** Account.tags filter (needs-pin-reset / scraper / source / active_checker / …). */
  const [filterTag, setFilterTag] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Status changes dropdown
  const [showStatusChanges, setShowStatusChanges] = useState(false);
  /** Startup / running / start-failed breakdown (same pattern as Status Changes) */
  const [showAutomationLifecycle, setShowAutomationLifecycle] = useState(false);
  
  // Group management
  const [groups, setGroups] = useState([]); // Tabs: leaf + main groups
  const [leafGroups, setLeafGroups] = useState([]); // Used for "assign to group" dropdowns (leaf only)
  const [mainGroupsByName, setMainGroupsByName] = useState({}); // { [name]: { name, subGroups } }
  /**
   * Group → { automation, attachedAgent } map.
   *
   * Populated by fetchGroups() from the enriched /accounts/groups
   * response — drives the small badges on each group tab indicating
   * "auto-runs preset on upload" and "auto-assigns → <agent>". Missing
   * entry = plain unmanaged group (no badges, no behavior change).
   */
  const [groupMetaByName, setGroupMetaByName] = useState({});
  const resolvedLeafGroupsCacheRef = useRef(new Map());

  /**
   * Preset management modal state. Driven by the per-tab "Manage preset"
   * action — operator can configure / clear an AccountGroup's
   * `automation` preset that auto-fires on bulk upload. The modal also
   * surfaces the attached AI VA Agent (when any) as read-only context;
   * agent attach itself lives in the VA Agents dashboard.
   */
  const [openGroupMenu, setOpenGroupMenu] = useState(null); // group name or null
  const [presetModalOpen, setPresetModalOpen] = useState(false);
  const [presetModalGroup, setPresetModalGroup] = useState(null); // { name, automation, attachedAgent }
  const [presetModalDraft, setPresetModalDraft] = useState({
    modules: [],
    bioSource: 'manual',
    bioModelId: '',
    concurrency: 15,
    delayBetweenStepsMin: 3,
    delayBetweenStepsMax: 8,
    delayBetweenAccountsMin: 5,
    delayBetweenAccountsMax: 15,
    moveToGroupOnSuccess: false,
    moveToGroupName: '',
    runOnUpload: true,
    label: ''
  });
  const [presetModalSubmitting, setPresetModalSubmitting] = useState(false);

  /** Auto-assign group → AI VA Agent modal (admin only). */
  const [agentAssignModalOpen, setAgentAssignModalOpen] = useState(false);
  const [agentAssignModalGroup, setAgentAssignModalGroup] = useState(null);
  const [agentAssignSelectedId, setAgentAssignSelectedId] = useState('');
  const [agentAssignSubmitting, setAgentAssignSubmitting] = useState(false);
  const [agentAssignLoadingAgents, setAgentAssignLoadingAgents] = useState(false);

  // When main group structure changes, invalidate the resolution cache.
  useEffect(() => {
    resolvedLeafGroupsCacheRef.current = new Map();
  }, [mainGroupsByName]);

  const [selectedGroup, setSelectedGroup] = useState('all');
  // Model-parent sidebar: models → their groups (from /accounts/groups `models`).
  const [modelGroups, setModelGroups] = useState([]); // [{modelId, modelName, total, active, suspended, captcha, auth, groups:[{name, total, ...}]}]
  const [collapsedModels, setCollapsedModels] = useState({}); // { [modelId]: bool }
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null); // { name: string, isNew: boolean, isMain: boolean }
  const [newGroupName, setNewGroupName] = useState('');

  // Drag & drop: build "main groups" by nesting one group onto another.
  const [draggingGroupName, setDraggingGroupName] = useState(null);
  const [dragOverGroupName, setDragOverGroupName] = useState(null);
  const [showMainGroupModal, setShowMainGroupModal] = useState(false);
  const [mainGroupModalDraft, setMainGroupModalDraft] = useState({
    sourceGroup: null,
    targetGroup: null,
    suggestedName: '',
    name: ''
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [usePagination, setUsePagination] = useState(false); // Toggle for pagination mode
  
  // Bulk operations state
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const lastSelectedIndexRef = useRef(null); // Track last selected account index for shift-click range selection
  const shiftKeyPressedRef = useRef(false); // Track if shift key is pressed during click
  const [bulkOperationLoading, setBulkOperationLoading] = useState(false);
  const [bulkOperationResults, setBulkOperationResults] = useState(null);
  const [bulkStartProgress, setBulkStartProgress] = useState({ current: 0, total: 0, currentAccount: null, successCount: 0, failCount: 0, status: null, operationId: null });
  const [addSigningKeyProgress, setAddSigningKeyProgress] = useState({
    current: 0, total: 0, currentAccount: null, successCount: 0, failCount: 0, skippedCount: 0, status: null, operationId: null, kind: null
  });
  const signingKeyTerminalRef = useRef(null);
  const bulkStartAccountIdsRef = useRef(new Set()); // Track accounts in bulk start to prevent duplicate toasts (use ref to avoid dependency issues)
  const [restartProgress, setRestartProgress] = useState({ current: 0, total: 0, currentAccount: null, successCount: 0, failCount: 0, status: null, operationId: null });
  const restartInProgressRef = useRef(false); // suppress per-account toasts during server restart
  const [bulkLoginTestProgress, setBulkLoginTestProgress] = useState({ current: 0, total: 0, currentAccount: null, status: null });
  const [bulkCaptchaSolveProgress, setBulkCaptchaSolveProgress] = useState({ current: 0, total: 0, currentAccount: null, status: null, successCount: 0, failCount: 0, message: '' });
  const [clearStatusesKey, setClearStatusesKey] = useState(0);
  const [showUpdateAuthModal, setShowUpdateAuthModal] = useState(false);
  const [updateAuthText, setUpdateAuthText] = useState('');
  const [showBulkUpdateProxiesModal, setShowBulkUpdateProxiesModal] = useState(false);
  const [bulkUpdateProxiesText, setBulkUpdateProxiesText] = useState('');
  const [bulkUpdateProxiesLoading, setBulkUpdateProxiesLoading] = useState(false);
  const [showBulkResetTlsModal, setShowBulkResetTlsModal] = useState(false);
  const [bulkResetTlsUserAgent, setBulkResetTlsUserAgent] = useState('');
  const [showAddToVaModal, setShowAddToVaModal] = useState(false);
  const [vaUsers, setVaUsers] = useState([]);
  /**
   * Selected owner for the "Add to VA" modal. A single picker drives
   * assignment to *either* a human VA or an AI VA Agent — never both
   * and never multiple (enforced by the backend too). Encoded as
   * `va:<id>` or `agent:<id>` so the dropdown can mix both kinds.
   */
  const [addToVaOwnerKey, setAddToVaOwnerKey] = useState('');
  const [addToVaLoading, setAddToVaLoading] = useState(false);
  const [addToVaJobId, setAddToVaJobId] = useState(null);
  const [addToVaJobProgress, setAddToVaJobProgress] = useState(null);
  const [addToVaCancelling, setAddToVaCancelling] = useState(false);
  /** AI VA Agents loaded for the unified picker in the "Add to VA" modal. */
  const [aiAgents, setAiAgents] = useState([]);
  /** DB-backed totals for dashboard (not derived from current page of accounts). */
  const [accountSummary, setAccountSummary] = useState({
    totalAccounts: 0,
    automationEnabled: 0,
    /** Currently running on this app instance (matches AutomationService.activeAutomations); informational only. */
    automationRunning: 0,
    /** Accounts with automation.enabled false in DB — pairs with Automation filter Inactive / hero counts. */
    automationInactive: 0,
    /** From GET /accounts/summary — counts by Account.group (trimmed); used for group tabs when list is paginated. */
    leafGroupCounts: {},
    leafGroupStatusCounts: {},
    ungroupedCount: 0,
    ungroupedStatusCounts: { active: 0, suspended: 0, captcha: 0 },
    /** Full fleet size when summary is scoped to filters (same as totalAccounts when not scoped). */
    globalTotalAccounts: 0,
    summaryScoped: false,
    distinctStatuses: [],
    statusCounts: { authExpired: 0, loginFailed: 0, captcha: 0, loginRequired: 0 }
  });
  /** Start All / Stop All counts for full dashboard scope (not current page only). */
  const [bulkScope, setBulkScope] = useState({
    startable: 0,
    stoppable: 0,
    loading: false
  });
  const bulkScopeDebounceRef = useRef(null);
  const [bulkUpdateAuthProgress, setBulkUpdateAuthProgress] = useState({ 
    current: 0, 
    total: 0, 
    currentAccount: null, 
    status: null, 
    successCount: 0, 
    failCount: 0,
    notFoundCount: 0 
  });
  const [bulkRenewAuthProgress, setBulkRenewAuthProgress] = useState({
    current: 0,
    total: 0,
    currentAccount: null,
    status: null,
    successCount: 0,
    failCount: 0,
    skippedCount: 0,
  });
  const [showUpdateProxyModal, setShowUpdateProxyModal] = useState(false);
  const [updateProxyText, setUpdateProxyText] = useState('');
  const [bulkUpdateProxyProgress, setBulkUpdateProxyProgress] = useState({
    current: 0,
    total: 0,
    currentAccount: null,
    status: null,
    successCount: 0,
    failCount: 0,
    notFoundCount: 0
  });
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/142.0.0.0 Safari/537.36',
    dailyLimit: 50,
    workingHours: { start: 9, end: 22 },
    keywords: ['hi', 'hello', 'hey'],
    blacklistKeywords: ['spam', 'bot'],
    scraperTag: false,
    activeCheckerTag: false,
    sourceTag: false,
    followBackCheckerTag: false,
    viewCheckerTag: false,
    aiSettings: {
      provider: 'template',
      personality: 'friendly',
      systemPrompt: 'You are a friendly, engaging assistant helping to convert X.com DMs into Fanvue subscribers.'
    },
    funnelSettings: {
      ctaText: 'Check out my exclusive content!',
      linkPlacement: 'end',
      includeEmoji: true
    },
    proxy: {
      enabled: false,
      proxyId: null
    },
    accountStringSimple: '', // Simple format: username:authToken
    proxyString: '',
    modelId: null,
    xchatPrivateKey: '' // XChat E2EE private key (PKCS#8 hex)
  });
  const [bulkFormData, setBulkFormData] = useState({
    accounts: '',
    accountsSimple: '',
    proxies: '',
    proxyProtocol: 'http',
    modelId: null,
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/142.0.0.0 Safari/537.36',
    addScraperTag: false,
    addActiveCheckerTag: false,
    addFollowBackCheckerTag: false,
    addViewCheckerTag: false,
    group: '',
    autoAssignProxy: false,
    maxAccountsPerProxy: 4,
    autoAssignProxyRequireAuth: false,
    disableProxyPoolReplace: false,
    postUploadTasks: {
      privacy_check: false,
      dob_phone_check: false,
      extract_profile: false,
      fetch_username: false,
      update_bio: false
    }
  });
  const [showProxyValidationModal, setShowProxyValidationModal] = useState(false);
  const [proxyValidationData, setProxyValidationData] = useState({ accountCount: 0, proxyCount: 0, missing: 0 });
  const [showStartAllBreakdownModal, setShowStartAllBreakdownModal] = useState(false);
  const [startAllBreakdown, setStartAllBreakdown] = useState(null);
  
  // Custom format drag-and-drop state
  const [customFormatFields, setCustomFormatFields] = useState([
    { id: 'username', label: 'Username', enabled: true },
    { id: 'password', label: 'Password', enabled: true },
    { id: 'accessToken', label: 'Auth Token', enabled: true },
    { id: 'proxyString', label: 'Proxy (IP:PORT:USERNAME:PASSWORD[:proxy_auth])', enabled: true }
  ]);
  const [customFormatDelimiter, setCustomFormatDelimiter] = useState(':');
  const [customFormatAccounts, setCustomFormatAccounts] = useState('');
  const [addProxiesSeparately, setAddProxiesSeparately] = useState(false);
  const [draggedField, setDraggedField] = useState(null);
  
  const { socket, connected } = useSocket();

  // Refs to track stale check timeouts
  const staleCheckTimeoutRef = React.useRef(null);
  // Ref to debounce fetchAccounts calls
  const fetchAccountsTimeoutRef = React.useRef(null);
  // Ref to track if fetchAccounts is currently in progress (prevent concurrent fetches)
  const isFetchingAccountsRef = React.useRef(false);
  // Ref to track consecutive errors (only show toast after multiple failures)
  const fetchErrorCountRef = React.useRef(0);
  // Ref to track if initial fetch has been done (prevents repeated fetches on socket reconnects)
  const initialFetchDoneRef = React.useRef(false);
  const filterEffectMountedRef = React.useRef(false);
  const fetchAccountsAbortRef = React.useRef(null);
  const fetchAccountsSeqRef = React.useRef(0);
  // Holds the latest fetchAccounts closure so filter-change effects can invoke the
  // freshest version without listing fetchAccounts in their dep array (which would
  // re-fire the effect every time totalAccounts/currentPage/etc. change).
  const fetchAccountsRef = React.useRef(null);

  // Helper function to clean message content (remove phase tags)

  // Restore progress from sessionStorage on mount
  useEffect(() => {
    try {
      const storedBulkStartProgress = sessionStorage.getItem('bulkStartProgress');
      const storedBulkLoginTestProgress = sessionStorage.getItem('bulkLoginTestProgress');
      const storedAddSigningKeyProgress = sessionStorage.getItem('addSigningKeyProgress');
      if (storedAddSigningKeyProgress) {
        const progress = JSON.parse(storedAddSigningKeyProgress);
        if (
          (progress.status === 'queued' || progress.status === 'processing' || progress.status === 'cancelling') &&
          progress.total > 0
        ) {
          setAddSigningKeyProgress(progress);
          setBulkOperationLoading(true);
        } else {
          sessionStorage.removeItem('addSigningKeyProgress');
        }
      }
      
      if (storedBulkStartProgress) {
        const progress = JSON.parse(storedBulkStartProgress);
        // Only restore if operation is not completed and has valid data
        if (progress.status !== 'completed' && progress.status !== 'cancelled' && progress.total > 0) {
          // Check if progress is stale (older than 5 minutes) - likely from a previous session
          const progressTimestamp = progress.timestamp || 0;
          const now = Date.now();
          const fiveMinutesAgo = now - (5 * 60 * 1000);
          
          if (progressTimestamp < fiveMinutesAgo) {
            // Progress is stale (older than 5 minutes), clear it
            sessionStorage.removeItem('bulkStartProgress');
            setBulkStartProgress({ current: 0, total: 0, currentAccount: null, successCount: 0, failCount: 0, status: null, operationId: null });
            setBulkOperationLoading(false);
            return;
          }
          
          // Progress seems recent, restore it but with a shorter timeout
          setBulkStartProgress(progress);
          setBulkOperationLoading(true);
          
          // Safety check: if no socket events arrive within 2 minutes, clear stale progress
          // Large batches (2000+ accounts) need time for backend to queue and start emitting events
          staleCheckTimeoutRef.current = setTimeout(() => {
            sessionStorage.removeItem('bulkStartProgress');
            setBulkStartProgress({ current: 0, total: 0, currentAccount: null, successCount: 0, failCount: 0, status: null, operationId: null });
            setBulkOperationLoading(false);
            staleCheckTimeoutRef.current = null;
          }, 120000); // 2 minutes — gives backend time to queue large batches
        } else {
          // Clear completed/cancelled progress
          sessionStorage.removeItem('bulkStartProgress');
        }
      }
      
      if (storedBulkLoginTestProgress) {
        const progress = JSON.parse(storedBulkLoginTestProgress);
        // Only restore if operation is not completed
        if (progress.status !== 'completed' && progress.total > 0) {
          setBulkLoginTestProgress(progress);
          setBulkOperationLoading(true);
          
          // Safety check: if no socket events arrive within 30 seconds, clear stale progress
          staleCheckTimeoutRef.current = setTimeout(() => {
            sessionStorage.removeItem('bulkLoginTestProgress');
            setBulkLoginTestProgress({ current: 0, total: 0, currentAccount: null, status: null, successCount: 0, failCount: 0 });
            setBulkOperationLoading(false);
            staleCheckTimeoutRef.current = null;
          }, 30000);
        } else {
          // Clear completed progress
          sessionStorage.removeItem('bulkLoginTestProgress');
        }
      }
    } catch (error) {
      console.error('Error restoring progress from sessionStorage:', error);
      // Clear corrupted data
      sessionStorage.removeItem('bulkStartProgress');
      sessionStorage.removeItem('bulkLoginTestProgress');
    }
    
    // Cleanup on unmount
    return () => {
      if (staleCheckTimeoutRef.current) {
        clearTimeout(staleCheckTimeoutRef.current);
        staleCheckTimeoutRef.current = null;
      }
      if (fetchAccountsTimeoutRef.current) {
        clearTimeout(fetchAccountsTimeoutRef.current);
        fetchAccountsTimeoutRef.current = null;
      }
    };
  }, []);

  // Fetch server-restart progress on mount (admin only)
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    let cancelled = false;
    api.get('/accounts/restart-status')
      .then((res) => {
        if (cancelled || !res.data) return;
        if (res.data.active && res.data.total > 0) {
          setRestartProgress({
            current: res.data.current || 0,
            total: res.data.total,
            currentAccount: res.data.currentAccount || null,
            successCount: res.data.successCount || 0,
            failCount: res.data.failCount || 0,
            status: res.data.status || 'processing',
            operationId: res.data.operationId || null
          });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const mergeSummaryPayload = useCallback((s) => {
    const total = s.totalAccounts ?? 0;
    const running = s.automationRunning ?? 0;
    const enabled = s.automationEnabled ?? 0;
    const rawCounts = s.leafGroupCounts;
    const leafGroupCounts =
      rawCounts && typeof rawCounts === 'object' && !Array.isArray(rawCounts) ? { ...rawCounts } : {};
    const sc = s.statusCounts && typeof s.statusCounts === 'object' ? s.statusCounts : {};
    setAccountSummary({
      totalAccounts: total,
      automationEnabled: enabled,
      automationRunning: running,
      automationInactive: Math.max(0, total - enabled),
      leafGroupCounts,
      leafGroupStatusCounts:
        s.leafGroupStatusCounts && typeof s.leafGroupStatusCounts === 'object' && !Array.isArray(s.leafGroupStatusCounts)
          ? { ...s.leafGroupStatusCounts }
          : {},
      ungroupedCount: typeof s.ungroupedCount === 'number' ? s.ungroupedCount : 0,
      ungroupedStatusCounts: {
        active: Number(s.ungroupedStatusCounts?.active) || 0,
        suspended: Number(s.ungroupedStatusCounts?.suspended) || 0,
        captcha: Number(s.ungroupedStatusCounts?.captcha) || 0
      },
      globalTotalAccounts: typeof s.globalTotalAccounts === 'number' ? s.globalTotalAccounts : total,
      summaryScoped: !!s.summaryScoped,
      distinctStatuses: Array.isArray(s.distinctStatuses) ? s.distinctStatuses : [],
      statusCounts: {
        authExpired: sc.authExpired ?? 0,
        loginFailed: sc.loginFailed ?? 0,
        captcha: sc.captcha ?? 0,
        loginRequired: sc.loginRequired ?? 0
      }
    });
  }, []);

  /** Sum DB-backed member counts for a set of leaf group names (main groups resolve to many leaves). */
  const sumLeafMemberCounts = useCallback((leafSet) => {
    const c = accountSummary.leafGroupCounts || {};
    if (!leafSet || leafSet.size === 0) return 0;
    let n = 0;
    for (const leaf of leafSet) {
      n += c[leaf] || 0;
    }
    return n;
  }, [accountSummary.leafGroupCounts]);

  const statusCountsForLeaves = useCallback((leafSet) => (
    sumLeafStatusCounts(leafSet, accountSummary.leafGroupStatusCounts)
  ), [accountSummary.leafGroupStatusCounts]);

  const refreshAccountSummary = useCallback(() => {
    const summaryParams = new URLSearchParams();
    if (selectedGroup && selectedGroup !== 'all') {
      summaryParams.set('filterGroup', selectedGroup);
    }
    if (filterModelId && filterModelId !== 'all') {
      summaryParams.set('filterModelId', String(filterModelId));
    }
    if (filterVpsAgentId && filterVpsAgentId !== 'all') {
      summaryParams.set('filterVpsAgentId', String(filterVpsAgentId));
    }
    if (filterStatus && filterStatus !== 'all') {
      summaryParams.set('filterStatus', filterStatus);
    }
    if (filterAutomation && filterAutomation !== 'all') {
      summaryParams.set('filterAutomation', filterAutomation);
    }
    if (filterProfile && filterProfile !== 'all') {
      summaryParams.set('filterProfile', filterProfile);
    }
    if (filterSandbox && filterSandbox !== 'all') {
      summaryParams.set('filterSandbox', filterSandbox);
    }
    if (filterTag && filterTag !== 'all') {
      summaryParams.set('filterTag', filterTag);
    }
    const rsq = searchQuery.trim();
    if (rsq) {
      summaryParams.set('search', rsq);
    }
    const summaryUrl =
      summaryParams.toString().length > 0
        ? `/accounts/summary?${summaryParams.toString()}`
        : '/accounts/summary';
    api.get(summaryUrl)
      .then((r) => {
        if (r?.data && typeof r.data.totalAccounts === 'number') {
          mergeSummaryPayload(r.data);
        }
      })
      .catch(() => {});
  }, [
    mergeSummaryPayload,
    selectedGroup,
    filterModelId,
    filterVpsAgentId,
    filterStatus,
    filterAutomation,
    filterProfile,
    filterSandbox,
    filterTag,
    searchQuery
  ]);

  const handleAutomationUpdate = useCallback((data) => {
    const aid = data?.accountId != null ? String(data.accountId) : '';
    setAccounts(prev => prev.map(account => {
      if (String(account._id) !== aid) return account;
      if (data.status === 'running') {
        return {
          ...account,
          automationRunning: true,
          automation: {
            ...account.automation,
            enabled: true,
            starting: false
          },
          automationStartFailed: false,
          automationStartError: undefined,
          automationStartFailureClassification: undefined
        };
      }
      if (data.status === 'failed') {
        return {
          ...account,
          automationRunning: false,
          automation: {
            ...account.automation,
            enabled: false,
            starting: false
          },
          automationStartFailed: true,
          automationStartError: data.error ?? account.automationStartError ?? account.lastError ?? null,
          automationStartFailureClassification: data.classification ?? account.automationStartFailureClassification ?? null
        };
      }
      if (data.status === 'initializing' || data.status === 'starting') {
        return {
          ...account,
          automationRunning: true,
          automation: {
            ...account.automation,
            enabled: false,
            starting: true
          }
        };
      }
      if (data.status === 'stopped') {
        const updatedAccount = {
          ...account,
          automationRunning: false,
          automation: {
            ...account.automation,
            enabled: false,
            starting: false
          }
        };
        if (data.reason === 'proxy_error' || data.proxyError) {
          updatedAccount.proxy = { ...account.proxy, proxyError: true };
          updatedAccount.status = 'proxy_error';
        }
        if (data.reason === 'xchat_key_error') {
          updatedAccount.status = 'xchat_key_error';
        }
        return updatedAccount;
      }
      return account;
    }));
    refreshAccountSummary();
  }, [refreshAccountSummary]);

  const handleMessageSent = useCallback((data) => {
    setAccounts(prev => prev.map(account =>
      String(account._id) === String(data.accountId)
        ? {
            ...account,
            stats: {
              ...account.stats,
              totalResponses: account.stats.totalResponses + 1,
              responsesToday: account.stats.responsesToday + 1
            }
          }
        : account
    ));
  }, []);

  /** Same filter query keys as GET /accounts (without pagination). */
  const hasActiveServerFilters = useMemo(() => (
    (selectedGroup && selectedGroup !== 'all')
    || (filterModelId && filterModelId !== 'all')
    || (filterVpsAgentId && filterVpsAgentId !== 'all')
    || (filterStatus && filterStatus !== 'all')
    || (filterAutomation && filterAutomation !== 'all')
    || (filterProfile && filterProfile !== 'all')
    || (filterSandbox && filterSandbox !== 'all')
    || (filterTag && filterTag !== 'all')
    || searchQuery.trim() !== ''
  ), [selectedGroup, filterModelId, filterVpsAgentId, filterStatus, filterAutomation, filterProfile, filterSandbox, filterTag, searchQuery]);

  /** When true, GET /accounts already applied group/status/model/search/profile — skip client-side re-filter. */
  const useServerScopeFilters = useMemo(
    () => usePagination || totalAccounts > 100 || hasActiveServerFilters,
    [usePagination, totalAccounts, hasActiveServerFilters]
  );

  const buildDashboardListFilterParams = useCallback(() => {
    const listParams = new URLSearchParams();
    const ageSortParam = filterAgeOrder === 'high' ? 'oldest' : 'newest';
    listParams.set('ageSort', ageSortParam);
    if (selectedGroup && selectedGroup !== 'all') {
      listParams.set('filterGroup', selectedGroup);
    }
    if (filterModelId && filterModelId !== 'all') {
      listParams.set('filterModelId', String(filterModelId));
    }
    if (filterVpsAgentId && filterVpsAgentId !== 'all') {
      listParams.set('filterVpsAgentId', String(filterVpsAgentId));
    }
    if (filterStatus && filterStatus !== 'all') {
      listParams.set('filterStatus', filterStatus);
    }
    if (filterAutomation && filterAutomation !== 'all') {
      listParams.set('filterAutomation', filterAutomation);
    }
    if (filterProfile && filterProfile !== 'all') {
      listParams.set('filterProfile', filterProfile);
    }
    if (filterSandbox && filterSandbox !== 'all') {
      listParams.set('filterSandbox', filterSandbox);
    }
    if (filterTag && filterTag !== 'all') {
      listParams.set('filterTag', filterTag);
    }
    const sq = searchQuery.trim();
    if (sq) {
      listParams.set('search', sq);
    }
    return listParams;
  }, [filterAgeOrder, selectedGroup, filterModelId, filterVpsAgentId, filterStatus, filterAutomation, filterProfile, filterSandbox, filterTag, searchQuery]);

  const refreshBulkScope = useCallback(async () => {
    if (!user) return;
    setBulkScope((p) => ({ ...p, loading: true }));
    try {
      const p = buildDashboardListFilterParams();
      const { data } = await api.get(`/accounts/bulk-scope?${p.toString()}`);
      setBulkScope({
        startable: typeof data.startableCount === 'number' ? data.startableCount : 0,
        stoppable: typeof data.stoppableCount === 'number' ? data.stoppableCount : 0,
        loading: false
      });
    } catch (_e) {
      setBulkScope((p) => ({ ...p, loading: false }));
    }
  }, [user, buildDashboardListFilterParams]);

  // OPTIMIZED: Memoize fetchAccounts to prevent unnecessary recreations
  // Define fetchAccounts early so it can be used in useEffect hooks
  const fetchAccounts = useCallback(async (showLoading = false, pageOverride = null) => {
    const softRefresh = showLoading === 'soft';
    const fetchSeq = ++fetchAccountsSeqRef.current;

    if (fetchAccountsTimeoutRef.current) {
      clearTimeout(fetchAccountsTimeoutRef.current);
      fetchAccountsTimeoutRef.current = null;
    }

    if (fetchAccountsAbortRef.current) {
      fetchAccountsAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAccountsAbortRef.current = controller;

    try {
      isFetchingAccountsRef.current = true;
      if (showLoading === true) {
        setLoading(true);
      } else if (softRefresh) {
        setListRefreshing(true);
      }
      
      const pageForRequest = pageOverride != null ? pageOverride : currentPage;
      // Cold load: totalAccounts is 0 before the first response. Without this, we would request
      // non-paginated GET /accounts (all rows), then the API runs peekJobStates per account + large
      // conversation aggregations — minutes or timeouts for large fleets.
      const shouldUsePagination = usePagination || totalAccounts > 100 || totalAccounts === 0 || hasActiveServerFilters;
      const listParams = buildDashboardListFilterParams();
      if (shouldUsePagination) {
        listParams.set('paginate', 'true');
        listParams.set('page', String(pageForRequest));
        listParams.set('limit', String(itemsPerPage));
      }
      const listQuery = listParams.toString();
      const url = `/accounts?${listQuery}`;

      const summaryParams = new URLSearchParams();
      for (const [k, v] of listParams.entries()) {
        if (k === 'ageSort' || k === 'paginate' || k === 'page' || k === 'limit') continue;
        summaryParams.append(k, v);
      }
      const summaryUrl =
        summaryParams.toString().length > 0
          ? `/accounts/summary?${summaryParams.toString()}`
          : '/accounts/summary';

      const [listResult, summaryResult] = await Promise.allSettled([
        api.get(url, { signal: controller.signal }),
        api.get(summaryUrl, { signal: controller.signal })
      ]);

      if (listResult.status === 'rejected') {
        throw listResult.reason;
      }
      const response = listResult.value;

      if (summaryResult.status === 'fulfilled' && summaryResult.value?.data) {
        const sd = summaryResult.value.data;
        if (typeof sd.totalAccounts === 'number') {
          mergeSummaryPayload(sd);
        }
      } else if (summaryResult.status === 'rejected' && summaryResult.reason?.name !== 'AbortError' && summaryResult.reason?.code !== 'ERR_CANCELED') {
        console.warn('[Accounts] /accounts/summary failed:', summaryResult.reason?.message || summaryResult.reason);
      }
      
      // Validate response structure
      if (!response || !response.data) {
        console.error('Invalid API response structure:', response);
        return; // Don't update state if response is invalid
      }
      
      // Handle paginated or non-paginated response
      let accountsData, paginationData;
      if (response.data.accounts && response.data.pagination) {
        // Paginated response
        accountsData = Array.isArray(response.data.accounts) ? response.data.accounts : [];
        paginationData = response.data.pagination;
        const serverTotal = paginationData.total;
        const serverTotalPages = paginationData.totalPages;

        // Small scoped catalogs: one non-paginated fetch so client-side tools see every row (≤100 matches API cap).
        if (serverTotal > 0 && serverTotal <= 100) {
          const haveAll = accountsData.length >= serverTotal;
          if (!haveAll) {
            const fullParams = buildDashboardListFilterParams();
            const fullRes = await api.get(`/accounts?${fullParams.toString()}`, {
              signal: controller.signal
            });
            if (fullRes?.data && Array.isArray(fullRes.data)) {
              accountsData = fullRes.data;
              setTotalAccounts(accountsData.length);
              setTotalPages(1);
              setUsePagination(false);
            } else {
              setTotalPages(serverTotalPages);
              setTotalAccounts(serverTotal);
              setUsePagination(true);
            }
          } else {
            setTotalAccounts(serverTotal);
            setTotalPages(1);
            setUsePagination(false);
          }
        } else {
          setTotalPages(serverTotalPages);
          setTotalAccounts(serverTotal);
          setUsePagination(serverTotal > 100);
        }
      } else if (Array.isArray(response.data)) {
        // Non-paginated response (backward compatible)
        accountsData = response.data;
        setTotalAccounts(accountsData.length);
        setTotalPages(1);
      } else {
        // Invalid response format - don't update state
        console.error('Invalid response format - expected array or paginated object:', response.data);
        return;
      }
      
      // Only update if we have valid data
      if (Array.isArray(accountsData)) {
        setAccounts((prev) => {
          const prevById = new Map(prev.map((a) => [String(a._id), a]));
          return accountsData.map((account) => {
            const old = prevById.get(String(account._id));
            const keepStarting = old?.automation?.starting === true;
            const queueBoot = account.automationQueueBooting === true;
            return {
              ...account,
              automationQueueBooting: queueBoot,
              automationStartJobState: account.automationStartJobState ?? null,
              automationRunning: Boolean(account.automationRunning === true || keepStarting || queueBoot),
              automation: {
                ...account.automation,
                enabled: account.automation?.enabled || false,
                starting: keepStarting || queueBoot
              },
              automationStartError:
                account.automationStartFailed === true
                  ? old?.automationStartError ?? account.lastError ?? undefined
                  : undefined,
              automationStartFailureClassification:
                account.automationStartFailed === true
                  ? old?.automationStartFailureClassification
                  : undefined
            };
          });
        });
      }
      
      // Clear any cached login statuses when accounts are refreshed
      setBulkOperationResults(null);
      // Reset error count on success
      fetchErrorCountRef.current = 0;
      refreshBulkScope();
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return;
      }
      fetchErrorCountRef.current += 1;
      const isRealError = error.response?.status >= 500 || 
                         (error.response?.status >= 400 && error.response?.status < 500 && error.response?.status !== 429) ||
                         (fetchErrorCountRef.current >= 3);
      if (isRealError) {
        toast.error('Failed to fetch accounts');
      }
      console.error('Error fetching accounts:', error);
    } finally {
      if (fetchAccountsAbortRef.current === controller) {
        fetchAccountsAbortRef.current = null;
      }
      isFetchingAccountsRef.current = false;
      if (fetchSeq !== fetchAccountsSeqRef.current) {
        return;
      }
      if (showLoading === true) {
        setLoading(false);
      } else if (softRefresh) {
        setListRefreshing(false);
      }
    }
  }, [
    usePagination,
    totalAccounts,
    currentPage,
    itemsPerPage,
    filterAgeOrder,
    selectedGroup,
    filterModelId,
    filterStatus,
    filterAutomation,
    filterProfile,
    filterSandbox,
    filterTag,
    searchQuery,
    hasActiveServerFilters,
    mergeSummaryPayload,
    buildDashboardListFilterParams,
    refreshBulkScope
  ]);

  // Keep the latest fetchAccounts closure available to effects that intentionally
  // don't list it as a dep (to avoid re-firing on totalAccounts/currentPage changes).
  useEffect(() => {
    fetchAccountsRef.current = fetchAccounts;
  }, [fetchAccounts]);

  // Apply a single account status update to an account object
  const applyStatusUpdate = useCallback((account, data) => {
    // Optimistically append to statusHistory if status actually changed
    let updatedStatusHistory = account.statusHistory || [];
    if (data.status && data.status !== account.status) {
      updatedStatusHistory = [
        ...updatedStatusHistory,
        {
          status: data.status,
          changedAt: new Date().toISOString(),
          reason: data.lastError || data.message || null,
          source: data.statusChangeSource || data.source || null
        }
      ].slice(-50);
    }

    return {
      ...account,
      status: data.status || account.status,
      statusHistory: updatedStatusHistory,
      pauseUntil: data.pauseUntil || account.pauseUntil,
      automation: {
        ...account.automation,
        enabled: data.automation?.enabled ?? account.automation?.enabled ?? false,
        starting: false
      },
      // Update profile fields if provided
      ...(data.profileBanner !== undefined && { profileBanner: data.profileBanner }),
      ...(data.profileAvatar !== undefined && { profileAvatar: data.profileAvatar }),
      ...(data.profileName !== undefined && { profileName: data.profileName }),
      ...(data.profileBio !== undefined && { profileBio: data.profileBio })
    };
  }, []);

  // Batched account status update handler - processes multiple updates at once using Map lookup (O(n) instead of O(n*m))
  const handleAccountStatusUpdateBatch = useCallback((updates) => {
    if (!Array.isArray(updates) || updates.length === 0) return;
    
    // Build a Map of accountId -> update data for O(1) lookup
    const updateMap = new Map();
    let hasProfileUpdate = false;
    for (const data of updates) {
      if (data && data.accountId) {
        updateMap.set(data.accountId, data);
        if (data.profileBanner !== undefined || data.profileAvatar !== undefined || 
            data.profileName !== undefined || data.profileBio !== undefined) {
          hasProfileUpdate = true;
        }
      }
    }
    if (updateMap.size === 0) return;

    setAccounts(prev => {
      let changed = false;
      const next = prev.map(account => {
        const data = updateMap.get(account._id);
        if (data) {
          changed = true;
          return applyStatusUpdate(account, data);
        }
        return account;
      });
      return changed ? next : prev; // Avoid state update if nothing changed
    });

    // Profile update - refresh account data once (debounced) instead of per-update
    if (hasProfileUpdate) {
      if (!fetchAccountsTimeoutRef.current) {
        fetchAccountsTimeoutRef.current = setTimeout(() => {
          fetchAccounts(false);
          fetchAccountsTimeoutRef.current = null;
        }, 2000);
      }
    }
  }, [applyStatusUpdate, fetchAccounts]);

  // Single account status update handler (backward compat / fallback)
  const handleAccountStatusUpdate = useCallback((data) => {
    handleAccountStatusUpdateBatch([data]);
  }, [handleAccountStatusUpdateBatch]);

  // Keep Start All / Stop All counts in sync with group + filters (full scope, not current page).
  useEffect(() => {
    if (!user) return;
    if (bulkScopeDebounceRef.current) clearTimeout(bulkScopeDebounceRef.current);
    bulkScopeDebounceRef.current = setTimeout(() => {
      bulkScopeDebounceRef.current = null;
      refreshBulkScope();
    }, 350);
    return () => {
      if (bulkScopeDebounceRef.current) clearTimeout(bulkScopeDebounceRef.current);
    };
  }, [
    user,
    refreshBulkScope,
    selectedGroup,
    filterModelId,
    filterStatus,
    filterAutomation,
    filterProfile,
    filterSandbox,
    filterTag,
    searchQuery,
    filterAgeOrder
  ]);

  // Separate useEffect for initial data fetch (runs only once on mount)
  useEffect(() => {
    // Load service URLs for routing to worker services
    const loadServiceUrls = async () => {
      try {
        const response = await api.get('/config/services');
        setServiceUrls(response.data || {});
      } catch (error) {
        console.error('[Accounts] Error loading service URLs:', error);
        setServiceUrls({});
      }
    };
    
    loadServiceUrls();
    
    if (!initialFetchDoneRef.current) {
      fetchAccounts(true); // Show loading only on initial fetch
      fetchModels();
      fetchPresets();
      fetchGroups();
      initialFetchDoneRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, fetchAccounts]); // Include user and fetchAccounts in dependencies to fetch when user loads

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/accounts/model-stats');
        if (!cancelled) {
          setModelStats(res.data?.models || []);
          setPinnedModelIds(res.data?.pinnedModelIds || []);
        }
      } catch (err) {
        console.error('Failed to fetch model stats', err);
      } finally {
        if (!cancelled) setModelStatsLoading(false);
      }
    };
    load();
    const timer = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const savePinnedModels = async () => {
    setPinsSaving(true);
    try {
      const res = await api.put('/accounts/model-stats/pins', { modelIds: pinDraft });
      const saved = res.data?.pinnedModelIds || pinDraft;
      setPinnedModelIds(saved);
      setModelStats((rows) => rows.map((row) => ({
        ...row,
        pinned: saved.includes(String(row.modelId))
      })));
      setModelPickerOpen(false);
      toast.success('Saved models');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save models');
    } finally {
      setPinsSaving(false);
    }
  };

  const focusModelStat = (modelId, status, automation) => {
    setActiveTab('accounts');
    setFilterModelId(modelId);
    setFilterStatus(status || 'all');
    setFilterAutomation(automation || 'all');
  };

  const applyAddSigningKeyProgress = useCallback((p) => {
    if (!p) return;
    const kind = p.kind || 'recover';
    const progress = {
      current: p.current || 0,
      total: p.total || 0,
      currentAccount: p.currentAccount || null,
      successCount: p.successCount || 0,
      failCount: p.failCount || 0,
      skippedCount: p.skippedCount || 0,
      status: p.status || null,
      operationId: p.operationId || null,
      inFlight: p.inFlight || 0,
      lastError: p.lastError || p.error || null,
      kind
    };
    setAddSigningKeyProgress(progress);
    try {
      if (progress.status === 'queued' || progress.status === 'processing' || progress.status === 'cancelling') {
        sessionStorage.setItem('addSigningKeyProgress', JSON.stringify({ ...progress, timestamp: Date.now() }));
      } else {
        sessionStorage.removeItem('addSigningKeyProgress');
      }
    } catch (_) { /* ignore */ }

    if (progress.status !== 'completed' && progress.status !== 'cancelled' && progress.status !== 'failed') {
      return;
    }
    setBulkOperationLoading(false);
    const terminalKey = `${progress.operationId}:${progress.status}`;
    if (signingKeyTerminalRef.current === terminalKey) return;
    signingKeyTerminalRef.current = terminalKey;
    const isReset = kind === 'reset-pin';
    if (progress.status === 'completed') {
      const skipped = progress.skippedCount ? `, ${progress.skippedCount} already had a signing key` : '';
      toast.success(
        isReset
          ? `PIN reset: ${progress.successCount || 0} registered, ${progress.failCount || 0} failed${skipped}`
          : `Sign keys: ${progress.successCount || 0} recovered, ${progress.failCount || 0} failed${skipped}`
      );
    } else if (progress.status === 'failed') {
      toast.error(p.error || (isReset ? 'PIN reset job failed' : 'Sign-key recover job failed'));
    } else {
      toast(
        isReset
          ? `PIN reset cancelled — ${progress.successCount || 0} registered`
          : `Sign-key recover cancelled — ${progress.successCount || 0} recovered`
      );
    }
  }, []);

  // Separate useEffect for socket listeners (runs when socket changes, but doesn't fetch accounts)
  useEffect(() => {
    if (socket && user) {
      // Join user-specific room for data isolation
      socket.emit('join-user-room', user._id || user.id);
      
      socket.on('automation-started', (data) => {
        // During server restart, do not show a toast per account
        const isRestartInProgress = restartInProgressRef.current;
        if (!bulkStartAccountIdsRef.current.has(data.accountId) && !isRestartInProgress) {
          handleAutomationUpdate(data);
        } else if (isRestartInProgress) {
          handleAutomationUpdate(data, false);
        } else {
          // Still update the UI, just don't show toast.
          // Bulk start progress must come only from `bulk-start-progress`: the worker emits
          // `automation-started` with status `initializing` on job active (before startAutomation
          // finishes). Counting those events made current reach total while jobs were still running.
          handleAutomationUpdate(data, false);
        }
      });
      socket.on('automation-stopped', handleAutomationUpdate);
      socket.on('automation-update', () => {
        refreshAccountSummary();
      });
      socket.on('all-automations-stopped', refreshAccountSummary);
      socket.on('message-sent', handleMessageSent);
      socket.on('account-status-updated', handleAccountStatusUpdate);
      socket.on('account-status-updated-batch', handleAccountStatusUpdateBatch);
      socket.on('bulk-add-progress', (p) => {
        const myUserId = user._id || user.id;
        if (p.userId && myUserId && String(p.userId) !== String(myUserId)) return;

        const expectedOp = bulkAddOperationIdRef.current;
        // Ignore stale events until we know which operation we started, then bind tightly
        if (!expectedOp) return;
        if (p.operationId && p.operationId !== expectedOp) return;

        const nextIndex = p.index != null ? p.index : (p.current != null ? p.current : null);

        setBulkAddProgress(prev => {
          const items = [...prev.items];
          // Slim per-account rows for the live list (avoid storing full account docs at 5k–10k)
          // Skips are counted in skippedCount — only list successes/failures to keep the panel readable
          if ((p.username || p.error || p.success) && !p.skipped) {
            items.push({
              username: p.username || p.currentAccount || 'account',
              success: !!p.success,
              skipped: false,
              error: p.error || null,
              missingCookies: p.missingCookies,
              availableCookies: p.availableCookies
            });
            // Keep only the last 200 rows in the UI list
            if (items.length > 200) items.splice(0, items.length - 200);
          }
          return {
            operationId: p.operationId || prev.operationId,
            total: p.total != null ? p.total : prev.total,
            index: nextIndex != null ? nextIndex : prev.index,
            items,
            status: p.status || prev.status || null,
            successCount: p.successCount != null ? p.successCount : prev.successCount,
            failCount: p.failCount != null ? p.failCount : prev.failCount,
            skippedCount: p.skippedCount != null ? p.skippedCount : prev.skippedCount,
            currentAccount: p.currentAccount != null ? p.currentAccount : prev.currentAccount
          };
        });

        if (p.status === 'completed' || p.status === 'cancelled') {
          setBulkAddRunning(false);
          setBulkProcessing(false);
          if (p.status === 'completed') {
            const successful = p.successCount || 0;
            const failed = p.failCount || 0;
            const skipped = p.skippedCount || 0;
            const total = p.total || 0;
            setBulkResults({
              successful,
              failed,
              skipped,
              total,
              summary: {
                successful,
                failed,
                skipped,
                total,
                successRate: total > 0 ? Math.round((successful / total) * 100) : 0
              },
              results: []
            });
            let message = `Bulk addition completed: ${successful}/${total} accounts logged in`;
            if (skipped > 0) message += `, ${skipped} skipped`;
            if (failed > 0) message += `, ${failed} failed`;
            toast.success(message);
            if (!isFetchingAccountsRef.current && !fetchAccountsTimeoutRef.current) {
              fetchAccountsTimeoutRef.current = setTimeout(() => {
                fetchAccounts(false);
                fetchGroups?.();
                fetchAccountsTimeoutRef.current = null;
              }, 1500);
            }
          } else {
            toast('Bulk add cancelled');
          }
        } else if (p.success && p.account) {
          // Legacy inline path may still send full account objects
          setAccounts(prev => {
            if (prev.some(a => a._id === p.account._id)) return prev;
            return [p.account, ...prev];
          });
        }
      });
      
      socket.on('bulk-login-test-progress', (p) => {
        const myUserId = user._id || user.id;
        if (p.userId && myUserId && String(p.userId) !== String(myUserId)) return;

        const progress = {
          current: p.current || 0,
          total: p.total || 0,
          currentAccount: p.currentAccount || null,
          status: p.status || null,
          successCount: p.successCount || 0,
          failCount: p.failCount || 0
        };
        
        setBulkLoginTestProgress(progress);
        
        // Clear stale check timeout since we received a socket event
        if (staleCheckTimeoutRef.current) {
          clearTimeout(staleCheckTimeoutRef.current);
          staleCheckTimeoutRef.current = null;
        }
        
        // Save to sessionStorage (except when completed, we'll clear it)
        if (p.status !== 'completed') {
          try {
            sessionStorage.setItem('bulkLoginTestProgress', JSON.stringify(progress));
          } catch (error) {
            console.error('Error saving bulk login test progress to sessionStorage:', error);
          }
        }
        
        // When status is 'completed', reset loading state and show completion toast
        if (p.status === 'completed') {
          setBulkOperationLoading(false);
          setSelectedAccounts([]);
          // REMOVED: fetchAccounts() - account-status-updated socket events handle status updates
          // Don't refetch as it can overwrite state with empty/invalid data
          
          // Clear from sessionStorage
          try {
            sessionStorage.removeItem('bulkLoginTestProgress');
          } catch (error) {
            console.error('Error clearing bulk login test progress from sessionStorage:', error);
          }
          
          // Show completion toast
          if (p.successCount !== undefined && p.failCount !== undefined) {
            toast.success(`Login tests completed: ${p.successCount} successful, ${p.failCount} failed`);
          }
          
          // Reset progress after a delay to show completion
          setTimeout(() => {
            setBulkLoginTestProgress({ current: 0, total: 0, currentAccount: null, status: null, successCount: 0, failCount: 0 });
          }, 3000);
        }
      });

      socket.on('bulk-captcha-solve-progress', (p) => {
        setBulkCaptchaSolveProgress({
          current: p.current || 0,
          total: p.total || 0,
          currentAccount: p.currentAccount || null,
          status: p.status || null,
          successCount: p.successCount || 0,
          failCount: p.failCount || 0,
          message: p.message || ''
        });

        if (p.status === 'completed' || p.status === 'cancelled') {
          setBulkOperationLoading(false);
          setSelectedAccounts([]);
          if (p.successCount !== undefined && p.failCount !== undefined) {
            toast.success(`CAPTCHA solve: ${p.successCount} solved, ${p.failCount} failed`);
          }
          setTimeout(() => {
            setBulkCaptchaSolveProgress({ current: 0, total: 0, currentAccount: null, status: null, successCount: 0, failCount: 0, message: '' });
          }, 3000);
        }
      });
      
      socket.on('add-signing-key-progress', applyAddSigningKeyProgress);

      socket.on('bulk-start-progress', (p) => {
        let progress;
        setBulkStartProgress((prev) => {
          progress = mergeBulkStartProgress(prev, p);
          return progress;
        });
        
        // Clear stale check timeout since we received a socket event
        if (staleCheckTimeoutRef.current) {
          clearTimeout(staleCheckTimeoutRef.current);
          staleCheckTimeoutRef.current = null;
        }
        
        // Save to sessionStorage (except when completed/cancelled, we'll clear it)
        if (p.status !== 'completed' && p.status !== 'cancelled') {
          try {
            sessionStorage.setItem('bulkStartProgress', JSON.stringify(progress));
          } catch (error) {
            console.error('Error saving bulk start progress to sessionStorage:', error);
          }
        }
        
        // When status is 'completed' or 'cancelled', reset loading state
        if (p.status === 'completed' || p.status === 'cancelled') {
          setBulkOperationLoading(false);
          setSelectedAccounts([]);
          // Clear bulk start account tracking
          bulkStartAccountIdsRef.current = new Set();

          // Set results for display
          setBulkOperationResults({
            successCount: p.successCount || 0,
            failCount: p.failCount || 0,
            total: p.total || 0
          });
          
          // REMOVED: fetchAccounts() - socket events (automation-started) already update accounts state
          // Don't refetch as it can overwrite state with empty/invalid data
          
          // Clear from sessionStorage
          try {
            sessionStorage.removeItem('bulkStartProgress');
          } catch (error) {
            console.error('Error clearing bulk start progress from sessionStorage:', error);
          }
          
          if (p.status === 'completed' && p.successCount !== undefined && p.failCount !== undefined) {
            toast.success(`Automation started: ${p.successCount} successful, ${p.failCount} failed`);
          } else if (p.status === 'cancelled') {
            toast.info(`Bulk start cancelled: ${p.successCount || 0} completed before cancellation`);
          }
          
          // Reset progress after a delay to show completion
          setTimeout(() => {
            setBulkStartProgress({ current: 0, total: 0, currentAccount: null, successCount: 0, failCount: 0, status: null, operationId: null });
            setBulkOperationResults(null); // Clear results after display
          }, 5000); // Increased delay to 5 seconds so users can see results
        }
      });

      if (user?.role === 'admin') {
        socket.on('restart-automations-progress', (p) => {
          const current = p.current || 0;
          const total = p.total || 0;
          const status = p.status || null;
          if (status === 'queued' || status === 'processing') {
            restartInProgressRef.current = true;
          } else if (status === 'completed' || status === 'cancelled') {
            restartInProgressRef.current = false;
          }
          setRestartProgress((prev) => {
            const next = {
              current,
              total,
              currentAccount: p.currentAccount || null,
              successCount: p.successCount || 0,
              failCount: p.failCount || 0,
              status: status || prev.status,
              operationId: p.operationId || prev.operationId
            };
            if (status === 'completed' || status === 'cancelled') return next;
            if (prev.total > 0 && total !== prev.total) return prev;
            if (current < prev.current) return prev;
            return next;
          });
          if (status === 'completed' || status === 'cancelled') {
            setTimeout(() => {
              setRestartProgress({ current: 0, total: 0, currentAccount: null, successCount: 0, failCount: 0, status: null, operationId: null });
            }, 5000);
          }
        });
      }
      
      socket.on('bulk-update-auth-progress', (p) => {
        const progress = {
          current: p.current || 0,
          total: p.total || 0,
          currentAccount: p.currentAccount || null,
          status: p.status || null,
          successCount: p.successCount || 0,
          failCount: p.failCount || 0,
          notFoundCount: p.notFoundCount || 0
        };
        
        setBulkUpdateAuthProgress(progress);
        
        if (p.status === 'completed') {
          setBulkOperationLoading(false);
          setShowUpdateAuthModal(false);
          setUpdateAuthText('');
          // Don't fetch if one is already in progress or scheduled
          if (!isFetchingAccountsRef.current && !fetchAccountsTimeoutRef.current) {
            fetchAccounts(); // Show loading for bulk operations completion
          }
          
          toast.success(`Auth updated: ${p.successCount} successful, ${p.failCount} failed, ${p.notFoundCount} not found`);
          
          setTimeout(() => {
            setBulkUpdateAuthProgress({ current: 0, total: 0, currentAccount: null, status: null, successCount: 0, failCount: 0, notFoundCount: 0 });
          }, 3000);
        }
      });

      socket.on('bulk-renew-auth-progress', (p) => {
        const progress = {
          current: p.current || 0,
          total: p.total || 0,
          currentAccount: p.currentAccount || null,
          status: p.status || null,
          successCount: p.successCount || 0,
          failCount: p.failCount || 0,
          skippedCount: p.skippedCount || 0,
        };
        setBulkRenewAuthProgress(progress);
        if (p.status === 'completed') {
          setBulkOperationLoading(false);
          setSelectedAccounts([]);
          if (!isFetchingAccountsRef.current && !fetchAccountsTimeoutRef.current) {
            fetchAccountsRef.current?.();
          }
          toast.success(`Auth renewal completed: ${p.successCount} renewed, ${p.failCount} failed, ${p.skippedCount} skipped`);
          setTimeout(() => {
            setBulkRenewAuthProgress({ current: 0, total: 0, currentAccount: null, status: null, successCount: 0, failCount: 0, skippedCount: 0 });
          }, 5000);
        } else if (p.status === 'cancelled') {
          setBulkOperationLoading(false);
          toast.success('Auth renewal cancelled');
          setTimeout(() => {
            setBulkRenewAuthProgress({ current: 0, total: 0, currentAccount: null, status: null, successCount: 0, failCount: 0, skippedCount: 0 });
          }, 3000);
        }
      });

      socket.on('bulk-update-proxy-progress', (p) => {
        const progress = {
          current: p.current || 0,
          total: p.total || 0,
          currentAccount: p.currentAccount || null,
          status: p.status || null,
          successCount: p.successCount || 0,
          failCount: p.failCount || 0,
          notFoundCount: p.notFoundCount || 0
        };
        setBulkUpdateProxyProgress(progress);
        if (p.status === 'completed') {
          setBulkOperationLoading(false);
          setShowUpdateProxyModal(false);
          setUpdateProxyText('');
          if (!isFetchingAccountsRef.current && !fetchAccountsTimeoutRef.current) {
            fetchAccountsRef.current?.();
          }
          toast.success(`Proxy updated: ${p.successCount} successful, ${p.failCount} failed, ${p.notFoundCount} not found`);
          setTimeout(() => {
            setBulkUpdateProxyProgress({ current: 0, total: 0, currentAccount: null, status: null, successCount: 0, failCount: 0, notFoundCount: 0 });
          }, 3000);
        }
      });
      
      return () => {
        socket.off('automation-started');
        socket.off('automation-stopped');
        socket.off('automation-update');
        socket.off('all-automations-stopped');
        socket.off('bulk-update-auth-progress');
        socket.off('bulk-renew-auth-progress');
        socket.off('bulk-update-proxy-progress');
        socket.off('message-sent');
        socket.off('account-status-updated');
        socket.off('account-status-updated-batch');
        socket.off('bulk-add-progress');
        socket.off('bulk-login-test-progress');
        socket.off('bulk-captcha-solve-progress');
        socket.off('add-signing-key-progress');
        socket.off('bulk-start-progress');
        socket.off('restart-automations-progress');
      };
    }
  }, [socket, user, handleAccountStatusUpdate, handleAccountStatusUpdateBatch, handleAutomationUpdate, handleMessageSent, refreshAccountSummary, applyAddSigningKeyProgress]);

  useEffect(() => {
    let cancelled = false;
    const fetchSnapshot = async () => {
      const paths = addSigningKeyProgress.kind === 'recover'
        ? ['/accounts/bulk-add-signing-key/status']
        : addSigningKeyProgress.kind === 'reset-pin'
          ? ['/accounts/bulk-reset-xchat-pin/status']
          : ['/accounts/bulk-reset-xchat-pin/status', '/accounts/bulk-add-signing-key/status'];
      for (const path of paths) {
        try {
          const { data } = await api.get(path);
          if (cancelled) return;
          const job = data?.job;
          if (!job) continue;
          if (job.status === 'queued' || job.status === 'processing' || job.status === 'cancelling') {
            applyAddSigningKeyProgress(job);
            return;
          }
          const uiLive = addSigningKeyProgress.status === 'queued'
            || addSigningKeyProgress.status === 'processing'
            || addSigningKeyProgress.status === 'cancelling';
          if (
            uiLive &&
            (job.status === 'cancelled' || job.status === 'completed' || job.status === 'failed')
          ) {
            applyAddSigningKeyProgress(job);
            return;
          }
        } catch (_) { /* ignore */ }
      }
    };
    fetchSnapshot();
    const poll = addSigningKeyProgress.status === 'cancelling'
      ? setInterval(fetchSnapshot, 3000)
      : null;
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchSnapshot();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [connected, addSigningKeyProgress.kind, addSigningKeyProgress.operationId, addSigningKeyProgress.status, applyAddSigningKeyProgress]);

  // Conversations tab: no auto-refresh — use the manual refresh button instead


  const fetchModels = async () => {
    try {
      const response = await api.get('/models?minimal=1');
      setModels(response.data);
    } catch (error) {
      toast.error('Failed to fetch models');
      console.error('Error fetching models:', error);
    }
  };

  const fetchPresets = async () => {
    try {
      const response = await api.get('/models/presets/list');
      setPresets(response.data);
    } catch (error) {
      console.error('Error fetching presets:', error);
    }
  };

  // Helper function to get preset name from preset key
  const getPresetName = (presetKey) => {
    if (!presetKey) return null;
    const preset = presets.find(p => p.key === presetKey);
    return preset ? preset.name : presetKey;
  };

  const fetchGroups = async () => {
    try {
      const response = await api.get('/accounts/groups');
      if (response.data.success) {
        const nextLeafGroups = response.data.leafGroups || [];
        const nextMainGroups = response.data.mainGroups || [];
        const nextLeafDetailed = response.data.leafGroupsDetailed || [];

        const nextMainByName = {};
        for (const g of nextMainGroups) {
          if (!g || !g.name) continue;
          nextMainByName[g.name] = {
            name: g.name,
            subGroups: Array.isArray(g.subGroups) ? g.subGroups : []
          };
        }

        // Build the metadata map covering BOTH main and leaf groups.
        // Server returns `automation` + `attachedAgent` only on groups
        // that actually have them; plain groups don't appear.
        const nextMeta = {};
        for (const g of nextMainGroups) {
          if (!g || !g.name) continue;
          if (g.automation || g.attachedAgent) {
            nextMeta[g.name] = {
              automation: g.automation || null,
              attachedAgent: g.attachedAgent || null
            };
          }
        }
        for (const g of nextLeafDetailed) {
          if (!g || !g.name) continue;
          if (g.automation || g.attachedAgent) {
            nextMeta[g.name] = {
              automation: g.automation || null,
              attachedAgent: g.attachedAgent || null
            };
          }
        }
        setGroupMetaByName(nextMeta);

        setLeafGroups(nextLeafGroups);
        setMainGroupsByName(nextMainByName);
        // "Folder view":
        // - Leaf groups that are used as a subgroup anywhere are hidden from top-level tabs.
        // - Main groups that are used as a subgroup anywhere are also hidden from top-level tabs.
        const usedAsSubGroupNames = new Set();
        for (const mg of nextMainGroups) {
          for (const sg of (Array.isArray(mg.subGroups) ? mg.subGroups : [])) {
            if (typeof sg === 'string' && sg.trim()) usedAsSubGroupNames.add(sg.trim());
          }
        }

        const topLevelLeafGroups = nextLeafGroups.filter(g => !usedAsSubGroupNames.has(g));
        const topLevelMainGroups = nextMainGroups
          .map(g => g.name)
          .filter(name => !usedAsSubGroupNames.has(name));

        setGroups(Array.from(new Set([...topLevelLeafGroups, ...topLevelMainGroups])).sort());
        setModelGroups(Array.isArray(response.data.models) ? response.data.models : []);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const isMainGroupName = (groupName) => !!mainGroupsByName?.[groupName];

  // Resolve a main group into the set of leaf groups under it.
  // Leaf group means: its name is stored on Account.group.
  const resolveLeafGroupsForGroupName = useCallback((groupName) => {
    const cache = resolvedLeafGroupsCacheRef.current;
    if (!groupName) return new Set();
    if (cache.has(groupName)) return cache.get(groupName);

    const visiting = new Set();

    const resolve = (name) => {
      if (!name) return new Set();
      if (cache.has(name)) return cache.get(name);
      if (visiting.has(name)) return new Set(); // cycle-safety (should be prevented server-side)

      visiting.add(name);
      const main = mainGroupsByName?.[name];

      let result;
      if (main) {
        // Folder name is itself a leaf, so unsorted accounts stay visible
        // in the folder until they move into a subgroup.
        result = new Set([name]);
        const children = Array.isArray(main.subGroups) ? main.subGroups : [];
        for (const child of children) {
          if (!child) continue;
          if (mainGroupsByName?.[child]) {
            for (const leaf of resolve(child)) result.add(leaf);
          } else {
            // Child is a leaf group (stored on Account.group)
            result.add(child);
          }
        }
      } else {
        // Leaf group: the name itself
        result = new Set([name]);
      }

      cache.set(name, result);
      visiting.delete(name);
      return result;
    };

    return resolve(groupName);
  }, [mainGroupsByName]);


  const handleEditAccount = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Convert simple format to full format for backend parsing
      let xAccountString = '';
      if (formData.accountStringSimple && formData.accountStringSimple.trim()) {
        const parts = formData.accountStringSimple.split(':');
        if (parts.length >= 2) {
          const username = parts[0].trim();
          const authToken = parts.slice(1).join(':'); // In case token contains colons
          // Convert to full format: username::::authToken:
          xAccountString = `${username}::::${authToken}:`;
        }
      }

      // Build tags array from checkboxes
      const tags = [];
      if (formData.scraperTag) {
        tags.push('scraper');
      }
      if (formData.activeCheckerTag) {
        tags.push('active_checker');
      }
      if (formData.sourceTag) {
        tags.push('source');
      }
      if (formData.followBackCheckerTag) {
        tags.push('follow_back_checker');
      }
      if (formData.viewCheckerTag) {
        tags.push('view_checker');
      }
      
      const accountData = {
        xAccountString: xAccountString,
        userAgent: formData.userAgent,
        modelId: formData.modelId || null,
        tags: tags
      };

      // Include XChat private key if provided
      if (formData.xchatPrivateKey && formData.xchatPrivateKey.trim()) {
        accountData.xchatPrivateKey = formData.xchatPrivateKey.trim();
      }

      // Handle proxy string - if provided, use it
      if (formData.proxyString && formData.proxyString.trim()) {
        accountData.newProxy = {
          proxyString: formData.proxyString.trim()
        };
        accountData.proxy = {
          enabled: true
        };
      } else {
        // If proxy string is empty, disable proxy
        accountData.proxy = {
          enabled: false
        };
      }

      const response = await api.put(`/accounts/${selectedAccount._id}`, accountData);
      toast.success('Account updated successfully');
      
      // Update the account in local state instead of refetching all accounts
      // This preserves filters, pagination, and doesn't cause a full page refresh
      if (response.data) {
        const updatedAccount = response.data;
        setAccounts(prevAccounts => 
          prevAccounts.map(account => 
            account._id === selectedAccount._id 
              ? {
                  ...updatedAccount,
                  automation: {
                    ...updatedAccount.automation,
                    enabled: updatedAccount.automation?.enabled || false,
                    starting: account.automation?.starting || false // Preserve starting state
                  }
                }
              : account
          )
        );
      }
      
      setShowEditModal(false);
      setSelectedAccount(null);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update account');
      console.error('Error updating account:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this account?')) return;
    
    try {
      await api.delete(`/accounts/${accountId}`);
      toast.success('Account deleted successfully');
      fetchAccounts();
    } catch (error) {
      toast.error('Failed to delete account');
      console.error('Error deleting account:', error);
    }
  };


  const handleStartAutomation = async (accountId) => {
    // Set starting state immediately (button will be disabled and show "Starting...")
    setAccounts(prev => prev.map(account => 
      account._id === accountId 
        ? { ...account, automation: { ...account.automation, enabled: false, starting: true } }
        : account
    ));
    
    // Fire the API call but don't wait for it - rely on socket events for status updates
    // The API call will complete when Step 6 finishes, but HTTP timeouts might occur
    // Socket events are the source of truth for UI updates
    const serviceUrl = serviceUrls.automation;
    
    if (!serviceUrl) {
      console.error('[Accounts] No automation service URL available. serviceUrls:', serviceUrls);
      toast.error('Automation service not available');
      setAccounts(prev => prev.map(account => 
        account._id === accountId 
          ? { ...account, automation: { ...account.automation, enabled: false, starting: false } }
          : account
      ));
      return;
    }
    
    const requestUrl = `${serviceUrl}/api/automation/start/${accountId}`;
    
    try {
      await axios.post(requestUrl, {}, {
        timeout: 60000,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // API call succeeded - update UI immediately (like stop does)
      // Socket event will still arrive and update state, but this ensures UI updates even if socket fails
      setAccounts(prev => prev.map(account => 
        account._id === accountId 
          ? { ...account, automation: { ...account.automation, enabled: true, starting: false } }
          : account
      ));
    } catch (error) {
      // Error occurred - reset starting state immediately
      setAccounts(prev => prev.map(account => 
        account._id === accountId 
          ? { ...account, automation: { ...account.automation, enabled: false, starting: false } }
          : account
      ));
      
      // Only show error toast if it's not a timeout (timeout might mean job is still processing)
      if (error.code !== 'ECONNABORTED' && !error.message?.includes('timeout')) {
        toast.error(error.response?.data?.message || 'Failed to start automation');
      } else {
        // Timeout - job might still be processing, keep starting state but log
        // Don't reset starting state on timeout - let socket event handle it
        // But set a shorter timeout to reset if no socket event arrives
        setTimeout(() => {
          setAccounts(prev => prev.map(account => {
            if (account._id === accountId && account.automation?.starting && !account.automation?.enabled) {
              // Still in starting state after 2 minutes - something went wrong
              return {
                ...account,
                automation: {
                  ...account.automation,
                  enabled: false,
                  starting: false
                }
              };
            }
            return account;
          }));
        }, 120000); // 2 minutes (shorter than the 5-minute safety timeout)
      }
      console.error('[Accounts] Error starting automation:', error);
    }
    
    // Set a safety timeout to reset if no socket event arrives after 5 minutes
    // This handles edge cases where socket events might not arrive
    setTimeout(() => {
      setAccounts(prev => prev.map(account => {
        if (account._id === accountId && account.automation?.starting && !account.automation?.enabled) {
          // Still in starting state after 5 minutes - something went wrong
          return {
            ...account,
            automation: {
              ...account.automation,
              enabled: false,
              starting: false
            }
          };
        }
        return account;
      }));
    }, 300000); // 5 minutes
  };

  const handleStopAutomation = async (accountId) => {
    try {
      const serviceUrl = serviceUrls.automation;
      
      if (!serviceUrl) {
        console.error('[Accounts] No automation service URL available');
        toast.error('Automation service not available');
        return;
      }
      
      const requestUrl = `${serviceUrl}/api/automation/stop/${accountId}`;
      
      await axios.post(requestUrl, {}, {
        timeout: 60000,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Update local state immediately
      setAccounts(prev => prev.map(account => 
        account._id === accountId 
          ? { ...account, automation: { ...account.automation, enabled: false, starting: false } }
          : account
      ));
    } catch (error) {
      console.error('[Accounts] Error stopping automation:', error);
    }
  };

  const openEditModal = async (account) => {
    setSelectedAccount(account);
    
    try {
      // Fetch full account details to get xSettings.accessToken
      const fullAccountResponse = await api.get(`/accounts/${account._id}`);
      const fullAccount = fullAccountResponse.data;
      
      // Build simple format string: username:authToken
      const authToken = fullAccount.xSettings?.accessToken || '';
      const accountStringSimple = authToken ? `${fullAccount.username}:${authToken}` : '';
      
      // Get proxy string from account data
      let proxyString = '';
      if (fullAccount.proxy && fullAccount.proxy.enabled) {
        if (fullAccount.proxy.host && fullAccount.proxy.port) {
          const username = fullAccount.proxy.username || '';
          const password = fullAccount.proxy.password || '';
          proxyString = `${fullAccount.proxy.host}:${fullAccount.proxy.port}:${username}:${password}`;
        }
      }
      
      // Handle modelId - extract _id if it's a populated object
      const modelId = typeof fullAccount.modelId === 'object' && fullAccount.modelId !== null 
        ? (fullAccount.modelId._id ? fullAccount.modelId._id.toString() : fullAccount.modelId.toString())
        : (fullAccount.modelId ? fullAccount.modelId.toString() : null);
      
      // Extract tags
      const tags = fullAccount.tags || [];
      const scraperTag = tags.includes('scraper');
      const activeCheckerTag = tags.includes('active_checker');
      const sourceTag = tags.includes('source');
      const followBackCheckerTag = tags.includes('follow_back_checker');
      const viewCheckerTag = tags.includes('view_checker');

      setFormData({
        username: fullAccount.username || '',
        accountStringSimple: accountStringSimple, // Simple format: username:authToken
        proxyString: proxyString,
        userAgent: fullAccount.userAgent || 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/142.0.0.0 Safari/537.36',
        modelId: modelId,
        scraperTag: scraperTag,
        activeCheckerTag: activeCheckerTag,
        sourceTag: sourceTag,
        followBackCheckerTag: followBackCheckerTag,
        viewCheckerTag: viewCheckerTag,
        xchatPrivateKey: fullAccount.xchat?.privateKeyHex || ''
      });
      setShowEditModal(true);
    } catch (error) {
      console.error('Error fetching account details:', error);
      toast.error('Failed to load account details');
    }
  };

  // Helper functions to count accounts and proxies
  const getAccountCount = () => {
    // Use custom format accounts
    return customFormatAccounts
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0).length;
  };

  const getProxyCount = () => {
    return bulkFormData.proxies
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0).length;
  };

  // Custom format drag-and-drop handlers
  const handleDragStart = (e, fieldId) => {
    setDraggedField(fieldId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedField === null) return;

    const draggedIndex = customFormatFields.findIndex(f => f.id === draggedField);
    if (draggedIndex === -1 || draggedIndex === targetIndex) {
      setDraggedField(null);
      return;
    }

    const newFields = [...customFormatFields];
    const [removed] = newFields.splice(draggedIndex, 1);
    newFields.splice(targetIndex, 0, removed);
    setCustomFormatFields(newFields);
    setDraggedField(null);
  };

  const toggleFieldEnabled = (fieldId) => {
    setCustomFormatFields(fields =>
      fields.map(f => f.id === fieldId ? { ...f, enabled: !f.enabled } : f)
    );
  };

  const getFormatPreview = () => {
    const enabledFields = customFormatFields.filter(f => f.enabled);
    if (enabledFields.length === 0) return 'No fields enabled';
    
    const exampleValues = {
      username: 'example_user',
      password: 'password123',
      accessToken: 'auth_token_here',
      ct0: 'hex_csrf_token_optional',
      proxyString: '192.168.1.1:8080:user:pass:proxy_auth',
      email: 'user@example.com',
      emailPassword: 'email_pass123',
      displayName: 'Display Name',
      phone: '+1234567890',
      sessionData: 'session_data',
      unknown: 'unknown',
      userAgent: 'Mozilla/5.0...',
      group: 'group_name',
      tags: 'tag1,tag2'
    };

    return enabledFields.map(f => exampleValues[f.id] || f.label).join(` ${customFormatDelimiter} `);
  };

  // Available fields for custom format
  const availableFields = [
    { id: 'username', label: 'Username' },
    { id: 'password', label: 'Password' },
    { id: 'accessToken', label: 'Auth Token' },
    { id: 'ct0', label: 'ct0 (CSRF)' },
    { id: 'proxyString', label: 'Proxy (IP:PORT:USERNAME:PASSWORD[:proxy_auth])' },
    { id: 'email', label: 'Email' },
    { id: 'emailPassword', label: 'Email password' },
    { id: 'displayName', label: 'Display Name' },
    { id: 'phone', label: 'Phone' },
    { id: 'sessionData', label: 'Session Data' },
    { id: 'unknown', label: 'Unknown' },
    { id: 'userAgent', label: 'User Agent' },
    { id: 'group', label: 'Group' },
    { id: 'tags', label: 'Tags' }
  ];

  const addFieldToFormat = (fieldId) => {
    const field = availableFields.find(f => f.id === fieldId);
    if (!field) return;
    
    const alreadyAdded = customFormatFields.find(f => f.id === fieldId);
    if (alreadyAdded) return;

    setCustomFormatFields([...customFormatFields, { ...field, enabled: true }]);
  };

  const removeFieldFromFormat = (fieldId) => {
    setCustomFormatFields(fields => fields.filter(f => f.id !== fieldId));
  };

  // Bulk Account Functions
  const handleBulkAddAccounts = async (e) => {
    e.preventDefault();
    setBulkProcessing(true);
    setBulkAddRunning(true);
    bulkAddOperationIdRef.current = null;
    setBulkAddProgress({
      total: 0,
      index: 0,
      items: [],
      status: 'starting',
      successCount: 0,
      failCount: 0,
      skippedCount: 0,
      currentAccount: null,
      operationId: null
    });
    setBulkResults(null);
    
    let response = null; // Declare response at function scope so it's accessible in catch block
    
    try {
      // Validate Model Preset is selected
      if (!bulkFormData.modelId || bulkFormData.modelId.trim() === '') {
        toast.error('Please select a Model Preset to proceed');
        setBulkProcessing(false);
        setBulkAddRunning(false);
        return;
      }

      // Parse custom format accounts
      const accountLines = customFormatAccounts
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      if (accountLines.length === 0) {
        toast.error('Please enter at least one account string');
        setBulkProcessing(false);
        setBulkAddRunning(false);
        return;
      }

      // Get enabled fields in order
      const enabledFields = customFormatFields.filter(f => f.enabled);
      if (enabledFields.length === 0) {
        toast.error('Please enable at least one field in the format');
        setBulkProcessing(false);
        setBulkAddRunning(false);
        return;
      }

      // Parse each account line based on custom format
      const parsedAccounts = accountLines.map((line, idx) => {
        // Split by delimiter, but handle cases where delimiter might be in values
        const parts = line.split(customFormatDelimiter);
        
        // Create account object with default values
        const account = {
          username: '',
          password: '',
          email: '',
          emailPassword: '',
          displayName: '',
          phone: '',
          sessionData: '',
          accessToken: '',
          ct0: '',
          unknown: '',
          userAgent: bulkFormData.userAgent || 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/142.0.0.0 Safari/537.36',
          proxyProtocol: bulkFormData.proxyProtocol || 'http',
          group: bulkFormData.group && bulkFormData.group.trim() !== '' ? bulkFormData.group.trim() : null,
          tags: [
            ...(bulkFormData.addScraperTag ? ['scraper'] : []),
            ...(bulkFormData.addActiveCheckerTag ? ['active_checker'] : []),
            ...(bulkFormData.addFollowBackCheckerTag ? ['follow_back_checker'] : []),
            ...(bulkFormData.addViewCheckerTag ? ['view_checker'] : [])
          ]
        };

        // Map parts to fields based on enabled fields order
        let partIndex = 0;
        enabledFields.forEach((field, fieldIndex) => {
          // Skip proxyString if proxies are added separately
          if (field.id === 'proxyString' && addProxiesSeparately) {
            partIndex++; // Still increment to skip this part in the array
            return;
          }

          // Proxy strings contain the delimiter (host:port:user:pass[:proxy_auth]).
          // Consume enough parts so later fields still get one segment each.
          let value;
          if (field.id === 'proxyString' && partIndex < parts.length) {
            const remainingFieldsAfter = enabledFields.length - fieldIndex - 1;
            const remainingParts = parts.length - partIndex;
            if (remainingFieldsAfter === 0) {
              value = parts.slice(partIndex).join(customFormatDelimiter).trim();
              partIndex = parts.length;
            } else {
              const take = Math.max(1, remainingParts - remainingFieldsAfter);
              value = parts.slice(partIndex, partIndex + take).join(customFormatDelimiter).trim();
              partIndex += take;
            }
          } else {
            value = (parts[partIndex] || '').trim();
            partIndex++;
          }
          
          switch (field.id) {
            case 'username':
              account.username = value;
              account.email = value ? `${value}@placeholder.local` : '';
              account.displayName = value;
              break;
            case 'password':
              account.password = value;
              break;
            case 'accessToken':
              account.accessToken = value;
              break;
            case 'ct0':
              account.ct0 = value;
              break;
            case 'proxyString':
              account.proxyString = value;
              break;
            case 'email':
              account.email = value || account.email;
              break;
            case 'emailPassword':
              account.emailPassword = value;
              break;
            case 'displayName':
              account.displayName = value || account.displayName;
              break;
            case 'phone':
              account.phone = value;
              break;
            case 'sessionData':
              account.sessionData = value;
              break;
            case 'unknown':
              account.unknown = value;
              break;
            case 'userAgent':
              account.userAgent = value || account.userAgent;
              break;
            case 'group':
              account.group = value || account.group;
              break;
            case 'tags':
              if (value) {
                account.tags = [...account.tags, ...value.split(',').map(t => t.trim()).filter(t => t)];
              }
              break;
            default:
              // Unknown field, skip
              break;
          }
        });

        // If proxies are separate, get from proxy strings
        if (addProxiesSeparately) {
          const proxyStrings = bulkFormData.proxies
            .split('\n')
            .map(line => line.trim())
            .filter(line => line.length > 0);
          account.proxyString = proxyStrings[idx] || '';
        }

        return account;
      });

      // Validate proxies if separate (skip when auto-assign proxies is enabled)
      if (addProxiesSeparately && !bulkFormData.autoAssignProxy) {
        const proxyStrings = bulkFormData.proxies
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0);

        if (proxyStrings.length === 0) {
          toast.error('Proxy strings are required. Please provide at least one proxy.');
          setBulkProcessing(false);
          setBulkAddRunning(false);
          return;
        }

        if (proxyStrings.length !== parsedAccounts.length) {
          const missing = parsedAccounts.length - proxyStrings.length;
          setProxyValidationData({
            accountCount: parsedAccounts.length,
            proxyCount: proxyStrings.length,
            missing: missing
          });
          setShowProxyValidationModal(true);
          setBulkProcessing(false);
          setBulkAddRunning(false);
          return;
        }
      }

      setBulkAddProgress({
        total: parsedAccounts.length,
        index: 0,
        items: [],
        status: 'starting',
        successCount: 0,
        failCount: 0,
        skippedCount: 0,
        currentAccount: null,
        operationId: null
      });

      const selectedTasks = Object.entries(bulkFormData.postUploadTasks || {})
        .filter(([, enabled]) => enabled)
        .map(([task]) => task);

      // Enqueue ack only — work runs on BullMQ across API replicas; progress via sockets.
      try {
        response = await api.post('/accounts/bulk-add-with-login', {
          accounts: parsedAccounts,
          modelId: bulkFormData.modelId || null,
          ...(bulkFormData.autoAssignProxy && {
            autoAssignProxy: true,
            maxAccountsPerProxy: Math.max(1, parseInt(bulkFormData.maxAccountsPerProxy, 10) || 4),
            ...(bulkFormData.autoAssignProxyRequireAuth && { autoAssignProxyRequireAuth: true })
          }),
          ...(bulkFormData.disableProxyPoolReplace && { disableProxyPoolReplace: true }),
          ...(selectedTasks.length > 0 && { postUploadTasks: selectedTasks })
        }, {
          // Body upload + enqueue for 10k accounts; login work is async after this returns
          timeout: 300000
        });
      } catch (error) {
        // Check if we got a response even though there was an error
        // This can happen if response was sent but connection errored afterward
        if (error.response && error.response.data) {
          response = error.response;
        } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          // Long enqueue — keep UI running; latch operationId from Redis progress if present
          toast('Enqueue is taking longer than expected — watching live progress…');
          setBulkProcessing(false);
          try {
            const { data: prog } = await api.get('/accounts/bulk-add-with-login/progress');
            if (prog?.operationId && prog?.status === 'testing') {
              bulkAddOperationIdRef.current = prog.operationId;
              setBulkAddProgress((prev) => ({
                ...prev,
                operationId: prog.operationId,
                total: prog.total || prev.total || parsedAccounts.length,
                index: prog.index != null ? prog.index : prev.index,
                status: prog.status || 'testing',
                successCount: prog.successCount || 0,
                failCount: prog.failCount || 0,
                skippedCount: prog.skippedCount || 0
              }));
            }
          } catch (_) { /* non-fatal */ }
          response = { data: { queued: true, total: parsedAccounts.length, operationId: bulkAddOperationIdRef.current } };
          return;
        } else {
          // No response data, show error
          toast.error(error.response?.data?.error || 'Failed to add accounts in bulk');
          console.error('Error adding accounts in bulk:', error);
          throw error;
        }
      }

      // Process response if we have one
      if (response && response.data) {
        // Queued path: HTTP returns immediately; sockets + Redis poll drive completion
        if (response.data.queued) {
          if (response.data.operationId) {
            bulkAddOperationIdRef.current = response.data.operationId;
            setBulkAddProgress((prev) => ({
              ...prev,
              operationId: response.data.operationId,
              total: response.data.total || prev.total || parsedAccounts.length,
              status: 'testing'
            }));
          }
          toast.success(
            response.data.message ||
              `Queued ${response.data.total || parsedAccounts.length} accounts — login tests running across API replicas`
          );
          setBulkProcessing(false);
          // Keep bulkAddRunning true until socket/poll status completed/cancelled
          return;
        }

        const successful = response.data.successful || (response.data.results || []).filter(r => r.success).length;
        const skipped = response.data.skipped || (response.data.results || []).filter(r => r.skipped).length;
        const failed = response.data.failed || (response.data.results || []).filter(r => !r.success && !r.skipped).length;
        const total = response.data.total || (response.data.results || []).length;
        setBulkResults(response.data);
        
        let message = `Bulk addition completed: ${successful}/${total} accounts logged in`;
        if (skipped > 0) {
          message += `, ${skipped} skipped (duplicates)`;
        }
        if (failed > 0) {
          message += `, ${failed} failed`;
        }
        toast.success(message);

        // Post-upload tasks are now scheduled during bulk add (streaming) when postUploadTasks was sent.
        // Only call bulk-post-upload-tasks when we did NOT send postUploadTasks (e.g. old client or no tasks selected).
        if (selectedTasks.length > 0 && successful > 0) {
          toast.success(`Post-upload tasks scheduled as accounts were added (${successful} account(s)).`);
        }

        // Refresh accounts and groups after task scheduling
        try {
          await fetchAccounts();
          await fetchGroups();
        } catch (refreshError) {
          console.error('Error refreshing accounts/groups:', refreshError);
        }
      } else {
        // No response data at all
        toast.error('Failed to add accounts in bulk - no response received');
        console.error('No response data received');
      }
      
    } catch (error) {
      // Only show error if we haven't already processed a response
      if (!response || !response.data) {
        toast.error(error.response?.data?.error || 'Failed to add accounts in bulk');
        console.error('Error adding accounts in bulk:', error);
      } else {
        // We got a response, so log the error but don't show error toast
      }
    } finally {
      // Queued runs stay "running" until socket completed/cancelled
      if (!(response && response.data && response.data.queued)) {
        setBulkProcessing(false);
        setBulkAddRunning(false);
      } else {
        setBulkProcessing(false);
      }
    }
  };

  const handleCancelBulkAdd = async () => {
    if (!bulkAddRunning && bulkAddProgress.items.length === 0) {
      toast.error('No active bulk add operation to cancel');
      return;
    }

    try {
      const response = await api.post('/accounts/bulk-add-with-login/cancel');
      toast.success(response.data.message || 'Bulk add operation cancelled');
      
      // Clear the bulk add progress state
      bulkAddOperationIdRef.current = null;
      setBulkAddRunning(false);
      setBulkAddProgress({
        total: 0,
        index: 0,
        items: [],
        status: null,
        successCount: 0,
        failCount: 0,
        skippedCount: 0,
        currentAccount: null,
        operationId: null
      });
      setBulkProcessing(false);
      
      // Clear from sessionStorage if it exists
      try {
        sessionStorage.removeItem('bulkAddProgress');
      } catch (error) {
        console.error('Error clearing bulk add progress from sessionStorage:', error);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to cancel bulk add operation');
      console.error('Error cancelling bulk add:', error);
    }
  };

  const handleRetryFailedLogins = async () => {
    if (!bulkResults) return;
    
    setBulkProcessing(true);
    
    try {
      const failedAccountIds = bulkResults.results
        .filter(result => !result.loginResult?.success && result.account?._id)
        .map(result => result.account._id);
      
      if (failedAccountIds.length === 0) {
        toast('No failed accounts to retry');
        setBulkProcessing(false);
        return;
      }

      const response = await api.post('/bulk-accounts/retry-login', {
        accountIds: failedAccountIds
      });

      // Update bulk results with retry results
      const updatedResults = [...bulkResults.results];
      response.data.results.forEach(retryResult => {
        const resultIndex = updatedResults.findIndex(r => r.account?._id === retryResult.accountId);
        if (resultIndex !== -1) {
          updatedResults[resultIndex].loginResult = {
            success: retryResult.success,
            message: retryResult.success ? 'Login successful on retry' : retryResult.error,
            error: retryResult.error
          };
          if (retryResult.success) {
            updatedResults[resultIndex].account.status = 'active';
            updatedResults[resultIndex].account.lastLoginAt = new Date();
          }
        }
      });

      setBulkResults({
        ...bulkResults,
        results: updatedResults,
        summary: {
          ...bulkResults.summary,
          successful: updatedResults.filter(r => r.loginResult?.success).length,
          failed: updatedResults.filter(r => !r.loginResult?.success).length
        }
      });

      toast.success(`Retry completed: ${response.data.summary.successful}/${response.data.summary.total} accounts logged in successfully`);
      
      // Refresh accounts list
      fetchAccounts();
      
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to retry logins');
      console.error('Error retrying logins:', error);
    } finally {
      setBulkProcessing(false);
    }
  };

  const resetBulkForm = () => {
    setBulkFormData({
      accounts: '',
      accountsSimple: '',
      proxies: '',
      proxyProtocol: 'http',
      modelId: null,
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/142.0.0.0 Safari/537.36',
      addScraperTag: false,
      addActiveCheckerTag: false,
      addFollowBackCheckerTag: false,
      addViewCheckerTag: false,
      group: '',
      postUploadTasks: {
        privacy_check: false,
        dob_phone_check: false,
        extract_profile: false,
        fetch_username: false,
        update_bio: false
      },
      autoAssignProxy: false,
      maxAccountsPerProxy: 4,
      autoAssignProxyRequireAuth: false,
      disableProxyPoolReplace: false
    });
    setCustomFormatAccounts('');
    setCustomFormatFields([
      { id: 'username', label: 'Username', enabled: true },
      { id: 'password', label: 'Password', enabled: true },
      { id: 'accessToken', label: 'Auth Token', enabled: true },
      { id: 'proxyString', label: 'Proxy (IP:PORT:USERNAME:PASSWORD[:proxy_auth])', enabled: true }
    ]);
    setCustomFormatDelimiter(':');
    setAddProxiesSeparately(false);
    setBulkResults(null);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      accountStringSimple: '',
      proxyString: '',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/142.0.0.0 Safari/537.36',
    modelId: null,
    scraperTag: false,
    activeCheckerTag: false,
    sourceTag: false,
    followBackCheckerTag: false,
    viewCheckerTag: false
  });
  };

  // Bulk operation functions
  const handleSelectAccount = (accountId, event) => {
    const id = String(accountId);
    // Store scroll position before state update
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;
    
    // Check if shift key is pressed for range selection
    // Check both event.shiftKey, shiftKeyPressedRef, and event.nativeEvent?.shiftKey for compatibility
    const isShiftClick = (event && (event.shiftKey || event.nativeEvent?.shiftKey)) || shiftKeyPressedRef.current;
    
    // Debug logging (can be removed later)
    if (isShiftClick) {
    }
    
    // Get current display order (age-sorted) for shift-click range selection
    const currentFilteredAccounts = ageSortedFilteredAccounts;
    
    // Use functional update to avoid dependency on state
    setSelectedAccounts(prev => {
      // Handle shift-click range selection
      if (isShiftClick && lastSelectedIndexRef.current !== null && currentFilteredAccounts.length > 0) {
        // Find the current account's index in filteredAccounts
        const currentIndex = currentFilteredAccounts.findIndex(acc => String(acc._id) === id);
        
        if (currentIndex !== -1) {
          // Get the range between last selected and current
          const startIndex = Math.min(lastSelectedIndexRef.current, currentIndex);
          const endIndex = Math.max(lastSelectedIndexRef.current, currentIndex);
          
          // Get all account IDs in the range
          const rangeAccountIds = currentFilteredAccounts
            .slice(startIndex, endIndex + 1)
            .map(acc => String(acc._id));
          
          // Add all accounts in range to selection (merge with existing selection)
          const newSelection = [...new Set([...prev, ...rangeAccountIds])];
          
          // Update last selected index to current
          lastSelectedIndexRef.current = currentIndex;
          
          // Immediately restore scroll position
          requestAnimationFrame(() => {
            window.scrollTo(scrollX, scrollY);
          });
          setTimeout(() => {
            window.scrollTo(scrollX, scrollY);
          }, 0);
          
          return newSelection;
        } else {
        }
      } else if (isShiftClick) {
      }
      
      // Normal click behavior (toggle single account)
      const isSelected = prev.some((selectedId) => String(selectedId) === id);
      const newSelection = isSelected
        ? prev.filter((selectedId) => String(selectedId) !== id)
        : [...prev, id];
      
      // Update last selected index to current account's index in filteredAccounts
      if (!isSelected) {
        const currentIndex = currentFilteredAccounts.findIndex(acc => String(acc._id) === id);
        if (currentIndex !== -1) {
          lastSelectedIndexRef.current = currentIndex;
        }
      } else {
        // If deselecting, only clear lastSelectedIndex if this was the last selected account
        const currentIndex = currentFilteredAccounts.findIndex(acc => String(acc._id) === id);
        if (currentIndex === lastSelectedIndexRef.current) {
          lastSelectedIndexRef.current = null;
        }
      }
      
      // Immediately restore scroll position
      requestAnimationFrame(() => {
        window.scrollTo(scrollX, scrollY);
      });
      setTimeout(() => {
        window.scrollTo(scrollX, scrollY);
      }, 0);
      
      return newSelection;
    });
    
    // Reset shift key ref after handling
    shiftKeyPressedRef.current = false;
    
    // Prevent default behavior that might cause scrolling
    if (event) {
      event.stopPropagation();
    }
  };

  // Reset last selected index when selection is cleared
  useEffect(() => {
    if (selectedAccounts.length === 0) {
      lastSelectedIndexRef.current = null;
    }
  }, [selectedAccounts.length]);

  // Filter accounts based on selected filters
  // OPTIMIZED: Memoize getFilteredAccounts to prevent unnecessary recreations
  const getFilteredAccounts = useCallback(() => {
    const selectedLeafGroups = (
      selectedGroup !== 'all' &&
      selectedGroup !== 'ungrouped'
    )
      ? resolveLeafGroupsForGroupName(selectedGroup)
      : null;

    return accounts.filter((account) => {
      // Profile/status/model/group/search are applied server-side when paginated
      // or when any dashboard filter is active — trust the API result set.
      if (useServerScopeFilters) {
        return true;
      }

      if (!passesProfileFilter(account, filterProfile)) {
        return false;
      }

      // Filter by group
      if (selectedGroup !== 'all') {
        if (selectedGroup === 'ungrouped') {
          // Show only ungrouped accounts
          if (account.group && account.group.trim() !== '') {
            return false;
          }
        } else {
          // Show accounts in the selected group (leaf groups + all descendant leaf groups for main groups)
          if (!account.group || !selectedLeafGroups?.has(account.group)) {
            return false;
          }
        }
      }
      
      // Filter by search query (account name/username)
      if (searchQuery.trim() !== '') {
        const searchLower = searchQuery.toLowerCase().trim();
        const username = (account.username || '').toLowerCase();
        const displayName = (account.displayName || '').toLowerCase();
        if (!username.includes(searchLower) && !displayName.includes(searchLower)) {
          return false;
        }
      }
      
      // Filter by model
      if (filterModelId !== 'all') {
        const accountModelId = typeof account.modelId === 'object' ? account.modelId?._id || account.modelId : account.modelId;
        const filterModelIdStr = typeof filterModelId === 'object' ? filterModelId.toString() : filterModelId;
        if (String(accountModelId) !== String(filterModelIdStr)) {
          return false;
        }
      }
      
      // Filter by status
      if (filterStatus !== 'all') {
        if (filterStatus === 'active' && account.status !== 'active') {
          return false;
        }
        if (filterStatus === 'not_active' && account.status === 'active') {
          return false;
        }
        if (filterStatus === 'suspended' && account.status !== 'suspended') {
          return false;
        }
        if (filterStatus === 'banned' && account.status !== 'banned') {
          return false;
        }
        if (filterStatus === 'proxy_required' && account.status !== 'proxy_required') {
          return false;
        }
        if (filterStatus === 'proxy_failed' && account.status !== 'proxy_failed') {
          return false;
        }
        if (filterStatus === 'proxy_error' && account.status !== 'proxy_error') {
          return false;
        }
        if (filterStatus === 'login_failed' && account.status !== 'login_failed') {
          return false;
        }
        if (filterStatus === 'session_extraction_failed' && account.status !== 'session_extraction_failed') {
          return false;
        }
        if (filterStatus === 'auth_expired' && account.status !== 'auth_expired' && account.status !== 'session_extraction_failed') {
          return false;
        }
        if (filterStatus === 'captcha' && account.status !== 'captcha') {
          return false;
        }
        if (filterStatus === 'login_required' && account.status !== 'login_required') {
          return false;
        }
        if (filterStatus === 'daily_limit' && account.status !== 'daily_limit') {
          return false;
        }
      }
      
      // Automation filter:
      // - `automationQueueBooting`: BullMQ automation-start job queued/active on Redis (from GET /accounts).
      // - `automation.starting`: socket + merged queue boot for UI.
      // - `automationRunning`: colocated API memory map and/or client merge during init.
      if (filterAutomation !== 'all') {
        const queueBoot = account.automationQueueBooting === true;
        const startingUp =
          account.automation?.starting === true || queueBoot;
        const automationOn =
          account.automation?.enabled === true ||
          startingUp ||
          account.automationRunning === true;
        if (filterAutomation === 'starting') {
          if (!startingUp) return false;
        } else if (filterAutomation === 'start_failed') {
          if (
            !account.automationStartFailed ||
            account.automation?.enabled ||
            startingUp
          ) {
            return false;
          }
        } else if (filterAutomation === 'running_stable') {
          if (!account.automation?.enabled || startingUp) return false;
        } else if (filterAutomation === 'active' && !automationOn) {
          return false;
        } else if (filterAutomation === 'inactive' && automationOn) {
          return false;
        }
      }

      if (filterSandbox !== 'all') {
        if (filterSandbox === 'sandbox' && account.hasGraduatedAccess !== false) {
          return false;
        }
        if (filterSandbox === 'graduated' && account.hasGraduatedAccess !== true) {
          return false;
        }
        if (
          filterSandbox === 'unknown' &&
          account.hasGraduatedAccess !== null &&
          account.hasGraduatedAccess !== undefined
        ) {
          return false;
        }
      }

      if (filterTag !== 'all') {
        const tags = Array.isArray(account.tags) ? account.tags : [];
        if (!tags.map((t) => String(t).toLowerCase()).includes(filterTag)) {
          return false;
        }
      }

      return true;
    });
  }, [
    accounts,
    useServerScopeFilters,
    filterModelId,
    filterStatus,
    filterAutomation,
    searchQuery,
    selectedGroup,
    filterProfile,
    filterSandbox,
    filterTag,
    resolveLeafGroupsForGroupName,
    mainGroupsByName
  ]);

  // OPTIMIZED: Memoize filtered accounts to prevent unnecessary recalculations
  const filteredAccounts = useMemo(() => getFilteredAccounts(), [getFilteredAccounts]);

  /** Same set as filteredAccounts, sorted by createdAt so Age filter updates instantly without refetch (non-paginated). */
  const ageSortedFilteredAccounts = useMemo(() => {
    const list = [...filteredAccounts];
    const getTime = (a) => {
      const t = a.createdAt != null ? new Date(a.createdAt).getTime() : NaN;
      return Number.isFinite(t) ? t : 0;
    };
    list.sort((a, b) => {
      const ta = getTime(a);
      const tb = getTime(b);
      return filterAgeOrder === 'high' ? ta - tb : tb - ta;
    });
    return list;
  }, [filteredAccounts, filterAgeOrder]);
  
  // Helper to get accounts ready to start (respects current filters)
  const hasUsableSession = (account) =>
    account.hasSessionData === true ||
    !!(account.sessionData && typeof account.sessionData === 'object');

  const getStartableAccounts = () => {
    return filteredAccounts.filter(account => {
      // Account must be logged in (status: 'active')
      // Automation must not be enabled
      // Must have a model assigned
      // Must have valid session data (hasSessionData from API, or legacy full session in memory)
      return account.status === 'active' 
        && !account.automation?.enabled 
        && account.modelId 
        && hasUsableSession(account)
        && !account.proxy?.proxyError; // Don't start accounts with proxy errors
    });
  };

  // Helper to get accounts that are running (respects current filters)
  const getStoppableAccounts = () => {
    return filteredAccounts.filter(account => {
      const queueBoot = account.automationQueueBooting === true;
      const startingUp = account.automation?.starting === true || queueBoot;
      const enabledOk =
        account.automation?.enabled === true && !BLOCKING_AUTOMATION_STATUSES.has(account.status);
      return enabledOk || startingUp;
    });
  };

  // Analyze why accounts cannot start
  const analyzeStartability = () => {
    const startable = [];
    const breakdown = {
      alreadyRunning: [],
      notLoggedIn: [],
      noModel: [],
      noSession: [],
      proxyError: [],
      other: []
    };

    filteredAccounts.forEach(account => {
      const queueBoot = account.automationQueueBooting === true;
      const startingUp = account.automation?.starting === true || queueBoot;
      const enabledDb =
        account.automation?.enabled === true && !BLOCKING_AUTOMATION_STATUSES.has(account.status);
      if (startingUp || enabledDb) {
        breakdown.alreadyRunning.push(account);
      } else if (account.status !== 'active') {
        breakdown.notLoggedIn.push(account);
      } else if (!account.modelId) {
        breakdown.noModel.push(account);
      } else if (!hasUsableSession(account)) {
        breakdown.noSession.push(account);
      } else if (account.proxy?.proxyError === true) {
        breakdown.proxyError.push(account);
      } else if (account.status === 'active' && !account.automation?.enabled && account.modelId && hasUsableSession(account) && !account.proxy?.proxyError) {
        startable.push(account);
      } else {
        breakdown.other.push(account);
      }
    });

    return { startable, breakdown };
  };
  
  // Pagination calculations
  // When using server-side pagination, accounts are already paginated
  // When using client-side pagination, paginate filtered accounts
  const displayTotalPages = usePagination ? totalPages : Math.ceil(filteredAccounts.length / itemsPerPage);
  const startIndex = usePagination ? 0 : (currentPage - 1) * itemsPerPage;
  const endIndex = usePagination ? ageSortedFilteredAccounts.length : startIndex + itemsPerPage;
  const paginatedAccounts = usePagination
    ? ageSortedFilteredAccounts
    : ageSortedFilteredAccounts.slice(startIndex, endIndex);
  
  // Reset to page 1 and refetch whenever group/filters change (always server-scoped).
  useEffect(() => {
    if (!filterEffectMountedRef.current) {
      filterEffectMountedRef.current = true;
      return;
    }
    setCurrentPage(1);
    fetchAccountsRef.current?.('soft', 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterModelId, filterStatus, filterAutomation, filterProfile, filterSandbox, filterTag, filterAgeOrder, searchQuery, selectedGroup]);

  // Refetch when page changes in server-side pagination mode (page 1 handled above).
  useEffect(() => {
    if (usePagination && currentPage > 1) {
      fetchAccountsRef.current?.(false, currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, usePagination]);

  // Status filter options: from API for current filter scope when paginated, else from loaded accounts
  const availableStatuses = useMemo(() => {
    // Status display name mapping
    const statusDisplayNames = {
      'active': 'Logged-in',
      'suspended': 'Suspended',
      'banned': 'Banned',
      'proxy_required': 'Proxy Required',
      'proxy_failed': 'Proxy Failed',
      'proxy_error': 'Proxy Error',
      'xchat_key_error': 'Key Error',
      'daily_limit': 'Daily limit',
      'login_failed': 'Login Failed',
      'session_extraction_failed': 'Session Extraction Failed',
      'auth_expired': 'Auth Expired',
      'captcha': 'Captcha',
      'login_required': 'Login required',
      'pending_validation': 'Auth expired'
    };

    const fromApi = accountSummary.distinctStatuses;
    if (Array.isArray(fromApi) && fromApi.length > 0) {
      return [...fromApi]
        .filter((s) => s && s !== 'XCHAT update')
        .sort((a, b) => {
          const nameA = statusDisplayNames[a] || a;
          const nameB = statusDisplayNames[b] || b;
          return nameA.localeCompare(nameB);
        });
    }

    const statusSet = new Set();
    accounts.forEach(account => {
      if (account.status && account.status !== 'XCHAT update') {
        statusSet.add(account.status);
      }
    });
    return Array.from(statusSet).sort((a, b) => {
      const nameA = statusDisplayNames[a] || a;
      const nameB = statusDisplayNames[b] || b;
      return nameA.localeCompare(nameB);
    });
  }, [accounts, accountSummary.distinctStatuses]);

  // Status display name mapping (for use in other parts of the component)
  const statusDisplayNames = {
    'active': 'Logged-in',
    'suspended': 'Suspended',
    'banned': 'Banned',
    'proxy_required': 'Proxy Required',
    'proxy_failed': 'Proxy Failed',
    'proxy_error': 'Proxy Error',
    'xchat_key_error': 'Key Error',
    'daily_limit': 'Daily limit',
    'login_failed': 'Login Failed',
    'session_extraction_failed': 'Session Extraction Failed',
    'auth_expired': 'Auth Expired',
    'captcha': 'Captcha',
    'login_required': 'Login required',
    'pending_validation': 'Auth expired'
  };

  const handleSelectAllAccounts = async () => {
    const filtered = ageSortedFilteredAccounts;
    const filteredIds = filtered.map(account => account._id);
    const scopeTotal = usePagination
      ? (accountSummary.totalAccounts || 0)
      : filteredIds.length;

    // In paginated mode the rendered list only holds the current page (~50 rows).
    // "Select All" must cover every account in the active filter/group scope, so
    // we ask the backend for the full id list rather than only what is on-screen.
    if (usePagination) {
      const selectedSet = new Set(selectedAccounts);
      const allOnPageSelected =
        filteredIds.length > 0 && filteredIds.every((id) => selectedSet.has(id));
      const alreadySelectedAllScope =
        scopeTotal > 0 && selectedAccounts.length >= scopeTotal && allOnPageSelected;

      if (alreadySelectedAllScope) {
        setSelectedAccounts([]);
        lastSelectedIndexRef.current = null;
        return;
      }

      try {
        setBulkOperationLoading(true);
        const p = buildDashboardListFilterParams();
        const { data } = await api.get(`/accounts/ids?${p.toString()}`);
        const ids = Array.isArray(data?.ids) ? data.ids.map(String) : [];
        if (ids.length === 0) {
          toast.error('No accounts match the current filter');
          return;
        }
        setSelectedAccounts(ids);
        lastSelectedIndexRef.current = filtered.length > 0 ? filtered.length - 1 : null;
        if (ids.length > filteredIds.length) {
          toast.success(`Selected ${ids.length} accounts across all pages`);
        }
      } catch (error) {
        console.error('Error selecting all accounts in scope:', error);
        toast.error('Failed to select all accounts in the current filter');
      } finally {
        setBulkOperationLoading(false);
      }
      return;
    }

    const allFilteredSelected = filteredIds.every((id) => selectedAccounts.includes(id));

    if (allFilteredSelected) {
      // Deselect all filtered accounts
      setSelectedAccounts(selectedAccounts.filter((id) => !filteredIds.includes(id)));
      lastSelectedIndexRef.current = null;
    } else {
      // Select all filtered accounts (add to existing selection)
      const newSelection = [...new Set([...selectedAccounts, ...filteredIds])];
      setSelectedAccounts(newSelection);
      if (filtered.length > 0) {
        lastSelectedIndexRef.current = filtered.length - 1;
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Please select accounts to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedAccounts.length} accounts? This action cannot be undone.`)) {
      return;
    }

    setBulkOperationLoading(true);
    try {
      const response = await api.post('/accounts/bulk-delete', {
        accountIds: selectedAccounts
      });
      
      const deleted =
        response.data.deletedCount ??
        response.data.deletedAccounts ??
        selectedAccounts.length;
      toast.success(`Successfully deleted ${deleted} accounts`);
      setSelectedAccounts([]);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete accounts');
      console.error('Error deleting accounts:', error);
    } finally {
      setBulkOperationLoading(false);
    }
  };

  const handleBulkStart = async (opts) => {
    const explicitIds =
      opts && Array.isArray(opts.accountIds) && opts.accountIds.length > 0 ? opts.accountIds : null;
    const idList = (explicitIds || selectedAccounts).map((id) =>
      typeof id === 'string' ? id : String(id)
    );
    if (idList.length === 0) {
      toast.error('Please select accounts to start');
      return;
    }

    let modelId = opts && opts.modelId != null ? opts.modelId : null;
    if (!modelId) {
      const firstAccount = accounts.find((account) => idList.includes(String(account._id)));
      if (!firstAccount || !firstAccount.modelId) {
        toast.error('Selected accounts must have a model assigned');
        return;
      }
      const mid = firstAccount.modelId;
      modelId = typeof mid === 'object' && mid?._id ? mid._id : mid;
    }
    modelId = typeof modelId === 'string' ? modelId : String(modelId);

    setBulkOperationLoading(true);
    // Track these accounts to prevent duplicate toasts. A second group joins the same run.
    const alreadyRunning = bulkStartProgress.status === 'queued' || bulkStartProgress.status === 'processing';
    if (alreadyRunning) {
      for (const id of idList) bulkStartAccountIdsRef.current.add(String(id));
    } else {
      bulkStartAccountIdsRef.current = new Set(idList.map((id) => String(id)));
    }

    const initialProgress = alreadyRunning
      ? {
          ...bulkStartProgress,
          currentAccount: `Adding ${idList.length} account(s) to the running start…`,
          timestamp: Date.now(),
        }
      : { 
          current: 0, 
          total: idList.length, 
          currentAccount: null, 
          successCount: 0, 
          failCount: 0, 
          status: 'queued',
          operationId: null,
          timestamp: Date.now()
        };
    setBulkStartProgress(initialProgress);
    
    // Save initial progress to sessionStorage
    try {
      sessionStorage.setItem('bulkStartProgress', JSON.stringify(initialProgress));
    } catch (error) {
      console.error('Error saving initial bulk start progress to sessionStorage:', error);
    }

    // API responds immediately now (queuing happens in background on backend)
    // Socket events drive all progress updates
    api.post('/accounts/bulk-start', {
      accountIds: idList,
      modelId
    }, {
      timeout: 30000 // 30s is plenty — backend responds immediately before queuing
    })
      .then((response) => {
        if (response.data?.operationId) {
          setBulkStartProgress(prev => ({ ...prev, operationId: response.data.operationId }));
        }
        setSelectedAccounts([]);
      })
      .catch((error) => {
        toast.error(error.response?.data?.error || 'Failed to start automation');
        console.error('Error starting automation:', error);
        setBulkOperationLoading(false);
        try {
          sessionStorage.removeItem('bulkStartProgress');
        } catch (e) {
          // ignore
        }
        setTimeout(() => {
          setBulkStartProgress({ current: 0, total: 0, currentAccount: null, successCount: 0, failCount: 0, status: null, operationId: null });
          bulkStartAccountIdsRef.current = new Set();
        }, 2000);
      });
  };

  const handleBulkStartCancel = async () => {
    if (!bulkStartProgress.operationId && bulkStartProgress.status !== 'processing' && bulkStartProgress.status !== 'queued') {
      toast.error('No active bulk start operation to cancel');
      return;
    }

    try {
      const response = await api.post('/accounts/bulk-start/cancel');
      toast.success(response.data.message || 'Bulk start cancelled');
      setBulkOperationLoading(false);
      bulkStartAccountIdsRef.current = new Set();
      // Progress will be updated via socket event
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to cancel bulk start');
      console.error('Error cancelling bulk start:', error);
    }
  };

  const handleRestartCancel = async () => {
    if (restartProgress.status !== 'processing' && restartProgress.status !== 'queued') {
      toast.error('No active restart operation to cancel');
      return;
    }
    try {
      const response = await api.post('/accounts/restart-cancel');
      toast.success(response.data.message || 'Restart cancelled; automation disabled for pending accounts.');
      setRestartProgress({ current: 0, total: 0, currentAccount: null, successCount: 0, failCount: 0, status: null, operationId: null });
      restartInProgressRef.current = false;
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to cancel restart');
    }
  };

  const handleBulkStop = async (opts) => {
    const explicitIds =
      opts && Array.isArray(opts.accountIds) && opts.accountIds.length > 0 ? opts.accountIds : null;
    const idList = (explicitIds || selectedAccounts).map((id) =>
      typeof id === 'string' ? id : String(id)
    );
    if (idList.length === 0) {
      toast.error('Please select accounts to stop');
      return;
    }

    setBulkOperationLoading(true);
    try {
      const response = await api.post('/accounts/bulk-stop', {
        accountIds: idList
      });
      
      // Update local state immediately to reflect stopped accounts
      const idSet = new Set(idList);
      setAccounts(prev => prev.map(account => 
        idSet.has(String(account._id))
          ? { ...account, automation: { ...account.automation, enabled: false, starting: false } }
          : account
      ));
      
      toast.success(`Stopped automation for ${response.data.successCount} accounts, ${response.data.failCount} failed`);
      setBulkOperationResults(response.data);
      setSelectedAccounts([]);
      refreshBulkScope();
      // REMOVED: fetchAccounts() - socket events will handle updates
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to stop automation');
      console.error('Error stopping automation:', error);
      // Only refresh on error, and preserve state if fetch fails
      try {
        await fetchAccounts();
      } catch (fetchError) {
        console.error('Error refreshing accounts after stop failure:', fetchError);
        // Don't clear accounts on fetch error - preserve existing state
      }
    } finally {
      setBulkOperationLoading(false);
    }
  };

  const handleStartAll = async (override = null) => {
    let scopeData;
    try {
      const p = override
        ? (() => {
            const pp = new URLSearchParams();
            if (override.modelId) pp.set('filterModelId', String(override.modelId));
            if (override.status) pp.set('filterStatus', override.status);
            return pp;
          })()
        : buildDashboardListFilterParams();
      const { data } = await api.get(`/accounts/bulk-scope?${p.toString()}&includeIds=1`);
      scopeData = data;
    } catch (e) {
      toast.error('Could not load startable accounts for this filter scope');
      return;
    }

    const ids = Array.isArray(scopeData.startableAccountIds) ? scopeData.startableAccountIds : [];
    const rows = Array.isArray(scopeData.startableAccounts) ? scopeData.startableAccounts : [];

    if (ids.length === 0) {
      const analysis = analyzeStartability();
      setStartAllBreakdown(analysis.breakdown);
      setShowStartAllBreakdownModal(true);
      return;
    }

    const modelIds = [...new Set(rows.map((r) => r.modelId).filter(Boolean))];
    if (modelIds.length > 1) {
      toast.error(
        'Startable accounts in this scope use multiple models. Filter by one model (or a narrower group), then try again.'
      );
      return;
    }
    const primaryModelId = modelIds[0];
    if (!primaryModelId) {
      toast.error('Accounts must have a model assigned to start automation');
      return;
    }

    const analysis = analyzeStartability();
    const totalCannotStart = Object.values(analysis.breakdown).reduce((sum, arr) => sum + arr.length, 0);
    if (totalCannotStart > 0) {
      setStartAllBreakdown(analysis.breakdown);
      setShowStartAllBreakdownModal(true);
      const proceed = window.confirm(
        `${ids.length} account(s) in this scope can be started.\n\n` +
          `${totalCannotStart} account(s) on the current page cannot start (see breakdown).\n\n` +
          `Proceed with starting ${ids.length} account(s) across the full scope?`
      );
      if (!proceed) {
        setShowStartAllBreakdownModal(false);
        return;
      }
    } else {
      if (!window.confirm(`Start automation for ${ids.length} account(s) in the current filter/group scope?`)) {
        return;
      }
    }

    setSelectedAccounts(ids);
    await handleBulkStart({ accountIds: ids, modelId: primaryModelId });
    setShowStartAllBreakdownModal(false);
  };

  // Model-parent click: start chatbot automation for ALL logged-in (active)
  // accounts of one model, across every group under that model.
  const handleStartModel = (model) => {
    handleStartAll({ modelId: model.modelId, status: 'active' });
  };

  const handleStopAll = async () => {
    let scopeData;
    try {
      const p = buildDashboardListFilterParams();
      const { data } = await api.get(`/accounts/bulk-scope?${p.toString()}&includeIds=1`);
      scopeData = data;
    } catch (e) {
      toast.error('Could not load running accounts for this filter scope');
      return;
    }

    const ids = Array.isArray(scopeData.stoppableAccountIds) ? scopeData.stoppableAccountIds : [];
    if (ids.length === 0) {
      toast.error('No accounts in this scope have automation enabled.');
      return;
    }

    if (!window.confirm(`Stop automation for ${ids.length} account(s) in the current filter/group scope?`)) {
      return;
    }

    setSelectedAccounts(ids);
    await handleBulkStop({ accountIds: ids });
  };

  const handleBulkLoginTest = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Please select accounts to test');
      return;
    }

    setBulkOperationLoading(true);
    const initialProgress = { current: 0, total: selectedAccounts.length, currentAccount: null, status: 'testing', successCount: 0, failCount: 0 };
    setBulkLoginTestProgress(initialProgress);
    
    // Save initial progress to sessionStorage
    try {
      sessionStorage.setItem('bulkLoginTestProgress', JSON.stringify(initialProgress));
    } catch (error) {
      console.error('Error saving initial bulk login test progress to sessionStorage:', error);
    }
    
    // Fire the API call but don't wait for it - rely on socket events for progress updates
    // The API call may timeout, but socket events will continue to arrive
    api.post('/accounts/bulk-login-test', {
      accountIds: selectedAccounts
    }, {
      timeout: 300000 // 5 minutes timeout for bulk operations
    })
      .then((response) => {
        // API call succeeded - update results if available
        if (response.data) {
          setBulkOperationResults(response.data);
          // Don't show toast here - wait for socket event with completion status
        }
        setSelectedAccounts([]);
        // REMOVED: fetchAccounts() - socket events will handle updates
      })
      .catch((error) => {
        // Only show error if it's not a timeout (timeout is expected for long operations)
        // Socket events will continue to provide progress updates
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          // HTTP timeout - but socket events should still arrive, so don't show error
        } else {
          // Immediate error (network, 500, etc.) - show error
          toast.error(error.response?.data?.error || 'Failed to run login tests');
          console.error('Error running login tests:', error);
          setBulkOperationLoading(false);
          // Clear from sessionStorage on error
          try {
            sessionStorage.removeItem('bulkLoginTestProgress');
          } catch (e) {
            console.error('Error clearing bulk login test progress from sessionStorage:', e);
          }
          // Reset progress on error
          setTimeout(() => {
            setBulkLoginTestProgress({ current: 0, total: 0, currentAccount: null, status: null, successCount: 0, failCount: 0 });
          }, 2000);
        }
      });
    
    // Listen for completion via socket event
    // The socket event handler will update bulkLoginTestProgress.status to 'completed'
    // and we'll reset the loading state when status becomes 'completed'
  };

  const handleBulkLoginTestCancel = async () => {
    if (bulkLoginTestProgress.status !== 'testing' && bulkLoginTestProgress.status !== 'starting') {
      toast.error('No active login test operation to cancel');
      return;
    }

    try {
      const response = await api.post('/accounts/bulk-login-test/cancel');
      toast.success(response.data.message || 'Login test cancelled');
      setBulkOperationLoading(false);
      setBulkLoginTestProgress(prev => ({ ...prev, status: 'cancelled' }));
      // Clear from sessionStorage
      try {
        sessionStorage.removeItem('bulkLoginTestProgress');
      } catch (e) {
        console.error('Error clearing bulk login test progress from sessionStorage:', e);
      }
    } catch (error) {
      // If backend says no active operation, it means the operation was already completed or doesn't exist
      // Clear the stale state gracefully
      if (error.response?.status === 404 || error.response?.data?.error?.includes('No active')) {
        toast.success('Operation already completed or not found. Clearing stale progress.');
        setBulkOperationLoading(false);
        setBulkLoginTestProgress({ current: 0, total: 0, currentAccount: null, status: null, successCount: 0, failCount: 0 });
        try {
          sessionStorage.removeItem('bulkLoginTestProgress');
        } catch (e) {
          console.error('Error clearing bulk login test progress from sessionStorage:', e);
        }
      } else {
        toast.error(error.response?.data?.error || 'Failed to cancel login test');
        console.error('Error cancelling login test:', error);
      }
    }
  };

  const handleBulkCaptchaSolve = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Please select accounts to solve CAPTCHAs');
      return;
    }

    setBulkOperationLoading(true);
    setBulkCaptchaSolveProgress({ current: 0, total: selectedAccounts.length, currentAccount: null, status: 'starting', successCount: 0, failCount: 0, message: '' });

    api.post('/accounts/bulk-solve-captcha', {
      accountIds: selectedAccounts
    }, {
      timeout: 600000
    })
      .then((response) => {
        if (response.data) {
          setBulkOperationResults(response.data);
        }
        setSelectedAccounts([]);
      })
      .catch((error) => {
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          // timeout expected for long operations - socket events handle progress
        } else {
          toast.error(error.response?.data?.error || 'Failed to solve CAPTCHAs');
          console.error('Error solving CAPTCHAs:', error);
          setBulkOperationLoading(false);
          setTimeout(() => {
            setBulkCaptchaSolveProgress({ current: 0, total: 0, currentAccount: null, status: null, successCount: 0, failCount: 0, message: '' });
          }, 2000);
        }
      });
  };

  /** Clear false-positive captcha / auth_expired / etc. → active (logged in). */
  const handleBulkClearStatus = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Please select accounts to clear status');
      return;
    }
    if (!window.confirm(
      `Clear status on ${selectedAccounts.length} selected account(s) and mark them active (logged in)?\n\n`
      + 'Use this for false-positive captcha / auth flags. Does not solve a real captcha or renew tokens.'
    )) return;

    setBulkOperationLoading(true);
    try {
      const response = await api.post('/accounts/bulk-clear-status', {
        accountIds: selectedAccounts
      });
      const modified = response.data?.modified ?? 0;
      const vaCleared = response.data?.vaProfilesCleared ?? 0;
      toast.success(
        `Cleared ${modified} account(s) to active`
        + (vaCleared ? ` · ${vaCleared} linked VA profile(s)` : '')
      );
      const selectedSet = new Set(selectedAccounts.map(String));
      setAccounts((prev) => prev.map((acc) => (
        selectedSet.has(String(acc._id))
          ? { ...acc, status: 'active', loginTestFailed: false }
          : acc
      )));
      setSelectedAccounts([]);
      setClearStatusesKey((k) => k + 1);
      refreshAccountSummary();
      fetchAccounts(false).catch(() => {});
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to clear status');
      console.error('Error clearing account status:', error);
    } finally {
      setBulkOperationLoading(false);
    }
  };

  const handleBulkCaptchaSolveCancel = async () => {
    if (bulkCaptchaSolveProgress.status !== 'solving' && bulkCaptchaSolveProgress.status !== 'starting') {
      toast.error('No active CAPTCHA solve operation to cancel');
      return;
    }

    try {
      const response = await api.post('/accounts/bulk-solve-captcha/cancel');
      toast.success(response.data.message || 'CAPTCHA solve cancelled');
      setBulkOperationLoading(false);
      setBulkCaptchaSolveProgress(prev => ({ ...prev, status: 'cancelled' }));
    } catch (error) {
      if (error.response?.status === 404) {
        toast.success('Operation already completed.');
        setBulkOperationLoading(false);
        setBulkCaptchaSolveProgress({ current: 0, total: 0, currentAccount: null, status: null, successCount: 0, failCount: 0, message: '' });
      } else {
        toast.error(error.response?.data?.error || 'Failed to cancel');
        console.error('Error cancelling captcha solve:', error);
      }
    }
  };

  const handleBulkUpdateAuth = async () => {
    if (!updateAuthText.trim()) {
      toast.error('Please enter username:auth_token pairs');
      return;
    }

    // Parse input: username:auth_token (one per line)
    const lines = updateAuthText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    if (lines.length === 0) {
      toast.error('Please enter at least one username:auth_token pair');
      return;
    }

    // Parse each line
    const updates = [];
    for (const line of lines) {
      const parts = line.split(':');
      if (parts.length < 2) {
        toast.error(`Invalid format: ${line}. Expected username:auth_token`);
        return;
      }
      const username = parts[0].trim();
      const authToken = parts.slice(1).join(':'); // In case auth_token contains colons
      if (!username || !authToken) {
        toast.error(`Invalid format: ${line}. Both username and auth_token are required`);
        return;
      }
      updates.push({ username, authToken });
    }

    setBulkOperationLoading(true);
    const initialProgress = { 
      current: 0, 
      total: updates.length, 
      currentAccount: null, 
      status: 'starting',
      successCount: 0, 
      failCount: 0,
      notFoundCount: 0
    };
    setBulkUpdateAuthProgress(initialProgress);

    try {
      await api.post('/accounts/bulk-update-auth', { updates });
      // Progress will be handled via socket events
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update auth tokens');
      console.error('Error updating auth:', error);
      setBulkOperationLoading(false);
      setBulkUpdateAuthProgress({ current: 0, total: 0, currentAccount: null, status: null, successCount: 0, failCount: 0, notFoundCount: 0 });
    }
  };

  const handleBulkRenewAuth = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Please select accounts to renew auth');
      return;
    }

    if (!window.confirm(`Renew auth tokens for ${selectedAccounts.length} account(s)? This will log into X.com for each account using its stored password and proxy. Each account takes ~60-90 seconds.`)) {
      return;
    }

    setBulkOperationLoading(true);
    setBulkRenewAuthProgress({
      current: 0,
      total: selectedAccounts.length,
      currentAccount: null,
      status: 'starting',
      successCount: 0,
      failCount: 0,
      skippedCount: 0,
    });

    try {
      await api.post('/accounts/bulk-renew-auth', { accountIds: selectedAccounts }, { timeout: 0 });
    } catch (error) {
      if (error.code !== 'ECONNABORTED' && !error.message?.includes('timeout')) {
        toast.error(error.response?.data?.error || 'Failed to start auth renewal');
        setBulkOperationLoading(false);
        setBulkRenewAuthProgress({ current: 0, total: 0, currentAccount: null, status: null, successCount: 0, failCount: 0, skippedCount: 0 });
      }
    }
  };

  const handleBulkRenewAuthCancel = async () => {
    try {
      const response = await api.post('/accounts/bulk-renew-auth/cancel');
      toast.success(response.data.message || 'Auth renewal cancelled');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.success('Operation already completed.');
        setBulkOperationLoading(false);
        setBulkRenewAuthProgress({ current: 0, total: 0, currentAccount: null, status: null, successCount: 0, failCount: 0, skippedCount: 0 });
      } else {
        toast.error('Failed to cancel auth renewal');
      }
    }
  };

  const handleBulkUpdateProxy = async () => {
    if (!updateProxyText.trim()) {
      toast.error('Please enter username:proxy pairs (one per line)');
      return;
    }
    const lines = updateProxyText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
    if (lines.length === 0) {
      toast.error('Please enter at least one username:proxy pair');
      return;
    }
    const updates = [];
    for (const line of lines) {
      const colonIndex = line.indexOf(':');
      if (colonIndex <= 0 || colonIndex === line.length - 1) {
        toast.error(`Invalid format: "${line}". Expected username:proxy (proxy = host:port:user:pass)`);
        return;
      }
      const username = line.slice(0, colonIndex).trim();
      const proxyString = line.slice(colonIndex + 1).trim();
      if (!username || !proxyString) {
        toast.error(`Invalid format: "${line}". Both username and proxy are required`);
        return;
      }
      updates.push({ username, proxyString });
    }
    setBulkOperationLoading(true);
    setBulkUpdateProxyProgress({
      current: 0,
      total: updates.length,
      currentAccount: null,
      status: 'starting',
      successCount: 0,
      failCount: 0,
      notFoundCount: 0
    });
    try {
      await api.post('/accounts/bulk-update-proxies-by-username', { updates });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update proxies');
      console.error('Error updating proxies:', error);
      setBulkOperationLoading(false);
      setBulkUpdateProxyProgress({ current: 0, total: 0, currentAccount: null, status: null, successCount: 0, failCount: 0, notFoundCount: 0 });
    }
  };

  const handleBulkRemoveProxy = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Select at least one account');
      return;
    }
    if (!window.confirm(`Remove proxy from ${selectedAccounts.length} selected account(s)?`)) return;
    setBulkOperationLoading(true);
    try {
      const response = await api.post('/accounts/bulk-remove-proxy', { accountIds: selectedAccounts });
      const modifiedCount = response.data?.modifiedCount ?? 0;
      toast.success(`Removed proxy from ${modifiedCount} account(s)`);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to remove proxy from accounts');
      console.error('Error bulk removing proxy:', error);
    } finally {
      setBulkOperationLoading(false);
    }
  };

  const openAddToVaModal = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Select at least one account');
      return;
    }
    setShowAddToVaModal(true);
    setAddToVaOwnerKey('');
    const tasks = [];
    if (vaUsers.length === 0) {
      tasks.push(
        api.get('/admin/va/users')
          .then((res) => setVaUsers(res.data?.users || []))
          .catch((error) => toast.error(error.response?.data?.error || 'Failed to load VAs'))
      );
    }
    // AI VA Agents — always refresh on open so newly-created agents show up.
    tasks.push(
      api.get('/admin/va/ai-agents')
        .then((res) => setAiAgents(res.data?.agents || []))
        .catch(() => { /* AI agents are optional; silently skip if endpoint unavailable */ })
    );
    if (tasks.length) await Promise.all(tasks);
  };

  const openAgentAssignModal = async (groupName) => {
    const current = groupMetaByName[groupName] || {};
    setAgentAssignModalGroup({
      name: groupName,
      attachedAgent: current.attachedAgent || null
    });
    setAgentAssignSelectedId(
      current.attachedAgent?.id ? String(current.attachedAgent.id) : ''
    );
    setAgentAssignModalOpen(true);
    setAgentAssignLoadingAgents(true);
    try {
      const res = await api.get('/admin/va/ai-agents');
      setAiAgents(res.data?.agents || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load AI VA Agents');
    } finally {
      setAgentAssignLoadingAgents(false);
    }
  };

  const handleAttachGroupToAgent = async () => {
    if (!agentAssignModalGroup?.name) return;
    if (!agentAssignSelectedId) {
      toast.error('Select an AI VA Agent');
      return;
    }
    setAgentAssignSubmitting(true);
    try {
      const res = await api.post('/admin/va/profile-accounts/from-account-group', {
        groupName: agentAssignModalGroup.name,
        assignedAgentId: agentAssignSelectedId
      });
      const data = res.data || {};
      const matched = data.matchedAccounts ?? 0;
      const promoted = (data.inserted || 0) + (data.updated || 0) + (data.moved || 0);
      if (matched === 0) {
        toast.success(
          `Auto-assign enabled on "${agentAssignModalGroup.name}" — future uploads will attach automatically`
        );
      } else {
        toast.success(
          `Attached ${promoted} account(s) in "${agentAssignModalGroup.name}" and enabled auto-assign`
        );
      }
      (data.warnings || []).forEach((w) => {
        if (w?.message) toast(w.message, { icon: '⚠️' });
      });
      setAgentAssignModalOpen(false);
      setAgentAssignModalGroup(null);
      await fetchGroups();
    } catch (error) {
      const data = error.response?.data;
      if (error.response?.status === 409 && Array.isArray(data?.conflicts)) {
        const detail = data.conflicts
          .map((c) => `${c.name} (${(c.collidingGroups || []).join(', ')})`)
          .join('; ');
        toast.error(`Group already owned by another agent: ${detail}`);
      } else {
        toast.error(data?.error || 'Failed to attach group to agent');
      }
    } finally {
      setAgentAssignSubmitting(false);
    }
  };

  const handleDetachGroupFromAgent = async () => {
    const groupName = agentAssignModalGroup?.name;
    const agentId = agentAssignModalGroup?.attachedAgent?.id;
    const agentLabel = agentAssignModalGroup?.attachedAgent?.name || 'agent';
    if (!groupName || !agentId) return;
    if (!window.confirm(
      `Stop auto-assigning new accounts in "${groupName}" to ${agentLabel}?\n\nAccounts already on the agent stay attached — only future uploads are affected.`
    )) return;
    setAgentAssignSubmitting(true);
    try {
      await api.post(`/admin/va/ai-agents/${agentId}/detach-group`, { groupName });
      toast.success(`Auto-assign disabled for "${groupName}"`);
      setAgentAssignModalOpen(false);
      setAgentAssignModalGroup(null);
      await fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to stop auto-assign');
    } finally {
      setAgentAssignSubmitting(false);
    }
  };

  const handleAddToVa = async () => {
    if (!addToVaOwnerKey) {
      toast.error('Select a VA or AI VA Agent');
      return;
    }
    // The unified picker encodes choice as `va:<id>` or `agent:<id>`. The
    // backend takes one of `assignedVaId` / `assignedAgentId` and rejects
    // both being present — matching the "single owner per account" rule.
    const [ownerKind, ownerId] = addToVaOwnerKey.split(':');
    if ((ownerKind !== 'va' && ownerKind !== 'agent') || !ownerId) {
      toast.error('Invalid selection');
      return;
    }
    setAddToVaLoading(true);
    setAddToVaJobProgress(null);
    setAddToVaJobId(null);
    setAddToVaCancelling(false);
    try {
      const payload = {
        accountIds: selectedAccounts,
        ...(ownerKind === 'va' ? { assignedVaId: ownerId } : { assignedAgentId: ownerId })
      };
      const res = await api.post('/admin/va/profile-accounts/from-automation-accounts', payload);
      if (res.data?.jobId) {
        setAddToVaJobId(res.data.jobId);
        toast.success(`Queued ${res.data.total || selectedAccounts.length} account(s) — progress below`);
        return;
      }
      // Legacy sync response fallback
      const inserted = res.data?.inserted ?? 0;
      const updated = res.data?.updated ?? 0;
      const moved = res.data?.moved ?? 0;
      const skipped = res.data?.skipped ?? 0;
      const ownerName = res.data?.owner?.name
        || (ownerKind === 'va'
          ? (vaUsers.find((u) => String(u._id) === ownerId)?.username || 'VA')
          : (aiAgents.find((a) => String(a._id) === ownerId)?.name || 'AI Agent'));
      const ownerLabel = ownerKind === 'agent' ? `AI Agent "${ownerName}"` : `VA "${ownerName}"`;
      const parts = [];
      if (inserted > 0) parts.push(`${inserted} added`);
      if (moved > 0) parts.push(`${moved} moved from another owner`);
      if (updated > 0) parts.push(`${updated} updated`);
      if (skipped > 0) parts.push(`${skipped} skipped`);
      toast.success(parts.length
        ? `${ownerLabel}: ${parts.join(', ')}`
        : `No accounts were added to ${ownerLabel}`);
      setShowAddToVaModal(false);
      setAddToVaOwnerKey('');
      setAddToVaLoading(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add accounts');
      setAddToVaLoading(false);
    }
  };

  const handleCancelAddToVa = async () => {
    if (!addToVaJobId || addToVaCancelling) return;
    setAddToVaCancelling(true);
    try {
      await api.post(`/admin/va/gologin/jobs/${addToVaJobId}/cancel`);
      toast('Stopping… finishes the current GoLogin profile, then halts', { icon: '⏹' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to stop job');
      setAddToVaCancelling(false);
    }
  };

  useEffect(() => {
    if (!addToVaJobId) return undefined;

    let cancelled = false;
    const poll = async () => {
      while (!cancelled) {
        try {
          const res = await api.get(`/admin/va/gologin/jobs/${addToVaJobId}`);
          if (cancelled) break;
          const job = res.data;
          setAddToVaJobProgress(job);
          if (job.status === 'cancelling') {
            setAddToVaCancelling(true);
          }

          if (job.status === 'completed' || job.status === 'cancelled') {
            const result = job.result || {};
            const ownerName = result.owner?.name || 'VA';
            const ownerLabel = result.owner?.kind === 'agent'
              ? `AI Agent "${ownerName}"`
              : `VA "${ownerName}"`;
            const parts = [];
            if (result.inserted > 0) parts.push(`${result.inserted} added`);
            if (result.moved > 0) parts.push(`${result.moved} moved`);
            if (result.updated > 0) parts.push(`${result.updated} updated`);
            if (result.skipped > 0) parts.push(`${result.skipped} skipped`);

            if (job.status === 'cancelled') {
              const gl = result.goLoginImport;
              const glNote = gl?.imported
                ? ` · GoLogin stopped after ${gl.imported} imported`
                : (gl?.remaining ? ` · ${gl.remaining} GoLogin left undone` : '');
              toast(
                `${ownerLabel}: stopped${parts.length ? ` (${parts.join(', ')})` : ''}${glNote}`,
                { icon: '⏹' }
              );
            } else {
              toast.success(parts.length
                ? `${ownerLabel}: ${parts.join(', ')}`
                : `${ownerLabel}: done`);

              const gl = result.goLoginImport;
              if (gl?.skipped && gl.reason === 'no_folder') {
                toast(
                  'GoLogin auto-import skipped — set a folder in VPS Configure / VA Setup',
                  { icon: 'ℹ️' }
                );
              } else if (gl?.error) {
                toast.error(`GoLogin: ${gl.error}`);
              } else if (gl && (gl.imported > 0 || gl.failed > 0 || gl.skipped > 0)) {
                toast.success(
                  `GoLogin: ${gl.imported || 0} imported`
                  + (gl.skipped ? `, ${gl.skipped} skipped` : '')
                  + (gl.failed ? `, ${gl.failed} failed` : '')
                );
              }

              if (result.groupAutoAssign?.groupName && !result.groupAutoAssign.error) {
                toast.success(
                  `Group "${result.groupAutoAssign.groupName}" is now auto-assigned to this agent`
                );
              }
              if (result.errorCount > 0) {
                const sample = (result.errorSamples || [])
                  .slice(0, 3)
                  .map((e) => `@${e.username}: ${e.error}`)
                  .join(' · ');
                toast.error(`${result.errorCount} row(s) had issues${sample ? `: ${sample}` : ''}`);
              }
            }

            setAddToVaLoading(false);
            setAddToVaCancelling(false);
            setAddToVaJobId(null);
            setShowAddToVaModal(false);
            setAddToVaOwnerKey('');
            break;
          }

          if (job.status === 'failed') {
            toast.error(job.error || 'Failed to add accounts');
            setAddToVaLoading(false);
            setAddToVaCancelling(false);
            setAddToVaJobId(null);
            break;
          }
        } catch (err) {
          if (!cancelled) {
            toast.error(err.response?.data?.error || 'Failed to poll attach progress');
            setAddToVaLoading(false);
            setAddToVaCancelling(false);
            setAddToVaJobId(null);
          }
          break;
        }
        await new Promise((r) => setTimeout(r, 1000));
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [addToVaJobId]);

  const handleBulkAddSigningKey = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Select at least one account');
      return;
    }
    if (
      !window.confirm(
        `Recover the Chat signing key for ${selectedAccounts.length} selected account(s)?\n\n` +
          `This uses PIN 1111 against Juicebox and stores the official ECDSA signer. It does not register a new key.\n` +
          `Accounts that already have a signing key are skipped. Accounts enrolled with ECDH-only will fail.`
      )
    ) {
      return;
    }
    setBulkOperationLoading(true);
    try {
      const response = await api.post('/accounts/bulk-add-signing-key', { accountIds: selectedAccounts });
      const total = response.data?.total ?? selectedAccounts.length;
      applyAddSigningKeyProgress({
        current: 0,
        total,
        currentAccount: null,
        successCount: 0,
        failCount: 0,
        skippedCount: 0,
        status: 'queued',
        operationId: response.data?.operationId || null,
        kind: 'recover'
      });
      toast.success(`Recovering signing keys for ${total} account(s)`);
    } catch (error) {
      setBulkOperationLoading(false);
      toast.error(error.response?.data?.error || 'Failed to start signing-key job');
      console.error('Error bulk adding signing key:', error);
    }
  };

  const handleBulkResetXchatPin = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Select at least one account');
      return;
    }
    if (
      !window.confirm(
        `Reset Chat PIN to 1111 and register new keys for ${selectedAccounts.length} selected account(s)?\n\n` +
          `This publishes a new Chat identity, stores the ECDH key and ECDSA signer, and wraps both in Juicebox with PIN 1111.\n` +
          `Cached conversation keys for those accounts are cleared. Accounts that already have a signing key are skipped.`
      )
    ) {
      return;
    }
    setBulkOperationLoading(true);
    try {
      const response = await api.post('/accounts/bulk-reset-xchat-pin', { accountIds: selectedAccounts });
      const total = response.data?.total ?? selectedAccounts.length;
      applyAddSigningKeyProgress({
        current: 0,
        total,
        currentAccount: null,
        successCount: 0,
        failCount: 0,
        skippedCount: 0,
        status: 'queued',
        operationId: response.data?.operationId || null,
        kind: 'reset-pin'
      });
      toast.success(`Resetting PIN and registering keys for ${total} account(s)`);
    } catch (error) {
      setBulkOperationLoading(false);
      toast.error(error.response?.data?.error || 'Failed to start PIN reset job');
      console.error('Error bulk resetting XChat PIN:', error);
    }
  };

  const handleBulkClearXchatKey = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Select at least one account');
      return;
    }
    if (
      !window.confirm(
        `Clear the stored XChat E2EE private key, per-account PIN, and cached conversation decrypt data for ${selectedAccounts.length} selected account(s)?\n\n` +
          `On the next XChat automation start, the PIN from the account’s model will be used (when no per-account PIN is set). Encrypted DMs cannot be decrypted until the key is recovered.`
      )
    ) {
      return;
    }
    setBulkOperationLoading(true);
    try {
      const response = await api.post('/accounts/bulk-clear-xchat-key', { accountIds: selectedAccounts });
      const modifiedCount = response.data?.modifiedCount ?? 0;
      const statusReset = response.data?.statusResetCount ?? 0;
      let msg = `Cleared XChat E2EE key data for ${modifiedCount} account(s)`;
      if (statusReset > 0) {
        msg += ` (${statusReset} account(s) moved out of xchat_key_error)`;
      }
      toast.success(msg);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to clear XChat keys');
      console.error('Error bulk clearing XChat key:', error);
    } finally {
      setBulkOperationLoading(false);
    }
  };

  const handleBulkResetTlsFingerprint = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Select at least one account');
      return;
    }
    setBulkOperationLoading(true);
    try {
      const body = { accountIds: selectedAccounts };
      const ua = bulkResetTlsUserAgent.trim();
      if (ua) {
        body.userAgent = ua;
      }
      const response = await api.post('/accounts/bulk-reset-tls-fingerprint', body);
      const modifiedCount = response.data?.modifiedCount ?? 0;
      const applied = response.data?.userAgentApplied;
      let msg = `Reset TLS (JA3/JA4/JA4R) and updated User-Agent for ${modifiedCount} account(s)`;
      if (applied && typeof applied === 'string') {
        msg += `. UA: ${applied.slice(0, 72)}${applied.length > 72 ? '…' : ''}`;
      }
      toast.success(msg);
      setShowBulkResetTlsModal(false);
      setBulkResetTlsUserAgent('');
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reset TLS fingerprint');
      console.error('Error bulk resetting TLS fingerprint:', error);
    } finally {
      setBulkOperationLoading(false);
    }
  };

  const handleBulkUpdateProxies = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Select at least one account');
      return;
    }
    const lines = bulkUpdateProxiesText.split(/\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length < selectedAccounts.length) {
      toast.error(`Provide at least ${selectedAccounts.length} proxy line(s) (one per selected account). You have ${lines.length}.`);
      return;
    }
    setBulkUpdateProxiesLoading(true);
    try {
      const response = await api.post('/accounts/bulk-update-proxies', {
        accountIds: selectedAccounts,
        proxies: lines.slice(0, selectedAccounts.length)
      });
      const { updatedCount, errors } = response.data || {};
      if (updatedCount > 0) {
        toast.success(`Updated proxy for ${updatedCount} account(s)`);
        setShowBulkUpdateProxiesModal(false);
        setBulkUpdateProxiesText('');
        fetchAccounts();
      }
      if (errors && errors.length > 0) {
        toast.error(`${errors.length} account(s) failed: ${errors.map(e => e.message).join('; ')}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to bulk update proxies');
    } finally {
      setBulkUpdateProxiesLoading(false);
    }
  };

  const handleExportAccounts = async () => {
    try {
      let accountIds = [];

      if (selectedAccounts.length > 0) {
        // selectedAccounts already holds all IDs across all pages (populated by Select All or manual picks)
        accountIds = selectedAccounts;
      } else {
        // Fetch every ID matching the active filters from the backend (not limited to the current page)
        const p = buildDashboardListFilterParams();
        const { data } = await api.get(`/accounts/ids?${p.toString()}`);
        accountIds = Array.isArray(data?.ids) ? data.ids : [];
      }

      if (accountIds.length === 0) {
        toast.error('No accounts to export');
        return;
      }

      const response = await api.post('/accounts/export', { accountIds });

      if (!response.data || !response.data.accounts) {
        toast.error('Failed to export accounts: Invalid response');
        return;
      }

      const exportLines = response.data.accounts.filter(line => line && line.trim() !== '');
      const accountText = exportLines.join('\n');

      const blob = new Blob([accountText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      let filename = `accounts_${timestamp}.txt`;

      if (selectedAccounts.length > 0) {
        filename = `accounts_selected_${selectedAccounts.length}_${timestamp}.txt`;
      } else {
        const filterParts = [];
        if (filterStatus !== 'all') {
          filterParts.push(filterStatus.replace(/\s+/g, '_'));
        }
        if (filterModelId !== 'all') {
          const model = models.find(m => m._id === filterModelId);
          if (model) {
            filterParts.push(model.name.replace(/\s+/g, '_'));
          }
        }
        if (filterParts.length > 0) {
          filename = `accounts_${filterParts.join('_')}_${timestamp}.txt`;
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${exportLines.length} account(s) to ${filename}`);
    } catch (error) {
      console.error('Error exporting accounts:', error);
      toast.error(error.response?.data?.error || 'Failed to export accounts');
    }
  };

  const clearBulkResults = () => {
    setBulkOperationResults(null);
  };

  // Latest handlers for the module-level account cards (see accountsPageRef).
  accountsPageRef.current = {
    fetchAccounts,
    fetchAccountsTimeoutRef,
    isFetchingAccountsRef,
    handleDeleteAccount,
    handleSelectAccount,
    handleStartAutomation,
    handleStopAutomation,
    navigate,
    openEditModal,
    setAccounts,
    shiftKeyPressedRef,
  };


  // Start All Breakdown Modal
  const StartAllBreakdownModal = () => {
    if (!showStartAllBreakdownModal || !startAllBreakdown) return null;

    const startableCount = bulkScope.startable;
    const scopeTotal = accountSummary.totalAccounts || 0;
    const cannotInScope = Math.max(0, scopeTotal - startableCount);

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
            setShowStartAllBreakdownModal(false);
          }}></div>
          
          <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
            <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Start All - Account Breakdown</h3>
                <button
                  onClick={() => {
                    setShowStartAllBreakdownModal(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mb-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-4 mb-4">
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">{startableCount}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Can Start</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-red-600 dark:text-red-400">{cannotInScope}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Cannot start (in scope)</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">{scopeTotal}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Accounts in scope</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {startAllBreakdown.alreadyRunning.length > 0 && (
                    <div className="border border-yellow-200 dark:border-yellow-800 rounded-2xl p-3 bg-yellow-50 dark:bg-yellow-900/20">
                      <div className="flex items-center gap-2 mb-2">
                        <XCircleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                        <h4 className="font-medium text-yellow-900 dark:text-yellow-100">
                          Already Running ({startAllBreakdown.alreadyRunning.length})
                        </h4>
                      </div>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-2">
                        These accounts already have automation enabled.
                      </p>
                      <div className="text-xs text-yellow-600 dark:text-yellow-400 space-y-1">
                        {startAllBreakdown.alreadyRunning.slice(0, 10).map(acc => (
                          <div key={acc._id}>• {acc.username || acc.displayName}</div>
                        ))}
                        {startAllBreakdown.alreadyRunning.length > 10 && (
                          <div>... and {startAllBreakdown.alreadyRunning.length - 10} more</div>
                        )}
                      </div>
                    </div>
                  )}

                  {startAllBreakdown.notLoggedIn.length > 0 && (
                    <div className="border border-red-200 dark:border-red-800 rounded-2xl p-3 bg-red-50 dark:bg-red-900/20">
                      <div className="flex items-center gap-2 mb-2">
                        <XCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                        <h4 className="font-medium text-red-900 dark:text-red-100">
                          Not Logged In ({startAllBreakdown.notLoggedIn.length})
                        </h4>
                      </div>
                      <p className="text-sm text-red-700 dark:text-red-300 mb-2">
                        These accounts are not in 'active' (logged-in) status. Status: {startAllBreakdown.notLoggedIn[0]?.status || 'unknown'}
                      </p>
                      <div className="text-xs text-red-600 dark:text-red-400 space-y-1">
                        {startAllBreakdown.notLoggedIn.slice(0, 10).map(acc => (
                          <div key={acc._id}>• {acc.username || acc.displayName} ({statusDisplayNames[acc.status] || acc.status})</div>
                        ))}
                        {startAllBreakdown.notLoggedIn.length > 10 && (
                          <div>... and {startAllBreakdown.notLoggedIn.length - 10} more</div>
                        )}
                      </div>
                    </div>
                  )}

                  {startAllBreakdown.noModel.length > 0 && (
                    <div className="border border-orange-200 dark:border-orange-800 rounded-2xl p-3 bg-orange-50 dark:bg-orange-900/20">
                      <div className="flex items-center gap-2 mb-2">
                        <XCircleIcon className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                        <h4 className="font-medium text-orange-900 dark:text-orange-100">
                          No Model Assigned ({startAllBreakdown.noModel.length})
                        </h4>
                      </div>
                      <p className="text-sm text-orange-700 dark:text-orange-300 mb-2">
                        These accounts don't have a model preset assigned.
                      </p>
                      <div className="text-xs text-orange-600 dark:text-orange-400 space-y-1">
                        {startAllBreakdown.noModel.slice(0, 10).map(acc => (
                          <div key={acc._id}>• {acc.username || acc.displayName}</div>
                        ))}
                        {startAllBreakdown.noModel.length > 10 && (
                          <div>... and {startAllBreakdown.noModel.length - 10} more</div>
                        )}
                      </div>
                    </div>
                  )}

                  {startAllBreakdown.noSession.length > 0 && (
                    <div className="border border-purple-200 dark:border-purple-800 rounded-2xl p-3 bg-purple-50 dark:bg-purple-900/20">
                      <div className="flex items-center gap-2 mb-2">
                        <XCircleIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        <h4 className="font-medium text-purple-900 dark:text-purple-100">
                          No Session Data ({startAllBreakdown.noSession.length})
                        </h4>
                      </div>
                      <p className="text-sm text-purple-700 dark:text-purple-300 mb-2">
                        These accounts don't have valid session data. Run a login test first.
                      </p>
                      <div className="text-xs text-purple-600 dark:text-purple-400 space-y-1">
                        {startAllBreakdown.noSession.slice(0, 10).map(acc => (
                          <div key={acc._id}>• {acc.username || acc.displayName}</div>
                        ))}
                        {startAllBreakdown.noSession.length > 10 && (
                          <div>... and {startAllBreakdown.noSession.length - 10} more</div>
                        )}
                      </div>
                    </div>
                  )}

                  {startAllBreakdown.proxyError.length > 0 && (
                    <div className="border border-red-200 dark:border-red-800 rounded-2xl p-3 bg-red-50 dark:bg-red-900/20">
                      <div className="flex items-center gap-2 mb-2">
                        <XCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                        <h4 className="font-medium text-red-900 dark:text-red-100">
                          Proxy Error ({startAllBreakdown.proxyError.length})
                        </h4>
                      </div>
                      <p className="text-sm text-red-700 dark:text-red-300 mb-2">
                        These accounts have proxy authentication errors. Fix the proxy configuration first.
                      </p>
                      <div className="text-xs text-red-600 dark:text-red-400 space-y-1">
                        {startAllBreakdown.proxyError.slice(0, 10).map(acc => (
                          <div key={acc._id}>• {acc.username || acc.displayName}</div>
                        ))}
                        {startAllBreakdown.proxyError.length > 10 && (
                          <div>... and {startAllBreakdown.proxyError.length - 10} more</div>
                        )}
                      </div>
                    </div>
                  )}

                  {startAllBreakdown.other.length > 0 && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-2xl p-3 bg-gray-50 dark:bg-gray-700/50">
                      <div className="flex items-center gap-2 mb-2">
                        <XCircleIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">
                          Other Issues ({startAllBreakdown.other.length})
                        </h4>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                        These accounts have other issues preventing automation start.
                      </p>
                      <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        {startAllBreakdown.other.slice(0, 10).map(acc => (
                          <div key={acc._id}>• {acc.username || acc.displayName}</div>
                        ))}
                        {startAllBreakdown.other.length > 10 && (
                          <div>... and {startAllBreakdown.other.length - 10} more</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={() => {
                  setShowStartAllBreakdownModal(false);
                }}
                className="btn btn-primary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Update Auth Modal
  const UpdateAuthModal = () => {
    if (!showUpdateAuthModal) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
            setShowUpdateAuthModal(false);
            setUpdateAuthText('');
          }}></div>
          
          <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
            <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Update Auth Tokens</h3>
                <button
                  onClick={() => {
                    setShowUpdateAuthModal(false);
                    setUpdateAuthText('');
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Username:Auth Token (one per line)
                </label>
                <textarea
                  value={updateAuthText}
                  onChange={(e) => setUpdateAuthText(e.target.value)}
                  className="form-textarea w-full"
                  rows={10}
                  placeholder="username1:new_auth_token_here&#10;username2:another_auth_token_here"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Format: username:auth_token (one per line). Accounts will be found by username and their auth token will be updated.
                </p>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={handleBulkUpdateAuth}
                disabled={bulkOperationLoading || !updateAuthText.trim()}
                className="btn btn-primary"
              >
                {bulkOperationLoading ? 'Updating...' : 'Update Auth'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowUpdateAuthModal(false);
                  setUpdateAuthText('');
                }}
                className="btn btn-secondary mr-3"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Update Proxy Modal (username:proxy per line)
  const UpdateProxyModal = () => {
    if (!showUpdateProxyModal) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
            if (!bulkOperationLoading) {
              setShowUpdateProxyModal(false);
              setUpdateProxyText('');
            }
          }}></div>

          <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
            <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Update Proxy</h3>
                <button
                  onClick={() => {
                    if (!bulkOperationLoading) {
                      setShowUpdateProxyModal(false);
                      setUpdateProxyText('');
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Username:Proxy (one per line)
                </label>
                <textarea
                  value={updateProxyText}
                  onChange={(e) => setUpdateProxyText(e.target.value)}
                  className="form-textarea w-full"
                  rows={10}
                  placeholder="username1:host:port:user:pass&#10;username2:host2:port2:user2:pass2"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Format: username:proxy (one per line). Proxy = host:port:username:password. Accounts are found by username and their proxy is updated.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={handleBulkUpdateProxy}
                disabled={bulkOperationLoading || !updateProxyText.trim()}
                className="btn btn-primary"
              >
                {bulkOperationLoading ? 'Updating...' : 'Update Proxy'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!bulkOperationLoading) {
                    setShowUpdateProxyModal(false);
                    setUpdateProxyText('');
                  }
                }}
                className="btn btn-secondary mr-3"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Bulk Update Proxies Modal
  const BulkUpdateProxiesModal = () => {
    if (!showBulkUpdateProxiesModal) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
            if (!bulkUpdateProxiesLoading) {
              setShowBulkUpdateProxiesModal(false);
              setBulkUpdateProxiesText('');
            }
          }}></div>

          <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
            <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Update Proxy</h3>
                <button
                  onClick={() => {
                    if (!bulkUpdateProxiesLoading) {
                      setShowBulkUpdateProxiesModal(false);
                      setBulkUpdateProxiesText('');
                    }
                  }}
                  disabled={bulkUpdateProxiesLoading}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Proxies (one per line, format: host:port:username:password)
                </label>
                <textarea
                  value={bulkUpdateProxiesText}
                  onChange={(e) => setBulkUpdateProxiesText(e.target.value)}
                  className="form-textarea w-full font-mono text-sm"
                  rows={10}
                  placeholder="host1:port1:user1:pass1&#10;host2:port2:user2:pass2"
                  disabled={bulkUpdateProxiesLoading}
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {selectedAccounts.length} account(s) selected. Paste at least {selectedAccounts.length} proxy line(s). First line → first selected account, etc. Extra lines are ignored. No relogin required.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={handleBulkUpdateProxies}
                disabled={bulkUpdateProxiesLoading || !bulkUpdateProxiesText.trim()}
                className="btn btn-primary"
              >
                {bulkUpdateProxiesLoading ? 'Updating...' : 'Update Proxies'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!bulkUpdateProxiesLoading) {
                    setShowBulkUpdateProxiesModal(false);
                    setBulkUpdateProxiesText('');
                  }
                }}
                disabled={bulkUpdateProxiesLoading}
                className="btn btn-secondary mr-3"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const AddToVaModal = () => {
    if (!showAddToVaModal) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={() => {
              if (!addToVaLoading) {
                setShowAddToVaModal(false);
                setAddToVaOwnerKey('');
              }
            }}
          />
          <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Add to Virtual Assistant</h3>
                <button
                  type="button"
                  onClick={() => {
                    if (!addToVaLoading) {
                      setShowAddToVaModal(false);
                      setAddToVaOwnerKey('');
                    }
                  }}
                  disabled={addToVaLoading}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Copy {selectedAccounts.length} selected automation account(s) to a VA or AI VA Agent profile account list. An account can belong to only one VA or one AI VA Agent at a time.
              </p>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select VA <span className="text-red-500">*</span>
              </label>
              <select
                value={addToVaOwnerKey}
                onChange={(e) => setAddToVaOwnerKey(e.target.value)}
                disabled={addToVaLoading}
                className="form-select w-full"
              >
                <option value="">Choose a VA or AI VA Agent…</option>
                {vaUsers.length > 0 && (
                  <optgroup label="Virtual Assistants">
                    {vaUsers.map((va) => (
                      <option key={`va:${va._id}`} value={`va:${va._id}`}>
                        {va.vaProfile?.displayName || va.name || va.username}
                        {va.username ? ` (@${va.username})` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
                {aiAgents.length > 0 && (
                  <optgroup label="AI VA Agents">
                    {aiAgents.map((agent) => (
                      <option key={`agent:${agent._id}`} value={`agent:${agent._id}`}>
                        {agent.name}
                        {agent.online ? '  •  desktop online' : '  •  desktop offline'}
                        {typeof agent.accountCount === 'number' ? `  •  ${agent.accountCount} attached` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              {vaUsers.length === 0 && aiAgents.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  No VAs or AI VA Agents found. Create one in /dashboard/admin/vas.
                </p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Username, password, phone, email, auth token, ct0, and proxy are copied.
                Large batches run in the background with a progress bar (including GoLogin import when a folder is configured).
                Accounts already owned elsewhere are moved to the selected owner.
              </p>
              {addToVaJobProgress && (
                <div className="mt-4 space-y-2">
                  <GoLoginJobProgressBar
                    progress={addToVaJobProgress}
                    title={
                      addToVaJobProgress.status === 'cancelling'
                        ? 'Stopping…'
                        : addToVaJobProgress.phase === 'gologin'
                          ? `GoLogin import${addToVaJobProgress.folderName ? ` → ${addToVaJobProgress.folderName}` : ''}`
                          : addToVaJobProgress.phase === 'loading'
                            ? 'Loading automation accounts…'
                            : 'Adding accounts to VA / AI Agent…'
                    }
                  />
                  {addToVaJobProgress.phase === 'gologin' && addToVaJobProgress.status === 'running' && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Only profiles without a GoLogin id are imported.
                      {typeof addToVaJobProgress.gologinAlreadyHadProfile === 'number' && (
                        <>
                          {' '}
                          {addToVaJobProgress.gologinAlreadyHadProfile.toLocaleString()} of{' '}
                          {(addToVaJobProgress.attachTotal
                            || (addToVaJobProgress.gologinQueued || 0)
                              + addToVaJobProgress.gologinAlreadyHadProfile
                          ).toLocaleString()}{' '}
                          already had a GoLogin profile and were skipped.
                        </>
                      )}
                      {' '}Progress {addToVaJobProgress.processed || 0} / {addToVaJobProgress.total || 0} is the remaining queue.
                    </p>
                  )}
                  {addToVaLoading && addToVaJobId && (
                    <button
                      type="button"
                      onClick={handleCancelAddToVa}
                      disabled={addToVaCancelling || addToVaJobProgress.status === 'cancelling'}
                      className="w-full inline-flex justify-center items-center gap-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm font-medium text-red-700 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/50 disabled:opacity-50"
                    >
                      {addToVaCancelling || addToVaJobProgress.status === 'cancelling'
                        ? 'Stopping…'
                        : 'Stop'}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={handleAddToVa}
                disabled={addToVaLoading || !addToVaOwnerKey}
                className="btn btn-primary"
              >
                {addToVaLoading
                  ? 'Working…'
                  : addToVaOwnerKey.startsWith('agent:')
                    ? 'Add to AI VA Agent'
                    : 'Add to VA'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!addToVaLoading) {
                    setShowAddToVaModal(false);
                    setAddToVaOwnerKey('');
                    setAddToVaJobProgress(null);
                  } else if (addToVaJobId) {
                    handleCancelAddToVa();
                  }
                }}
                disabled={addToVaCancelling}
                className="btn btn-secondary mr-3"
              >
                {addToVaLoading ? (addToVaCancelling ? 'Stopping…' : 'Stop') : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const BulkResetTlsModal = () => {
    if (!showBulkResetTlsModal) return null;

    const defaultUaPlaceholder =
      'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/142.0.0.0 Safari/537.36';

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={() => {
              if (!bulkOperationLoading) {
                setShowBulkResetTlsModal(false);
                setBulkResetTlsUserAgent('');
              }
            }}
          />

          <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
            <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Reset TLS fingerprint &amp; User-Agent</h3>
                <button
                  type="button"
                  onClick={() => {
                    if (!bulkOperationLoading) {
                      setShowBulkResetTlsModal(false);
                      setBulkResetTlsUserAgent('');
                    }
                  }}
                  disabled={bulkOperationLoading}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Removes stored <span className="font-mono">sessionData</span> JA3, JA4, and JA4R (CycleTLS) for{' '}
                {selectedAccounts.length} selected account(s) and sets the User-Agent below. Leave the field empty to use
                the default Windows Chrome string (same as new bulk-added accounts).
              </p>

              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">User-Agent (optional)</label>
                <textarea
                  value={bulkResetTlsUserAgent}
                  onChange={(e) => setBulkResetTlsUserAgent(e.target.value)}
                  className="form-textarea w-full font-mono text-sm"
                  rows={3}
                  placeholder={defaultUaPlaceholder}
                  disabled={bulkOperationLoading}
                />
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={handleBulkResetTlsFingerprint}
                disabled={bulkOperationLoading}
                className="btn btn-primary"
              >
                {bulkOperationLoading ? 'Applying…' : 'Reset TLS & User-Agent'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!bulkOperationLoading) {
                    setShowBulkResetTlsModal(false);
                    setBulkResetTlsUserAgent('');
                  }
                }}
                disabled={bulkOperationLoading}
                className="btn btn-secondary mr-3"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const AccountModal = ({ isOpen, onClose, onSubmit, title }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 glass-overlay transition-opacity" onClick={onClose}></div>
          
          <div className="inline-block align-bottom glass-modal text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <form onSubmit={onSubmit}>
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
                
                <div className="space-y-4">
                  {/* Username */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Username</label>
                    <input
                      type="text"
                      value={formData.username || ''}
                      onChange={(e) => setFormData({...formData, username: e.target.value})}
                      className="form-input mt-1"
                      required
                    />
                  </div>
                  
                  {/* Account String (Simple Format: username:authToken) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Auth Token</label>
                    <input
                      type="text"
                      value={formData.accountStringSimple || ''}
                      onChange={(e) => setFormData({...formData, accountStringSimple: e.target.value})}
                      className="form-input mt-1"
                      placeholder="username:auth_token"
                      required
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Format: username:auth_token
                    </p>
                  </div>
                  
                  {/* Model Preset */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Model Preset</label>
                    <select
                      value={formData.modelId || ''}
                      onChange={(e) => setFormData({...formData, modelId: e.target.value})}
                      className="form-select mt-1"
                    >
                      <option value="">Select a model preset...</option>
                      {models.map((model) => {
                        const presetName = getPresetName(model.preset);
                        return (
                          <option key={model._id} value={model._id}>
                            {model.displayName || model.name}{model.displayName ? ` (${model.name})` : ''} - {model.age}y, {model.gender}{presetName ? ` • ${presetName}` : ''}{user?.role === 'admin' && model.userId?.username ? ` (${model.userId.username})` : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  
                  {/* Proxy String */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Proxy String</label>
                    <input
                      type="text"
                      value={formData.proxyString || ''}
                      onChange={(e) => setFormData({...formData, proxyString: e.target.value})}
                      className="form-input mt-1"
                      placeholder="v2.proxyempire.io:5000:username:password:proxy_auth"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Format: IP:PORT:Username:Password[:proxy_auth] (leave empty to disable proxy)
                    </p>
                  </div>
                  
                  {/* User Agent */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">User Agent</label>
                    <input
                      type="text"
                      value={formData.userAgent || ''}
                      onChange={(e) => setFormData({...formData, userAgent: e.target.value})}
                      className="form-input mt-1"
                      required
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Browser user agent string for X.com login
                    </p>
                  </div>
                  
                  {/* XChat E2EE Private Key */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      XChat E2EE Private Key
                      {formData.xchatPrivateKey && (
                        <span className="ml-2 text-xs text-green-500">● Set</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={formData.xchatPrivateKey || ''}
                      onChange={(e) => setFormData({...formData, xchatPrivateKey: e.target.value})}
                      className="form-input mt-1 font-mono text-xs"
                      placeholder="PKCS#8 hex (e.g. 308187020100...)"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      P-256 ECDH private key in PKCS#8 hex format. Auto-extracted during automation or paste manually.
                    </p>
                  </div>
                  
                  {/* Tags Section - visible if user has scraper or checker features */}
                  {(user?.features?.canUseScraper || user?.features?.canUseActiveCheckers || user?.features?.canUseFollowBackCheckers || user?.role === 'admin') && (
                    <div className="border-t pt-4 space-y-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Tags
                      </label>
                      <div className="space-y-2">
                        {(user?.features?.canUseScraper || user?.role === 'admin') && (
                          <>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.scraperTag || false}
                                onChange={(e) => setFormData({...formData, scraperTag: e.target.checked})}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                Add 'scraper' tag
                              </span>
                            </label>
                            <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                              Tag account with 'scraper' tag for use in scraping followers
                            </p>
                          </>
                        )}
                        {(user?.features?.canUseScraper || user?.features?.canUseActiveCheckers || user?.role === 'admin') && (
                          <>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.activeCheckerTag || false}
                                onChange={(e) => setFormData({...formData, activeCheckerTag: e.target.checked})}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                Add 'active_checker' tag
                              </span>
                            </label>
                            <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                              Tag account with 'active_checker' tag for checking user activity in parallel
                            </p>
                          </>
                        )}
                        {(user?.features?.canUseScraper || user?.role === 'admin') && (
                          <>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.sourceTag || false}
                                onChange={(e) => setFormData({...formData, sourceTag: e.target.checked})}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                Add 'source' tag
                              </span>
                            </label>
                            <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                              Tag account for sourcing new target usernames from the For You feed (auto-discovers accounts to scrape)
                            </p>
                          </>
                        )}
                        {(user?.features?.canUseScraper || user?.features?.canUseFollowBackCheckers || user?.role === 'admin') && (
                          <>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.followBackCheckerTag || false}
                                onChange={(e) => setFormData({...formData, followBackCheckerTag: e.target.checked})}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                Add 'follow_back_checker' tag
                              </span>
                            </label>
                            <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                              Tag account for follow-back automation scanning (pool used when &quot;Use follow-back checkers&quot; is on)
                            </p>
                          </>
                        )}
                        {user?.role === 'admin' && (
                          <>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.viewCheckerTag || false}
                                onChange={(e) => setFormData({...formData, viewCheckerTag: e.target.checked})}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                Add 'view_checker' tag
                              </span>
                            </label>
                            <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                              Tag account for auto-comment view checking (pool used for tracking comment view counts)
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-gray-50/50 dark:bg-gray-700/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-white/20 dark:border-gray-700/30">
                <button type="submit" className="btn btn-primary sm:ml-3" disabled={submitting}>
                  {submitting ? 'Saving...' : (selectedAccount ? 'Update' : 'Add')} Account
                </button>
                <button type="button" onClick={onClose} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner h-8 w-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className={`relative overflow-hidden rounded-2xl ${
        isDarkMode 
          ? 'bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900' 
          : 'bg-gradient-to-br from-sky-50 via-blue-100 to-sky-100'
      }`}>
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Animated gradient orbs */}
          <div className={`absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl ${
            isDarkMode ? 'bg-blue-600/20' : 'bg-blue-300/40'
          }`} />
          <div className={`absolute -bottom-32 -left-32 w-80 h-80 rounded-full blur-3xl ${
            isDarkMode ? 'bg-sky-600/15' : 'bg-sky-300/30'
          }`} />
          <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl ${
            isDarkMode ? 'bg-cyan-600/10' : 'bg-cyan-200/30'
          }`} />
          
          {/* Grid pattern overlay */}
          <div 
            className={`absolute inset-0 ${isDarkMode ? 'opacity-[0.03]' : 'opacity-[0.08]'}`}
            style={{
              backgroundImage: `linear-gradient(${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px), linear-gradient(90deg, ${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />
          
          {/* Floating decorative shapes */}
          <svg className="absolute top-8 right-12 w-20 h-20 opacity-10" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="40" fill="none" stroke={isDarkMode ? '#93c5fd' : '#3b82f6'} strokeWidth="2" />
            <circle cx="50" cy="50" r="25" fill="none" stroke={isDarkMode ? '#93c5fd' : '#3b82f6'} strokeWidth="1.5" />
          </svg>
          <svg className="absolute bottom-6 right-1/4 w-16 h-16 opacity-10" viewBox="0 0 100 100">
            <rect x="20" y="20" width="60" height="60" rx="8" fill="none" stroke={isDarkMode ? '#7dd3fc' : '#0284c7'} strokeWidth="2" transform="rotate(15 50 50)" />
          </svg>
          <svg className="absolute top-1/3 left-8 w-12 h-12 opacity-10" viewBox="0 0 100 100">
            <polygon points="50,10 90,90 10,90" fill="none" stroke={isDarkMode ? '#67e8f9' : '#0891b2'} strokeWidth="2" />
          </svg>
        </div>
        
        {/* Content */}
        <div className="relative px-8 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              {/* Icon/Logo area */}
              <div className={`relative flex items-center justify-center w-16 h-16 rounded-2xl ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-blue-500 to-sky-600 shadow-lg shadow-blue-500/30' 
                  : 'bg-gradient-to-br from-blue-500 to-sky-500 shadow-lg shadow-blue-400/40'
              }`}>
                <UserIcon className="w-8 h-8 text-white" />
                <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                  isDarkMode ? 'bg-cyan-400' : 'bg-cyan-500'
                }`}>
                  <SparklesIcon className="w-3 h-3 text-white" />
                </div>
              </div>
              
              {/* Text content */}
              <div>
                <h1 className={`text-3xl font-bold tracking-tight ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  Account Management
                </h1>
                <p className={`mt-1.5 text-base ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  Manage, monitor, and automate your X.com accounts
                </p>
                <div className={`mt-3 flex items-center space-x-4 text-sm ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}>
                  <span className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${
                      isDarkMode ? 'bg-emerald-400' : 'bg-emerald-500'
                    }`} />
                    {accountSummary.automationEnabled} Automation on
                  </span>
                  <span className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${
                      isDarkMode ? 'bg-slate-500' : 'bg-slate-400'
                    }`} />
                    {accountSummary.totalAccounts} Total
                    {accountSummary.summaryScoped &&
                      accountSummary.globalTotalAccounts != null &&
                      accountSummary.globalTotalAccounts !== accountSummary.totalAccounts && (
                        <span
                          className="ml-1.5 text-xs font-normal opacity-80"
                          title="Counts match the current filters / group tab"
                        >
                          ({accountSummary.globalTotalAccounts} fleet-wide)
                        </span>
                      )}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={async () => {
                  try {
                    setBulkOperationResults(null);
                    await api.post('/accounts/clear-errors', {});
                    await fetchAccounts();
                    setClearStatusesKey(prev => prev + 1);
                  } catch (error) {
                    console.error('Error clearing account errors:', error);
                    toast.error('Failed to clear account errors');
                  }
                }}
                className={`inline-flex items-center px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-white/10 hover:bg-white/15 text-white border border-white/10 hover:border-white/20' 
                    : 'bg-white/60 hover:bg-white/80 text-slate-700 border border-white/60 shadow-sm hover:shadow'
                }`}
                title="Clear all status messages"
              >
                <XCircleIcon className="h-4 w-4 mr-2" />
                Clear Statuses
              </button>
              {(user?.role === 'admin' || user?.features?.canAddAccounts) && (
                <button
                  onClick={() => setShowBulkModal(true)}
                  className={`inline-flex items-center px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40' 
                      : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                  }`}
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Account
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Models active in the last 7 days{pinnedModelIds.length ? ', plus saved' : ''}
          </p>
          <button
            type="button"
            onClick={() => {
              setPinDraft(pinnedModelIds.map(String));
              setModelPickerOpen((open) => !open);
            }}
            className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
          >
            {modelPickerOpen ? 'Close' : 'Choose models'}
          </button>
        </div>
        {modelPickerOpen && (
          <div className="card mb-3 p-3">
            <div className="max-h-52 overflow-y-auto space-y-1 mb-3">
              {modelStats.map((model) => {
                const checked = pinDraft.includes(String(model.modelId));
                return (
                  <label key={model.modelId} className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const id = String(model.modelId);
                        setPinDraft((ids) => (checked ? ids.filter((item) => item !== id) : [...ids, id]));
                      }}
                    />
                    <span className="truncate">{model.name}</span>
                    {!model.activeRecently && (
                      <span className="text-[10px] text-gray-400">quiet</span>
                    )}
                  </label>
                );
              })}
            </div>
            <button
              type="button"
              onClick={savePinnedModels}
              disabled={pinsSaving}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50"
            >
              {pinsSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
        {modelStatsLoading && modelStats.length === 0 ? (
          <div className="card"><div className="card-body py-4 text-center text-sm text-gray-400">Loading model counts…</div></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {modelStats.filter((model) => model.activeRecently || model.pinned).map((model) => (
              <div key={model.modelId} className="card px-3 py-2">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{model.name}</p>
                  {model.pinned && <span className="text-[10px] text-primary-500 shrink-0">Saved</span>}
                </div>
                <div className="grid grid-cols-6 gap-1">
                  {[
                    { label: 'Running', value: model.running, color: 'text-green-600 dark:text-green-400', status: 'active', automation: 'active' },
                    { label: 'Total Enabled', value: model.totalEnabled, color: 'text-blue-600 dark:text-blue-400', status: 'all', automation: 'active' },
                    { label: 'Captcha', value: model.captcha, color: 'text-yellow-600 dark:text-yellow-400', status: 'captcha', automation: 'all' },
                    { label: 'Key Error', value: model.keyError, color: 'text-orange-600 dark:text-orange-400', status: 'xchat_key_error', automation: 'all' },
                    { label: 'Auth Expired', value: model.authExpired, color: 'text-red-700 dark:text-red-300', status: 'auth_expired', automation: 'all' },
                    { label: 'Suspended', value: model.suspended, color: 'text-red-600 dark:text-red-400', status: 'suspended', automation: 'all' },
                  ].map((stat) => {
                    const active = String(filterModelId) === String(model.modelId)
                      && filterStatus === stat.status
                      && filterAutomation === stat.automation;
                    return (
                      <button
                        key={stat.label}
                        type="button"
                        title={stat.label}
                        onClick={() => focusModelStat(model.modelId, stat.status, stat.automation)}
                        className={`rounded-lg py-1 text-center hover:bg-gray-50 dark:hover:bg-gray-700/40 ${active ? 'ring-2 ring-primary-500' : ''}`}
                      >
                        <p className={`text-base font-bold leading-tight ${stat.color}`}>{stat.value}</p>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">{stat.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {!modelStatsLoading && modelStats.filter((model) => model.activeRecently || model.pinned).length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400">No models active in the last 7 days. Choose models to keep them here.</p>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('accounts');
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'accounts'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <UserIcon className="h-5 w-5 inline mr-2" />
            Accounts
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'accounts' && (
        <>
          {/* Bulk Actions */}
          {accounts.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg mb-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Selection Controls */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSelectAllAccounts}
                      disabled={bulkOperationLoading}
                      className="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {(() => {
                        if (usePagination) {
                          const scopeTotal = accountSummary.totalAccounts || 0;
                          const allOnPageSelected =
                            filteredAccounts.length > 0 &&
                            filteredAccounts.every((acc) => selectedAccounts.includes(acc._id));
                          const allScopeSelected =
                            scopeTotal > 0 &&
                            selectedAccounts.length >= scopeTotal &&
                            allOnPageSelected;
                          return allScopeSelected ? 'Deselect All' : 'Select All';
                        }
                        return filteredAccounts.every((acc) => selectedAccounts.includes(acc._id))
                          ? 'Deselect All'
                          : 'Select All';
                      })()}
                    </button>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {usePagination ? (
                        <>
                          {selectedAccounts.length} selected
                          <span className="ml-1 text-gray-400">
                            · {accountSummary.totalAccounts} in scope · {accounts.length} on page
                          </span>
                        </>
                      ) : (
                        <>
                          {selectedAccounts.length} of {accounts.length} selected
                          {filteredAccounts.length !== accounts.length && (
                            <span className="ml-1 text-gray-400">({filteredAccounts.length} filtered)</span>
                          )}
                        </>
                      )}
                    </span>
                  </div>
                  
                  {/* Divider */}
                  <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 hidden sm:block"></div>
                  
                  {/* Quick Actions - Start All / Stop All */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleStartAll}
                      disabled={bulkOperationLoading || bulkScope.loading || bulkScope.startable === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      title={
                        bulkScope.startable === 0
                          ? 'No accounts ready to start in this scope'
                          : `Start ${bulkScope.startable} ready account(s) in this scope (all pages)`
                      }
                    >
                      <PlayIcon className="h-4 w-4" />
                      Start All ({bulkScope.loading ? '…' : bulkScope.startable})
                    </button>
                    <button
                      onClick={handleStopAll}
                      disabled={bulkOperationLoading || bulkScope.loading || bulkScope.stoppable === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      title={
                        bulkScope.stoppable === 0
                          ? 'No accounts with automation enabled in this scope'
                          : `Stop ${bulkScope.stoppable} account(s) in this scope (all pages)`
                      }
                    >
                      <StopIcon className="h-4 w-4" />
                      Stop All ({bulkScope.loading ? '…' : bulkScope.stoppable})
                    </button>
                  </div>
                </div>
                
                {/* Filters */}
                <div className="flex items-center space-x-3 flex-wrap">
                  {/* Search Bar */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search accounts..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="text-sm border border-gray-300 dark:border-gray-600 rounded-xl pl-10 pr-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 w-48"
                    />
                  </div>
                  
                  {/* Model Filter */}
                  <select
                    value={filterModelId}
                    onChange={(e) => setFilterModelId(e.target.value)}
                    className="text-sm border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">All Models</option>
                    {models.map(model => (
                      <option key={model._id} value={model._id}>
                        {model.displayName || model.name}{model.displayName ? ` (${model.name})` : ''}
                      </option>
                    ))}
                  </select>
                  
                  {/* Status Filter */}
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="text-sm border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">All Status</option>
                    {availableStatuses.map(status => (
                      <option key={status} value={status}>
                        {statusDisplayNames[status] || status}
                      </option>
                    ))}
                  </select>
                  
                  {/* Automation Filter */}
                  <select
                    value={filterAutomation}
                    onChange={(e) => setFilterAutomation(e.target.value)}
                    className="text-sm border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">All Automation</option>
                    <option value="starting">Starting up</option>
                    <option value="running_stable">Running (started)</option>
                    <option value="start_failed">Start failed</option>
                    <option value="active">Active (any)</option>
                    <option value="inactive">Inactive</option>
                  </select>

                  {/* Profile data filter (Missing DoB / Phone / Update status) */}
                  <select
                    value={filterProfile}
                    onChange={(e) => setFilterProfile(e.target.value)}
                    className="text-sm border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    title="Filter by extracted DoB / phone from X profile"
                  >
                    <option value="all">All profile data</option>
                    <option value="missing_dob">Missing DoB</option>
                    <option value="missing_phone">Missing Phone</option>
                    <option value="missing_both">Missing DoB &amp; Phone</option>
                    <option value="email_verified">Email Verified</option>
                    <optgroup label="Profile Update Status">
                      <option value="profile_updated">✓ Fully updated</option>
                      <option value="profile_partial">⚠ Partial / Failed</option>
                      <option value="profile_never_updated">— Never updated</option>
                    </optgroup>
                  </select>

                  {/* Sandbox graduation (has_graduated_access) — account state */}
                  <select
                    value={filterSandbox}
                    onChange={(e) => setFilterSandbox(e.target.value)}
                    className="text-sm border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    title="Filter by X sandbox graduation (has_graduated_access)"
                  >
                    <option value="all">All sandbox states</option>
                    <option value="sandbox">In sandbox</option>
                    <option value="graduated">Out of sandbox</option>
                    <option value="unknown">Unknown (not checked)</option>
                  </select>

                  {/* Tags (Account.tags) */}
                  <select
                    value={filterTag}
                    onChange={(e) => setFilterTag(e.target.value)}
                    className="text-sm border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    title="Filter by account tag"
                  >
                    <option value="all">All tags</option>
                    <option value="needs-pin-reset">needs-pin-reset</option>
                    <option value="scraper">scraper</option>
                    <option value="source">source</option>
                    <option value="active_checker">active_checker</option>
                    <option value="follow_back_checker">follow_back_checker</option>
                    <option value="view_checker">view_checker</option>
                  </select>

                  {/* Age (createdAt): high = oldest accounts first, low = newest first */}
                  <select
                    value={filterAgeOrder}
                    onChange={(e) => setFilterAgeOrder(e.target.value)}
                    className="text-sm border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    title="Sort by when the account was added (createdAt)"
                  >
                    <option value="low">Age: Low → High (newest first)</option>
                    <option value="high">Age: High → Low (oldest first)</option>
                  </select>
                  
                  {/* Clear Filters */}
                  {(filterModelId !== 'all' || filterVpsAgentId !== 'all' || filterStatus !== 'all' || filterAutomation !== 'all' || filterProfile !== 'all' || filterSandbox !== 'all' || filterTag !== 'all' || filterAgeOrder !== 'low' || searchQuery.trim() !== '') && (
                    <button
                      onClick={() => {
                        setFilterModelId('all');
                        setFilterVpsAgentId('all');
                        setFilterStatus('all');
                        setFilterAutomation('all');
                        setFilterProfile('all');
                        setFilterSandbox('all');
                        setFilterTag('all');
                        setFilterAgeOrder('low');
                        setSearchQuery('');
                      }}
                      className="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100 underline"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>
              
              {/* Account Status Summary */}
              {accounts.length > 0 && (() => {
                const authExpiredCount = accountSummary.statusCounts?.authExpired ?? 0;
                const loginFailedCount = accountSummary.statusCounts?.loginFailed ?? 0;
                const captchaCount = accountSummary.statusCounts?.captcha ?? 0;
                const loginRequiredCount = accountSummary.statusCounts?.loginRequired ?? 0;
                
                return (
                  <div className="mb-6 bg-white dark:bg-gray-800 rounded-2xl shadow p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Account Status</h3>
                        {/* Status Changes Dropdown */}
                        {(() => {
                          // One row per account: first time they hit a "problem" status (suspended, auth, captcha, etc.)
                          const leafSetForStatus = (
                            selectedGroup !== 'all' &&
                            selectedGroup !== 'ungrouped'
                          )
                            ? resolveLeafGroupsForGroupName(selectedGroup)
                            : null;
                          const visibleAccounts = selectedGroup === 'all'
                            ? accounts
                            : selectedGroup === 'ungrouped'
                              ? accounts.filter((a) => !(a.group && String(a.group).trim()))
                              : accounts.filter(
                                  (a) => a.group && leafSetForStatus && leafSetForStatus.has(a.group)
                                );
                          const DROPDOWN_NOTABLE_STATUSES = new Set([
                            'suspended', 'banned', 'auth_expired', 'captcha', 'login_failed',
                            'session_extraction_failed', 'proxy_required', 'proxy_failed', 'proxy_error',
                            'daily_limit', 'conversation_limit_reached', 'xchat_key_error', 'login_required'
                          ]);
                          const allChanges = [];
                          for (const acc of visibleAccounts) {
                            const hist = [...(acc.statusHistory || [])].sort(
                              (a, b) => new Date(a.changedAt).getTime() - new Date(b.changedAt).getTime()
                            );
                            const firstNotable = hist.find((e) => DROPDOWN_NOTABLE_STATUSES.has(e.status));
                            if (firstNotable) {
                              allChanges.push({
                                ...firstNotable,
                                username: acc.username,
                                accountId: acc._id
                              });
                            }
                          }
                          allChanges.sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime());
                          const displayChanges = allChanges.slice(0, 100);

                          const statusLabel = (s) => ({
                            active: 'Logged-in', suspended: 'Suspended', banned: 'Banned',
                            pending_validation: 'Pending', login_failed: 'Login Failed',
                            session_extraction_failed: 'Extraction Failed', proxy_required: 'Proxy Required',
                            proxy_failed: 'Proxy Failed', proxy_error: 'Proxy Error',
                            auth_expired: 'Auth Expired', captcha: 'Captcha',
                            daily_limit: 'Daily Limit', conversation_limit_reached: 'Conv. Limit',
                            xchat_key_error: 'Key Error', login_required: 'Login Required'
                          }[s] || s);
                          const statusColor = (s) => ({
                            active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                            suspended: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                            banned: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                            auth_expired: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
                            captcha: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
                            daily_limit: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
                            login_failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                            proxy_error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                            proxy_failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                            proxy_required: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                            session_extraction_failed: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
                            login_required: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                            conversation_limit_reached: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
                            xchat_key_error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }[s] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300');
                          const timeAgo = (d) => {
                            const diffMs = Date.now() - new Date(d).getTime();
                            const mins = Math.floor(diffMs / 60000);
                            if (mins < 1) return 'just now';
                            if (mins < 60) return `${mins}m ago`;
                            const hrs = Math.floor(mins / 60);
                            if (hrs < 24) return `${hrs}h ago`;
                            const days = Math.floor(hrs / 24);
                            return `${days}d ago`;
                          };

                          const statusChangeSourceDisplay = (src) => {
                            if (!src) return 'Unknown source';
                            const map = {
                              follow_task: 'Follow task',
                              follow_back: 'Follow-back automation',
                              follow_task_unfollow: 'Follow task (unfollow)',
                              mass_dm: 'Mass DM',
                              chatbot_automation: 'Chatbot automation',
                              dm_send: 'DM send',
                              xchat: 'XChat / DM keys',
                              x_api: 'X API (other)',
                              mass_task: 'Mass task',
                              login: 'Login / dashboard',
                              keep_alive: 'Keep-alive / refresh',
                              scraper: 'Scraper'
                            };
                            return map[src] || src.replace(/_/g, ' ');
                          };

                          return (
                            <div className="relative">
                              <button
                                onClick={() => setShowStatusChanges(!showStatusChanges)}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-xl border transition-colors ${
                                  showStatusChanges
                                    ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700'
                                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                                }`}
                              >
                                <ClockIcon className="h-3.5 w-3.5" />
                                Status Changes
                                <span className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none ${
                                  showStatusChanges
                                    ? 'bg-indigo-200 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200'
                                    : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200'
                                }`}>{allChanges.length}</span>
                              </button>
                              {showStatusChanges && (
                                <>
                                  <div className="fixed inset-0 z-10" onClick={() => setShowStatusChanges(false)}></div>
                                  <div className="absolute left-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-20 overflow-hidden">
                                    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                                      <div>
                                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 block">
                                          First problem status (per account)
                                        </span>
                                        <span className="text-[10px] text-gray-400 dark:text-gray-500 font-normal">
                                          One entry per account — when it first hit suspended, auth, captcha, etc.
                                          {selectedGroup !== 'all' && (
                                            <span className="ml-1">
                                              · {selectedGroup === 'ungrouped' ? 'Ungrouped' : selectedGroup}
                                            </span>
                                          )}
                                        </span>
                                      </div>
                                      <span className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                                        {displayChanges.length}{allChanges.length > 100 ? ` / ${allChanges.length}` : ''}
                                      </span>
                                    </div>
                                    <div className="max-h-80 overflow-y-auto divide-y divide-gray-50 dark:divide-gray-700/50">
                                      {displayChanges.length === 0 ? (
                                        <div className="px-4 py-8 text-center">
                                          <ClockIcon className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                                          <p className="text-xs text-gray-400 dark:text-gray-500">No first-time problem statuses yet</p>
                                          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">Shows the first suspended / auth / captcha (etc.) per account from history</p>
                                        </div>
                                      ) : displayChanges.map((entry) => {
                                        const ts = new Date(entry.changedAt);
                                        return (
                                          <div key={`${entry.accountId}-${entry.changedAt}`} className="px-4 py-2 flex items-start gap-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap mt-0.5 ${statusColor(entry.status)}`}>
                                              {statusLabel(entry.status)}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                              <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">@{entry.username}</p>
                                              <p className="text-[10px] text-indigo-600 dark:text-indigo-400 truncate" title={entry.source || ''}>
                                                {statusChangeSourceDisplay(entry.source)}
                                              </p>
                                              {entry.reason && (
                                                <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate" title={entry.reason}>{entry.reason}</p>
                                              )}
                                            </div>
                                            <span className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap mt-0.5" title={ts.toLocaleString()}>
                                              {timeAgo(entry.changedAt)}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex items-center space-x-6">
                        <div 
                          className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => {
                            setFilterStatus('active');
                            setFilterAutomation('active');
                          }}
                        >
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                          <span className="text-sm text-gray-600 dark:text-gray-400">Active:</span>
                          <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                            {accountSummary.automationEnabled}
                          </span>
                        </div>
                        <div 
                          className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => {
                            setFilterStatus('not_active');
                            setFilterAutomation('inactive');
                          }}
                        >
                          <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                          <span className="text-sm text-gray-600 dark:text-gray-400">Inactive:</span>
                          <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                            {accountSummary.automationInactive}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Total: <span className="font-semibold">{accountSummary.totalAccounts}</span>
                        </div>
                      </div>
                    </div>

                    {/* Who is starting / fully running / start failed — between summary row and problem-status shortcuts */}
                    {(() => {
                      const leafSetLifecycle =
                        selectedGroup !== 'all' && selectedGroup !== 'ungrouped'
                          ? resolveLeafGroupsForGroupName(selectedGroup)
                          : null;
                      const visibleAccounts =
                        selectedGroup === 'all'
                          ? accounts
                          : selectedGroup === 'ungrouped'
                            ? accounts.filter((a) => !(a.group && String(a.group).trim()))
                            : accounts.filter(
                                (a) => a.group && leafSetLifecycle && leafSetLifecycle.has(a.group)
                              );
                      const isStartingUp = (a) =>
                        a.automation?.starting === true || a.automationQueueBooting === true;
                      const startingList = visibleAccounts.filter(isStartingUp);
                      const runningList = visibleAccounts.filter(
                        (a) => a.automation?.enabled === true && !isStartingUp(a)
                      );
                      const failedList = visibleAccounts.filter(
                        (a) =>
                          a.automationStartFailed === true &&
                          !a.automation?.enabled &&
                          !isStartingUp(a)
                      );
                      const classifyShort = (c) =>
                        (
                          {
                            xchat_key_error: 'Key error',
                            lock_timeout: 'Lock timeout',
                            generic: 'Error'
                          }[c] || (c ? String(c).replace(/_/g, ' ') : '')
                        );
                      const failReason = (a) =>
                        (a.automationStartError || a.lastError || 'Unknown error').trim();
                      const totalListed = startingList.length + runningList.length + failedList.length;

                      if (visibleAccounts.length === 0) return null;

                      return (
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              Automation activity
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setShowAutomationLifecycle(!showAutomationLifecycle);
                                if (!showAutomationLifecycle) setShowStatusChanges(false);
                              }}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-xl border transition-colors ${
                                showAutomationLifecycle
                                  ? 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-800 dark:text-cyan-200 border-cyan-300 dark:border-cyan-700'
                                  : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                              }`}
                            >
                              <CpuChipIcon className="h-3.5 w-3.5" />
                              Who is starting / running / failed
                              <span
                                className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none ${
                                  showAutomationLifecycle
                                    ? 'bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100'
                                    : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200'
                                }`}
                              >
                                {totalListed}
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setFilterAutomation('starting');
                                setFilterStatus('all');
                              }}
                              disabled={startingList.length === 0}
                              className="px-2.5 py-1 text-[11px] font-medium rounded-lg bg-amber-50 dark:bg-amber-900/25 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-amber-100 dark:hover:bg-amber-900/40"
                            >
                              Starting ({startingList.length})
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setFilterAutomation('running_stable');
                                setFilterStatus('active');
                              }}
                              disabled={runningList.length === 0}
                              className="px-2.5 py-1 text-[11px] font-medium rounded-lg bg-emerald-50 dark:bg-emerald-900/25 text-emerald-800 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-emerald-100 dark:hover:bg-emerald-900/40"
                            >
                              Started ({runningList.length})
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setFilterAutomation('start_failed');
                                setFilterStatus('all');
                              }}
                              disabled={failedList.length === 0}
                              className="px-2.5 py-1 text-[11px] font-medium rounded-lg bg-rose-50 dark:bg-rose-900/25 text-rose-800 dark:text-rose-200 border border-rose-200 dark:border-rose-800 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-rose-100 dark:hover:bg-rose-900/40"
                            >
                              Start failed ({failedList.length})
                            </button>
                          </div>
                          {showAutomationLifecycle && (
                            <>
                              <div
                                className="fixed inset-0 z-10"
                                onClick={() => setShowAutomationLifecycle(false)}
                                aria-hidden="true"
                              />
                              <div className="relative z-20 mb-1">
                                <div className="absolute left-0 mt-1 w-full max-w-xl bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                                  <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 text-[10px] text-gray-500 dark:text-gray-400">
                                    {selectedGroup !== 'all' && (
                                      <span>
                                        Group: {selectedGroup === 'ungrouped' ? 'Ungrouped' : selectedGroup}
                                        {' · '}
                                      </span>
                                    )}
                                    Accounts currently starting up, finished starting (automation on), or whose last start attempt failed.
                                  </div>
                                  <div className="max-h-96 overflow-y-auto">
                                    <div className="px-3 py-2 bg-amber-50/80 dark:bg-amber-900/15 border-b border-amber-100 dark:border-amber-900/40">
                                      <p className="text-xs font-semibold text-amber-900 dark:text-amber-100">
                                        Starting up ({startingList.length})
                                      </p>
                                    </div>
                                    {startingList.length === 0 ? (
                                      <div className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">None</div>
                                    ) : (
                                      startingList.map((a) => (
                                        <button
                                          type="button"
                                          key={String(a._id)}
                                          onClick={() => {
                                            setSearchQuery(a.username || '');
                                            setFilterAutomation('starting');
                                            setFilterStatus('all');
                                            setShowAutomationLifecycle(false);
                                          }}
                                          className="w-full text-left px-4 py-2 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700/40 border-b border-gray-50 dark:border-gray-700/50"
                                        >
                                          <ArrowPathIcon className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                                          <div className="min-w-0 flex-1">
                                            <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                                              @{a.username}
                                            </p>
                                            {a.automationStartJobState ? (
                                              <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                                                Queue: {a.automationStartJobState}
                                              </p>
                                            ) : null}
                                          </div>
                                          <span className="text-[10px] text-gray-400 shrink-0">filter</span>
                                        </button>
                                      ))
                                    )}
                                    <div className="px-3 py-2 bg-emerald-50/80 dark:bg-emerald-900/15 border-b border-emerald-100 dark:border-emerald-900/40">
                                      <p className="text-xs font-semibold text-emerald-900 dark:text-emerald-100">
                                        Started — automation on ({runningList.length})
                                      </p>
                                    </div>
                                    {runningList.length === 0 ? (
                                      <div className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">None</div>
                                    ) : (
                                      runningList.map((a) => (
                                        <button
                                          type="button"
                                          key={String(a._id)}
                                          onClick={() => {
                                            setSearchQuery(a.username || '');
                                            setFilterAutomation('running_stable');
                                            setFilterStatus('active');
                                            setShowAutomationLifecycle(false);
                                          }}
                                          className="w-full text-left px-4 py-2 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700/40 border-b border-gray-50 dark:border-gray-700/50"
                                        >
                                          <CheckCircleIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                                          <span className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                                            @{a.username}
                                          </span>
                                          <span className="text-[10px] text-gray-400 ml-auto shrink-0">filter</span>
                                        </button>
                                      ))
                                    )}
                                    <div className="px-3 py-2 bg-rose-50/80 dark:bg-rose-900/15 border-b border-rose-100 dark:border-rose-900/40">
                                      <p className="text-xs font-semibold text-rose-900 dark:text-rose-100">
                                        Start failed ({failedList.length})
                                      </p>
                                    </div>
                                    {failedList.length === 0 ? (
                                      <div className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">None</div>
                                    ) : (
                                      failedList.map((a) => (
                                        <button
                                          type="button"
                                          key={String(a._id)}
                                          onClick={() => {
                                            setSearchQuery(a.username || '');
                                            setFilterAutomation('start_failed');
                                            setFilterStatus('all');
                                            setShowAutomationLifecycle(false);
                                          }}
                                          className="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/40 border-b border-gray-50 dark:border-gray-700/50"
                                        >
                                          <div className="flex items-start gap-2">
                                            <XCircleIcon className="h-4 w-4 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
                                            <div className="min-w-0 flex-1">
                                              <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                                                @{a.username}
                                              </p>
                                              {a.automationStartFailureClassification && (
                                                <p className="text-[10px] text-rose-700 dark:text-rose-300 mt-0.5">
                                                  {classifyShort(a.automationStartFailureClassification)}
                                                </p>
                                              )}
                                              <p
                                                className="text-[11px] text-gray-600 dark:text-gray-400 mt-0.5 break-words line-clamp-2"
                                                title={failReason(a)}
                                              >
                                                {failReason(a)}
                                              </p>
                                            </div>
                                            <span className="text-[10px] text-gray-400 shrink-0">filter</span>
                                          </div>
                                        </button>
                                      ))
                                    )}
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })()}

                    {/* Status Filter Buttons */}
                    {(authExpiredCount > 0 || loginFailedCount > 0 || captchaCount > 0 || loginRequiredCount > 0) && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2 flex-wrap">
                          {authExpiredCount > 0 && (
                            <button
                              onClick={() => {
                                setFilterStatus('auth_expired');
                                setFilterAutomation('all');
                              }}
                              className="px-3 py-1.5 text-xs font-medium rounded-xl transition-colors bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/30 border border-orange-200 dark:border-orange-800"
                            >
                              Auth expired ({authExpiredCount})
                            </button>
                          )}
                          {loginFailedCount > 0 && (
                            <button
                              onClick={() => {
                                setFilterStatus('login_failed');
                                setFilterAutomation('all');
                              }}
                              className="px-3 py-1.5 text-xs font-medium rounded-xl transition-colors bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800"
                            >
                              Login Failed ({loginFailedCount})
                            </button>
                          )}
                          {captchaCount > 0 && (
                            <button
                              onClick={() => {
                                setFilterStatus('captcha');
                                setFilterAutomation('all');
                              }}
                              className="px-3 py-1.5 text-xs font-medium rounded-xl transition-colors bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800"
                            >
                              Captcha ({captchaCount})
                            </button>
                          )}
                          {loginRequiredCount > 0 && (
                            <button
                              onClick={() => {
                                setFilterStatus('login_required');
                                setFilterAutomation('all');
                              }}
                              className="px-3 py-1.5 text-xs font-medium rounded-xl transition-colors bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-800"
                            >
                              Login Required ({loginRequiredCount})
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              
              {/* Restart automations progress (after server restart) */}
              {restartProgress.total > 0 && (restartProgress.status === 'queued' || restartProgress.status === 'processing' || restartProgress.status === 'completed' || restartProgress.status === 'cancelled') && (
                <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-amber-500">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <ArrowPathIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {restartProgress.status === 'completed'
                        ? `Restart complete: ${restartProgress.successCount || 0} started, ${restartProgress.failCount || 0} failed`
                        : restartProgress.status === 'cancelled'
                        ? `Restart cancelled: ${restartProgress.successCount || 0} started; automation disabled for remaining accounts`
                        : restartProgress.status === 'queued'
                        ? `Restarting automations: ${restartProgress.total} accounts queued...`
                        : `Restarting: ${restartProgress.currentAccount || 'Starting...'} (${restartProgress.current}/${restartProgress.total})`}
                    </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {restartProgress.current} / {restartProgress.total}
                      </span>
                      {(restartProgress.status === 'processing' || restartProgress.status === 'queued') && (
                        <button
                          onClick={handleRestartCancel}
                          className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 bg-red-50 dark:bg-red-900/20 rounded-xl transition-colors"
                          title="Cancel restart and disable automation for remaining accounts"
                        >
                          <StopIcon className="h-4 w-4" />
                          Cancel &amp; stop all
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-2">
                    <div
                      className={`h-2.5 rounded-full transition-all duration-300 ${
                        restartProgress.status === 'completed'
                          ? restartProgress.failCount > 0 ? 'bg-yellow-500' : 'bg-green-500'
                          : restartProgress.status === 'cancelled'
                          ? 'bg-orange-500'
                          : 'bg-amber-500'
                      }`}
                      style={{ width: `${Math.max(0, Math.min(100, (restartProgress.current / restartProgress.total) * 100))}%` }}
                    />
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="font-medium text-green-600 dark:text-green-400">{restartProgress.successCount || 0} started</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                      <span className="font-medium text-red-600 dark:text-red-400">{restartProgress.failCount || 0} failed</span>
                    </span>
                    {(restartProgress.status === 'queued' || restartProgress.status === 'processing') && (
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-amber-500" />
                        <span className="font-medium text-amber-600 dark:text-amber-400">{restartProgress.total - (restartProgress.successCount || 0) - (restartProgress.failCount || 0)} pending</span>
                      </span>
                    )}
                  </div>
                </div>
              )}

              {(addSigningKeyProgress.status === 'queued' || addSigningKeyProgress.status === 'processing' || addSigningKeyProgress.status === 'cancelling') && addSigningKeyProgress.total > 0 && (
                <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {addSigningKeyProgress.kind === 'reset-pin' ? (
                        <LockClosedIcon className="h-5 w-5 text-sky-600 dark:text-sky-400" />
                      ) : (
                        <KeyIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                      )}
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {addSigningKeyProgress.kind === 'reset-pin'
                          ? (addSigningKeyProgress.status === 'queued'
                            ? `Queued: resetting PIN + keys for ${addSigningKeyProgress.total} account(s)`
                            : addSigningKeyProgress.status === 'cancelling'
                              ? `Reset PIN: finishing current account(s)… (${addSigningKeyProgress.current}/${addSigningKeyProgress.total})`
                              : `Reset PIN: ${(addSigningKeyProgress.inFlight > 1) ? `${addSigningKeyProgress.inFlight} at a time · ` : ''}${addSigningKeyProgress.currentAccount || '…'} (${addSigningKeyProgress.current}/${addSigningKeyProgress.total})`)
                          : (addSigningKeyProgress.status === 'queued'
                            ? `Queued: recovering signing keys for ${addSigningKeyProgress.total} account(s)`
                            : addSigningKeyProgress.status === 'cancelling'
                              ? `Recover Sign key: finishing current account(s)… (${addSigningKeyProgress.current}/${addSigningKeyProgress.total})`
                              : `Recover Sign key: ${(addSigningKeyProgress.inFlight > 1) ? `${addSigningKeyProgress.inFlight} at a time · ` : ''}${addSigningKeyProgress.currentAccount || '…'} (${addSigningKeyProgress.current}/${addSigningKeyProgress.total})`)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {addSigningKeyProgress.current} / {addSigningKeyProgress.total}
                      </span>
                      {addSigningKeyProgress.status === 'cancelling' ? (
                        <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                          Finishing current account(s)…
                        </span>
                      ) : (
                      <button
                        type="button"
                        onClick={async () => {
                          const statusPath = addSigningKeyProgress.kind === 'reset-pin'
                            ? '/accounts/bulk-reset-xchat-pin/status'
                            : '/accounts/bulk-add-signing-key/status';
                          try {
                            await api.post(
                              addSigningKeyProgress.kind === 'reset-pin'
                                ? '/accounts/bulk-reset-xchat-pin/cancel'
                                : '/accounts/bulk-add-signing-key/cancel'
                            );
                            const { data } = await api.get(statusPath);
                            if (data?.job) applyAddSigningKeyProgress(data.job);
                            if (!data?.job || data.job.status === 'queued' || data.job.status === 'processing' || data.job.status === 'cancelling') {
                              toast('Finishing current account(s), then this bar will close.');
                            }
                          } catch (err) {
                            toast.error(err.response?.data?.error || 'Failed to cancel');
                          }
                        }}
                        className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 bg-red-50 dark:bg-red-900/20 rounded-xl transition-colors"
                      >
                        <StopIcon className="h-4 w-4" />
                        Cancel
                      </button>
                      )}
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-2">
                    <div
                      className={`h-2.5 rounded-full transition-all duration-300 ${
                        addSigningKeyProgress.kind === 'reset-pin' ? 'bg-sky-600' : 'bg-emerald-600'
                      }`}
                      style={{ width: `${Math.max(0, Math.min(100, (addSigningKeyProgress.current / addSigningKeyProgress.total) * 100))}%` }}
                    />
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                    <span>{addSigningKeyProgress.kind === 'reset-pin' ? 'Registered' : 'Recovered'} {addSigningKeyProgress.successCount || 0}</span>
                    <span>Skipped {addSigningKeyProgress.skippedCount || 0}</span>
                    <span>Failed {addSigningKeyProgress.failCount || 0}</span>
                    {addSigningKeyProgress.kind === 'reset-pin' && addSigningKeyProgress.inFlight > 0 && (
                      <span>In flight {addSigningKeyProgress.inFlight}</span>
                    )}
                    {addSigningKeyProgress.lastError && (
                      <span className="truncate max-w-md" title={addSigningKeyProgress.lastError}>
                        Last error: {addSigningKeyProgress.lastError}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Bulk Start Progress Bar */}
              {(bulkOperationLoading || bulkStartProgress.status === 'processing' || bulkStartProgress.status === 'queued') && bulkStartProgress.total > 0 && (
                <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <PlayIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {bulkStartProgress.status === 'completed' 
                        ? `Completed: ${bulkStartProgress.successCount || 0} successful, ${bulkStartProgress.failCount || 0} failed`
                          : bulkStartProgress.status === 'cancelled'
                          ? `Cancelled: ${bulkStartProgress.successCount || 0} completed before cancellation`
                          : bulkStartProgress.status === 'queued'
                          ? `Queued: ${bulkStartProgress.total} accounts queued, starting soon...`
                          : bulkStartProgress.status === 'processing'
                          ? `Processing: ${bulkStartProgress.currentAccount || 'Starting accounts...'} (${bulkStartProgress.current}/${bulkStartProgress.total})`
                        : `Starting automation: ${bulkStartProgress.currentAccount || 'Processing...'}`}
                    </span>
                    </div>
                    <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {bulkStartProgress.current} / {bulkStartProgress.total}
                    </span>
                      {(bulkStartProgress.status === 'processing' || bulkStartProgress.status === 'queued') && (
                        <button
                          onClick={handleBulkStartCancel}
                          className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 bg-red-50 dark:bg-red-900/20 rounded-xl transition-colors"
                          title="Cancel bulk start operation"
                        >
                          <StopIcon className="h-4 w-4" />
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-2">
                    <div
                      className={`h-2.5 rounded-full transition-all duration-300 ${
                        bulkStartProgress.status === 'completed' 
                          ? bulkStartProgress.failCount > 0 ? 'bg-yellow-500' : 'bg-green-500'
                          : bulkStartProgress.status === 'cancelled'
                          ? 'bg-orange-500'
                          : 'bg-primary-600'
                      }`}
                      style={{ width: `${Math.max(0, Math.min(100, (bulkStartProgress.current / bulkStartProgress.total) * 100))}%` }}
                    ></div>
                  </div>
                  {/* Success/Fail Counts */}
                  <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      <span className="font-medium text-green-600 dark:text-green-400">{bulkStartProgress.successCount || 0} succeeded</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      <span className="font-medium text-red-600 dark:text-red-400">{bulkStartProgress.failCount || 0} failed</span>
                    </span>
                    {bulkStartProgress.status === 'queued' && (
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        <span className="font-medium text-blue-600 dark:text-blue-400">{bulkStartProgress.total - (bulkStartProgress.successCount || 0) - (bulkStartProgress.failCount || 0)} queued</span>
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Bulk Update Auth Progress Bar */}
              {bulkOperationLoading && bulkUpdateAuthProgress.total > 0 && (
                <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {bulkUpdateAuthProgress.status === 'completed' 
                        ? `Completed: ${bulkUpdateAuthProgress.successCount || 0} successful, ${bulkUpdateAuthProgress.failCount || 0} failed, ${bulkUpdateAuthProgress.notFoundCount || 0} not found`
                        : `Updating auth: ${bulkUpdateAuthProgress.currentAccount || 'Processing...'}`}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {bulkUpdateAuthProgress.current} / {bulkUpdateAuthProgress.total}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-2">
                    <div
                      className={`h-2.5 rounded-full transition-all duration-300 ${
                        bulkUpdateAuthProgress.status === 'completed' 
                          ? bulkUpdateAuthProgress.failCount > 0 || bulkUpdateAuthProgress.notFoundCount > 0 ? 'bg-yellow-500' : 'bg-green-500'
                          : 'bg-primary-600'
                      }`}
                      style={{ width: `${(bulkUpdateAuthProgress.current / bulkUpdateAuthProgress.total) * 100}%` }}
                    ></div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      <span className="font-medium text-green-600 dark:text-green-400">{bulkUpdateAuthProgress.successCount || 0} succeeded</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      <span className="font-medium text-red-600 dark:text-red-400">{bulkUpdateAuthProgress.failCount || 0} failed</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                      <span className="font-medium text-orange-600 dark:text-orange-400">{bulkUpdateAuthProgress.notFoundCount || 0} not found</span>
                    </span>
                  </div>
                </div>
              )}

              {/* Bulk Update Proxy Progress Bar */}
              {bulkOperationLoading && bulkUpdateProxyProgress.total > 0 && (
                <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {bulkUpdateProxyProgress.status === 'completed' 
                        ? `Completed: ${bulkUpdateProxyProgress.successCount || 0} successful, ${bulkUpdateProxyProgress.failCount || 0} failed, ${bulkUpdateProxyProgress.notFoundCount || 0} not found`
                        : `Updating proxy: ${bulkUpdateProxyProgress.currentAccount || 'Processing...'}`}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {bulkUpdateProxyProgress.current} / {bulkUpdateProxyProgress.total}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-2">
                    <div
                      className={`h-2.5 rounded-full transition-all duration-300 ${
                        bulkUpdateProxyProgress.status === 'completed' 
                          ? bulkUpdateProxyProgress.failCount > 0 || bulkUpdateProxyProgress.notFoundCount > 0 ? 'bg-yellow-500' : 'bg-green-500'
                          : 'bg-primary-600'
                      }`}
                      style={{ width: `${(bulkUpdateProxyProgress.current / bulkUpdateProxyProgress.total) * 100}%` }}
                    ></div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      <span className="font-medium text-green-600 dark:text-green-400">{bulkUpdateProxyProgress.successCount || 0} succeeded</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      <span className="font-medium text-red-600 dark:text-red-400">{bulkUpdateProxyProgress.failCount || 0} failed</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                      <span className="font-medium text-orange-600 dark:text-orange-400">{bulkUpdateProxyProgress.notFoundCount || 0} not found</span>
                    </span>
                  </div>
                </div>
              )}

              {/* Bulk Renew Auth Progress Bar */}
              {bulkRenewAuthProgress.total > 0 && (
                <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {bulkRenewAuthProgress.status === 'completed'
                          ? `Completed: ${bulkRenewAuthProgress.successCount || 0} renewed, ${bulkRenewAuthProgress.failCount || 0} failed, ${bulkRenewAuthProgress.skippedCount || 0} skipped`
                          : bulkRenewAuthProgress.status === 'cancelled'
                          ? `Cancelled: ${bulkRenewAuthProgress.successCount || 0} renewed before cancellation`
                          : `Renewing auth: ${bulkRenewAuthProgress.currentAccount || 'Starting...'} (${bulkRenewAuthProgress.current}/${bulkRenewAuthProgress.total})`}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {bulkRenewAuthProgress.current} / {bulkRenewAuthProgress.total}
                      </span>
                      {bulkRenewAuthProgress.status === 'renewing' && (
                        <button
                          onClick={handleBulkRenewAuthCancel}
                          className="px-3 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 rounded-md hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-2">
                    <div
                      className={`h-2.5 rounded-full transition-all duration-300 ${
                        bulkRenewAuthProgress.status === 'completed'
                          ? bulkRenewAuthProgress.failCount > 0 ? 'bg-yellow-500' : 'bg-green-500'
                          : bulkRenewAuthProgress.status === 'cancelled'
                          ? 'bg-orange-500'
                          : 'bg-amber-500'
                      }`}
                      style={{ width: `${bulkRenewAuthProgress.total > 0 ? Math.max(0, Math.min(100, (bulkRenewAuthProgress.current / bulkRenewAuthProgress.total) * 100)) : 0}%` }}
                    ></div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      <span className="font-medium text-green-600 dark:text-green-400">{bulkRenewAuthProgress.successCount || 0} renewed</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      <span className="font-medium text-red-600 dark:text-red-400">{bulkRenewAuthProgress.failCount || 0} failed</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                      <span className="font-medium text-gray-500 dark:text-gray-400">{bulkRenewAuthProgress.skippedCount || 0} skipped</span>
                    </span>
                  </div>
                </div>
              )}

              {/* Bulk Login Test Progress Bar */}
              {bulkOperationLoading && bulkLoginTestProgress.total > 0 && (
                <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {bulkLoginTestProgress.status === 'completed' 
                        ? `Completed: ${bulkLoginTestProgress.successCount || 0} successful, ${bulkLoginTestProgress.failCount || 0} failed`
                        : bulkLoginTestProgress.status === 'cancelled'
                        ? `Cancelled: ${bulkLoginTestProgress.successCount || 0} completed before cancellation`
                        : `Testing login: ${bulkLoginTestProgress.currentAccount || 'Processing...'}`}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {bulkLoginTestProgress.current} / {bulkLoginTestProgress.total}
                      </span>
                      {(bulkLoginTestProgress.status === 'testing' || bulkLoginTestProgress.status === 'starting') && bulkLoginTestProgress.total > 0 && (
                        <button
                          onClick={handleBulkLoginTestCancel}
                          className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 bg-red-50 dark:bg-red-900/20 rounded-xl transition-colors"
                          title="Cancel login test operation"
                        >
                          <StopIcon className="h-4 w-4" />
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-2">
                    <div
                      className={`h-2.5 rounded-full transition-all duration-300 ${
                        bulkLoginTestProgress.status === 'completed' 
                          ? bulkLoginTestProgress.failCount > 0 ? 'bg-yellow-500' : 'bg-green-500'
                          : bulkLoginTestProgress.status === 'cancelled'
                          ? 'bg-orange-500'
                          : 'bg-primary-600'
                      }`}
                      style={{ width: `${(bulkLoginTestProgress.current / bulkLoginTestProgress.total) * 100}%` }}
                    ></div>
                  </div>
                  {/* Success/Fail Counts */}
                  <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      <span className="font-medium text-green-600 dark:text-green-400">{bulkLoginTestProgress.successCount || 0} succeeded</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      <span className="font-medium text-red-600 dark:text-red-400">{bulkLoginTestProgress.failCount || 0} failed</span>
                    </span>
                  </div>
                </div>
              )}

              {/* Bulk CAPTCHA Solve Progress Bar */}
              {bulkCaptchaSolveProgress.total > 0 && (
                <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {bulkCaptchaSolveProgress.status === 'completed'
                        ? `Completed: ${bulkCaptchaSolveProgress.successCount || 0} solved, ${bulkCaptchaSolveProgress.failCount || 0} failed`
                        : bulkCaptchaSolveProgress.status === 'cancelled'
                        ? `Cancelled: ${bulkCaptchaSolveProgress.successCount || 0} solved before cancellation`
                        : `Solving CAPTCHA: ${bulkCaptchaSolveProgress.currentAccount || 'Processing...'}`}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {bulkCaptchaSolveProgress.current} / {bulkCaptchaSolveProgress.total}
                      </span>
                      {(bulkCaptchaSolveProgress.status === 'solving' || bulkCaptchaSolveProgress.status === 'starting') && bulkCaptchaSolveProgress.total > 0 && (
                        <button
                          onClick={handleBulkCaptchaSolveCancel}
                          className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 bg-red-50 dark:bg-red-900/20 rounded-xl transition-colors"
                          title="Cancel CAPTCHA solve operation"
                        >
                          <StopIcon className="h-4 w-4" />
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-2">
                    <div
                      className={`h-2.5 rounded-full transition-all duration-300 ${
                        bulkCaptchaSolveProgress.status === 'completed'
                          ? bulkCaptchaSolveProgress.failCount > 0 ? 'bg-yellow-500' : 'bg-green-500'
                          : bulkCaptchaSolveProgress.status === 'cancelled'
                          ? 'bg-orange-500'
                          : 'bg-yellow-500'
                      }`}
                      style={{ width: `${(bulkCaptchaSolveProgress.current / bulkCaptchaSolveProgress.total) * 100}%` }}
                    ></div>
                  </div>
                  {bulkCaptchaSolveProgress.message && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{bulkCaptchaSolveProgress.message}</p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      <span className="font-medium text-green-600 dark:text-green-400">{bulkCaptchaSolveProgress.successCount || 0} solved</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      <span className="font-medium text-red-600 dark:text-red-400">{bulkCaptchaSolveProgress.failCount || 0} failed</span>
                    </span>
                  </div>
                </div>
              )}
              
              {selectedAccounts.length > 0 && (() => {
                // Get selected account objects
                const selectedAccountObjects = accounts.filter(acc => selectedAccounts.includes(acc._id));
                
                // Check if all selected accounts have active automation running
                const allHaveActiveAutomation = selectedAccountObjects.every(acc => 
                  acc.status === 'active' && acc.automation?.enabled === true
                );
                
                // Check if any selected accounts have active automation running
                const anyHaveActiveAutomation = selectedAccountObjects.some(acc => 
                  acc.status === 'active' && acc.automation?.enabled === true
                );
                
                // Stop button: disabled if any selected account doesn't have active automation
                const canStop = allHaveActiveAutomation;
                
                // Start button: disabled if any selected account already has active automation
                const canStart = !anyHaveActiveAutomation;
                
                // Login Test button: disabled if any selected account has active automation
                const canLoginTest = !anyHaveActiveAutomation;
                
                return (
                  <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={handleBulkStart}
                      disabled={bulkOperationLoading || !canStart}
                      className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                        isDarkMode
                          ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 shadow-lg shadow-green-500/30 hover:shadow-green-500/40'
                          : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 shadow-lg shadow-green-500/30 hover:shadow-green-500/40'
                      }`}
                      title={!canStart ? 'Cannot start: Some selected accounts already have active automation' : ''}
                    >
                      <PlayIcon className="h-4 w-4" />
                      {bulkOperationLoading ? 'Starting...' : `Start (${selectedAccounts.length})`}
                    </button>
                    <button
                      onClick={handleBulkStop}
                      disabled={bulkOperationLoading || !canStop}
                      className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                        isDarkMode
                          ? 'bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-400 hover:to-rose-400 shadow-lg shadow-red-500/30 hover:shadow-red-500/40'
                          : 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 shadow-lg shadow-red-500/30 hover:shadow-red-500/40'
                      }`}
                      title={!canStop ? 'Cannot stop: Some selected accounts do not have active automation running' : ''}
                    >
                      <StopIcon className="h-4 w-4" />
                      {bulkOperationLoading ? 'Stopping...' : `Stop (${selectedAccounts.length})`}
                    </button>
                    <button
                      onClick={handleBulkLoginTest}
                      disabled={bulkOperationLoading || !canLoginTest}
                      className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                          : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                      }`}
                      title={!canLoginTest ? 'Cannot test: Some selected accounts have active automation running' : ''}
                    >
                      {bulkOperationLoading ? 'Testing...' : `Login (${selectedAccounts.length})`}
                    </button>
                    {user?.role === 'admin' && (
                    <button
                      onClick={handleBulkCaptchaSolve}
                      disabled={bulkOperationLoading}
                      className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 ${
                        isDarkMode
                          ? 'bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 shadow-lg shadow-yellow-500/30 hover:shadow-yellow-500/40'
                          : 'bg-gradient-to-r from-yellow-600 to-amber-600 hover:from-yellow-500 hover:to-amber-500 shadow-lg shadow-yellow-500/30 hover:shadow-yellow-500/40'
                      }`}
                      title="Solve CAPTCHAs for selected accounts using CapMonster"
                    >
                      {bulkOperationLoading && bulkCaptchaSolveProgress.total > 0 ? 'Solving...' : `Solve Captcha (${selectedAccounts.length})`}
                    </button>
                    )}
                    <button
                      onClick={handleBulkClearStatus}
                      disabled={bulkOperationLoading}
                      className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 ${
                        isDarkMode
                          ? 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/40'
                          : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/40'
                      }`}
                      title="Clear false-positive captcha / auth flags — mark selected accounts active (logged in)"
                    >
                      Clear status → active ({selectedAccounts.length})
                    </button>
                    <button
                      onClick={handleBulkRenewAuth}
                      disabled={bulkOperationLoading || !canLoginTest}
                      className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 ${
                        isDarkMode
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/40'
                          : 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/40'
                      }`}
                      title={!canLoginTest ? 'Cannot renew: Some selected accounts have active automation running' : 'Log into X.com and extract fresh auth tokens'}
                    >
                      {bulkOperationLoading && bulkRenewAuthProgress.total > 0 ? 'Renewing...' : `Renew Auth (${selectedAccounts.length})`}
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      disabled={bulkOperationLoading}
                      className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                        isDarkMode
                          ? 'bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-400 hover:to-rose-400 shadow-lg shadow-red-500/30 hover:shadow-red-500/40'
                          : 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 shadow-lg shadow-red-500/30 hover:shadow-red-500/40'
                      }`}
                    >
                      {bulkOperationLoading ? 'Deleting...' : `Delete (${selectedAccounts.length})`}
                    </button>
                    {user?.role === 'admin' && (
                    <button
                      onClick={openAddToVaModal}
                      disabled={bulkOperationLoading || addToVaLoading}
                      className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                        isDarkMode
                          ? 'bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-400 hover:to-violet-400 shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/40'
                          : 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/40'
                      }`}
                      title="Add selected accounts to a virtual assistant panel"
                    >
                      <UserGroupIcon className="h-4 w-4" />
                      {addToVaLoading ? 'Adding…' : `Add to VA (${selectedAccounts.length})`}
                    </button>
                    )}
                    
                    {/* Tags Dropdown - show when user has any tag feature or is admin */}
                    {(user?.role === 'admin' || user?.features?.canUseScraper || user?.features?.canUseActiveCheckers || user?.features?.canUseFollowBackCheckers) && (
                    <div className="relative group/tags">
                      <button
                        disabled={bulkOperationLoading}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 border border-indigo-300 dark:border-indigo-700 hover:bg-indigo-100 dark:hover:bg-indigo-900/40"
                      >
                        <SparklesIcon className="h-4 w-4" />
                        Tags
                      </button>
                      <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-700 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 opacity-0 invisible group-hover/tags:opacity-100 group-hover/tags:visible transition-all z-20 min-w-[240px]">
                        {/* Add Tags Section */}
                        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-600">
                          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Add Tags</div>
                          {(user?.role === 'admin' || user?.features?.canUseScraper) && (
                            <>
                              <button
                                onClick={async () => {
                                  try {
                                    await api.post('/accounts/bulk-tags', {
                                      accountIds: selectedAccounts,
                                      action: 'add',
                                      tags: ['scraper']
                                    });
                                    toast.success(`Added 'scraper' tag to ${selectedAccounts.length} account(s)`);
                                    setAccounts(prevAccounts => 
                                      prevAccounts.map(acc => 
                                        selectedAccounts.includes(acc._id) 
                                          ? { ...acc, tags: [...new Set([...(acc.tags || []), 'scraper'])] }
                                          : acc
                                      )
                                    );
                                    setSelectedAccounts([]);
                                  } catch (error) {
                                    toast.error('Failed to add tags');
                                    console.error('Error adding tags:', error);
                                  }
                                }}
                                className="w-full px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 rounded"
                              >
                                <span className="font-medium">Add 'scraper' tag</span>
                              </button>
                              <div className="text-xs text-gray-500 dark:text-gray-400 ml-5 mb-2">
                                Tag account with 'scraper' tag for use in scraping followers
                              </div>
                            </>
                          )}
                          {(user?.role === 'admin' || user?.features?.canUseActiveCheckers) && (
                            <>
                              <button
                                onClick={async () => {
                                  try {
                                    await api.post('/accounts/bulk-tags', {
                                      accountIds: selectedAccounts,
                                      action: 'add',
                                      tags: ['active_checker']
                                    });
                                    toast.success(`Added 'active_checker' tag to ${selectedAccounts.length} account(s)`);
                                    setAccounts(prevAccounts => 
                                      prevAccounts.map(acc => 
                                        selectedAccounts.includes(acc._id) 
                                          ? { ...acc, tags: [...new Set([...(acc.tags || []), 'active_checker'])] }
                                          : acc
                                      )
                                    );
                                    setSelectedAccounts([]);
                                  } catch (error) {
                                    toast.error('Failed to add tags');
                                    console.error('Error adding tags:', error);
                                  }
                                }}
                                className="w-full px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 rounded"
                              >
                                <span className="font-medium">Add 'active_checker' tag</span>
                              </button>
                              <div className="text-xs text-gray-500 dark:text-gray-400 ml-5">
                                Tag account with 'active_checker' tag for checking user activity in parallel
                              </div>
                            </>
                          )}
                          {(user?.role === 'admin' || user?.features?.canUseScraper) && (
                            <>
                              <button
                                onClick={async () => {
                                  try {
                                    await api.post('/accounts/bulk-tags', {
                                      accountIds: selectedAccounts,
                                      action: 'add',
                                      tags: ['source']
                                    });
                                    toast.success(`Added 'source' tag to ${selectedAccounts.length} account(s)`);
                                    setAccounts(prevAccounts => 
                                      prevAccounts.map(acc => 
                                        selectedAccounts.includes(acc._id) 
                                          ? { ...acc, tags: [...new Set([...(acc.tags || []), 'source'])] }
                                          : acc
                                      )
                                    );
                                    setSelectedAccounts([]);
                                  } catch (error) {
                                    toast.error('Failed to add tags');
                                    console.error('Error adding tags:', error);
                                  }
                                }}
                                className="w-full px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 rounded"
                              >
                                <span className="font-medium">Add 'source' tag</span>
                              </button>
                              <div className="text-xs text-gray-500 dark:text-gray-400 ml-5">
                                Tag account for sourcing target usernames from the For You feed
                              </div>
                            </>
                          )}
                          {(user?.role === 'admin' || user?.features?.canUseFollowBackCheckers) && (
                            <>
                              <button
                                onClick={async () => {
                                  try {
                                    await api.post('/accounts/bulk-tags', {
                                      accountIds: selectedAccounts,
                                      action: 'add',
                                      tags: ['follow_back_checker']
                                    });
                                    toast.success(`Added 'follow_back_checker' tag to ${selectedAccounts.length} account(s)`);
                                    setAccounts(prevAccounts => 
                                      prevAccounts.map(acc => 
                                        selectedAccounts.includes(acc._id) 
                                          ? { ...acc, tags: [...new Set([...(acc.tags || []), 'follow_back_checker'])] }
                                          : acc
                                      )
                                    );
                                    setSelectedAccounts([]);
                                  } catch (error) {
                                    toast.error('Failed to add tags');
                                    console.error('Error adding tags:', error);
                                  }
                                }}
                                className="w-full px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 rounded"
                              >
                                <span className="font-medium">Add 'follow_back_checker' tag</span>
                              </button>
                              <div className="text-xs text-gray-500 dark:text-gray-400 ml-5 mb-2">
                                Tag for follow-back automation scanning pool
                              </div>
                            </>
                          )}
                          {user?.role === 'admin' && (
                            <>
                              <button
                                onClick={async () => {
                                  try {
                                    await api.post('/accounts/bulk-tags', {
                                      accountIds: selectedAccounts,
                                      action: 'add',
                                      tags: ['view_checker']
                                    });
                                    toast.success(`Added 'view_checker' tag to ${selectedAccounts.length} account(s)`);
                                    setAccounts(prevAccounts => 
                                      prevAccounts.map(acc => 
                                        selectedAccounts.includes(acc._id) 
                                          ? { ...acc, tags: [...new Set([...(acc.tags || []), 'view_checker'])] }
                                          : acc
                                      )
                                    );
                                    setSelectedAccounts([]);
                                  } catch (error) {
                                    toast.error('Failed to add tags');
                                    console.error('Error adding tags:', error);
                                  }
                                }}
                                className="w-full px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 rounded"
                              >
                                <span className="font-medium">Add 'view_checker' tag</span>
                              </button>
                              <div className="text-xs text-gray-500 dark:text-gray-400 ml-5 mb-2">
                                Tag for auto-comment view checking pool
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Remove Tags Section */}
                        <div className="px-3 py-2">
                          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Remove Tags</div>
                          {(user?.role === 'admin' || user?.features?.canUseScraper) && (
                            <button
                              onClick={async () => {
                                try {
                                  await api.post('/accounts/bulk-tags', {
                                    accountIds: selectedAccounts,
                                    action: 'remove',
                                    tags: ['scraper']
                                  });
                                  toast.success(`Removed 'scraper' tag from ${selectedAccounts.length} account(s)`);
                                  setAccounts(prevAccounts => 
                                    prevAccounts.map(acc => 
                                      selectedAccounts.includes(acc._id) 
                                        ? { ...acc, tags: (acc.tags || []).filter(tag => tag !== 'scraper') }
                                        : acc
                                    )
                                  );
                                  setSelectedAccounts([]);
                                } catch (error) {
                                  toast.error('Failed to remove tags');
                                  console.error('Error removing tags:', error);
                                }
                              }}
                              className="w-full px-3 py-2 text-sm text-left text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 rounded"
                            >
                              <XCircleIcon className="h-4 w-4" />
                              Remove 'scraper' tag
                            </button>
                          )}
                          {(user?.role === 'admin' || user?.features?.canUseActiveCheckers) && (
                            <button
                              onClick={async () => {
                                try {
                                  await api.post('/accounts/bulk-tags', {
                                    accountIds: selectedAccounts,
                                    action: 'remove',
                                    tags: ['active_checker']
                                  });
                                  toast.success(`Removed 'active_checker' tag from ${selectedAccounts.length} account(s)`);
                                  setAccounts(prevAccounts => 
                                    prevAccounts.map(acc => 
                                      selectedAccounts.includes(acc._id) 
                                        ? { ...acc, tags: (acc.tags || []).filter(tag => tag !== 'active_checker') }
                                        : acc
                                    )
                                  );
                                  setSelectedAccounts([]);
                                } catch (error) {
                                  toast.error('Failed to remove tags');
                                  console.error('Error removing tags:', error);
                                }
                              }}
                              className="w-full px-3 py-2 text-sm text-left text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 rounded"
                            >
                              <XCircleIcon className="h-4 w-4" />
                              Remove 'active_checker' tag
                            </button>
                          )}
                          {(user?.role === 'admin' || user?.features?.canUseScraper) && (
                            <button
                              onClick={async () => {
                                try {
                                  await api.post('/accounts/bulk-tags', {
                                    accountIds: selectedAccounts,
                                    action: 'remove',
                                    tags: ['source']
                                  });
                                  toast.success(`Removed 'source' tag from ${selectedAccounts.length} account(s)`);
                                  setAccounts(prevAccounts => 
                                    prevAccounts.map(acc => 
                                      selectedAccounts.includes(acc._id) 
                                        ? { ...acc, tags: (acc.tags || []).filter(tag => tag !== 'source') }
                                        : acc
                                    )
                                  );
                                  setSelectedAccounts([]);
                                } catch (error) {
                                  toast.error('Failed to remove tags');
                                  console.error('Error removing tags:', error);
                                }
                              }}
                              className="w-full px-3 py-2 text-sm text-left text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 rounded"
                            >
                              <XCircleIcon className="h-4 w-4" />
                              Remove 'source' tag
                            </button>
                          )}
                          {(user?.role === 'admin' || user?.features?.canUseFollowBackCheckers) && (
                            <button
                              onClick={async () => {
                                try {
                                  await api.post('/accounts/bulk-tags', {
                                    accountIds: selectedAccounts,
                                    action: 'remove',
                                    tags: ['follow_back_checker']
                                  });
                                  toast.success(`Removed 'follow_back_checker' tag from ${selectedAccounts.length} account(s)`);
                                  setAccounts(prevAccounts => 
                                    prevAccounts.map(acc => 
                                      selectedAccounts.includes(acc._id) 
                                        ? { ...acc, tags: (acc.tags || []).filter(tag => tag !== 'follow_back_checker') }
                                        : acc
                                    )
                                  );
                                  setSelectedAccounts([]);
                                } catch (error) {
                                  toast.error('Failed to remove tags');
                                  console.error('Error removing tags:', error);
                                }
                              }}
                              className="w-full px-3 py-2 text-sm text-left text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 rounded"
                            >
                              <XCircleIcon className="h-4 w-4" />
                              Remove 'follow_back_checker' tag
                            </button>
                          )}
                          {user?.role === 'admin' && (
                            <button
                              onClick={async () => {
                                try {
                                  await api.post('/accounts/bulk-tags', {
                                    accountIds: selectedAccounts,
                                    action: 'remove',
                                    tags: ['view_checker']
                                  });
                                  toast.success(`Removed 'view_checker' tag from ${selectedAccounts.length} account(s)`);
                                  setAccounts(prevAccounts => 
                                    prevAccounts.map(acc => 
                                      selectedAccounts.includes(acc._id) 
                                        ? { ...acc, tags: (acc.tags || []).filter(tag => tag !== 'view_checker') }
                                        : acc
                                    )
                                  );
                                  setSelectedAccounts([]);
                                } catch (error) {
                                  toast.error('Failed to remove tags');
                                  console.error('Error removing tags:', error);
                                }
                              }}
                              className="w-full px-3 py-2 text-sm text-left text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 rounded"
                            >
                              <XCircleIcon className="h-4 w-4" />
                              Remove 'view_checker' tag
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    )}
                    
                    {/* Assign to Group Dropdown */}
                    <div className="relative group/assigngroup">
                      <button
                        disabled={bulkOperationLoading}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border border-purple-300 dark:border-purple-700 hover:bg-purple-100 dark:hover:bg-purple-900/40"
                      >
                        <FolderIcon className="h-4 w-4" />
                        Assign to Group
                      </button>
                      <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-700 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 opacity-0 invisible group-hover/assigngroup:opacity-100 group-hover/assigngroup:visible transition-all z-20 min-w-[180px] max-h-60 overflow-y-auto">
                        {/* Remove from group option */}
                        <button
                          onClick={async () => {
                            try {
                              await api.post('/accounts/bulk-group', {
                                accountIds: selectedAccounts,
                                group: null
                              });
                              toast.success(`Removed ${selectedAccounts.length} account(s) from group`);
                              // Update local state instead of refreshing
                              setAccounts(prevAccounts => 
                                prevAccounts.map(acc => 
                                  selectedAccounts.includes(acc._id) 
                                    ? { ...acc, group: null }
                                    : acc
                                )
                              );
                              fetchGroups(); // Still fetch groups to update the list
                              setSelectedAccounts([]);
                            } catch (error) {
                              toast.error('Failed to update group');
                              console.error('Error updating group:', error);
                            }
                          }}
                          className="w-full px-3 py-2 text-sm text-left text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 border-b border-gray-200 dark:border-gray-600"
                        >
                          <XCircleIcon className="h-4 w-4" />
                          Remove from group
                        </button>
                        
                        {/* Existing groups */}
                        {leafGroups.map((group) => (
                          <button
                            key={group}
                            onClick={async () => {
                              try {
                                await api.post('/accounts/bulk-group', {
                                  accountIds: selectedAccounts,
                                  group: group
                                });
                                toast.success(`Assigned ${selectedAccounts.length} account(s) to "${group}"`);
                                // Update local state instead of refreshing
                                setAccounts(prevAccounts => 
                                  prevAccounts.map(acc => 
                                    selectedAccounts.includes(acc._id) 
                                      ? { ...acc, group: group }
                                      : acc
                                  )
                                );
                                setSelectedAccounts([]);
                              } catch (error) {
                                toast.error('Failed to update group');
                                console.error('Error updating group:', error);
                              }
                            }}
                            className="w-full px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2"
                          >
                            <FolderIcon className="h-4 w-4" />
                            {group}
                          </button>
                        ))}
                        
                        {/* Create new group option */}
                        <button
                          onClick={async () => {
                            const groupName = window.prompt('Enter new group name:');
                            if (groupName && groupName.trim()) {
                              try {
                                await api.post('/accounts/bulk-group', {
                                  accountIds: selectedAccounts,
                                  group: groupName.trim()
                                });
                                toast.success(`Created group "${groupName.trim()}" and assigned ${selectedAccounts.length} account(s)`);
                                // Update local state instead of refreshing
                                setAccounts(prevAccounts => 
                                  prevAccounts.map(acc => 
                                    selectedAccounts.includes(acc._id) 
                                      ? { ...acc, group: groupName.trim() }
                                      : acc
                                  )
                                );
                                fetchGroups(); // Still fetch groups to update the list
                                setSelectedAccounts([]);
                              } catch (error) {
                                toast.error('Failed to create group');
                                console.error('Error creating group:', error);
                              }
                            }
                          }}
                          className="w-full px-3 py-2 text-sm text-left text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 flex items-center gap-2 border-t border-gray-200 dark:border-gray-600"
                        >
                          <FolderPlusIcon className="h-4 w-4" />
                          Create new group...
                        </button>
                      </div>
                    </div>
                    
                    {/* Update Model Dropdown */}
                    <div className="relative group/model">
                      <button
                        disabled={bulkOperationLoading}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                      >
                        <UserIcon className="h-4 w-4" />
                        Update Model
                      </button>
                      <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-700 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 opacity-0 invisible group-hover/model:opacity-100 group-hover/model:visible transition-all z-20 min-w-[300px] max-h-80 overflow-y-auto">
                        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-600">
                          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Select Model</div>
                          {/* Remove Model option */}
                          <button
                            onClick={async () => {
                              try {
                                setBulkOperationLoading(true);
                                const response = await api.post('/accounts/bulk-update-model', {
                                  accountIds: selectedAccounts,
                                  modelId: null
                                });
                                toast.success(response.data.message || `Removed model assignment from ${selectedAccounts.length} account(s)`);
                                // Update local state instead of refreshing
                                setAccounts(prevAccounts => 
                                  prevAccounts.map(acc => 
                                    selectedAccounts.includes(acc._id) 
                                      ? { ...acc, modelId: null }
                                      : acc
                                  )
                                );
                                setSelectedAccounts([]);
                              } catch (error) {
                                toast.error(error.response?.data?.error || 'Failed to update model');
                                console.error('Error updating model:', error);
                              } finally {
                                setBulkOperationLoading(false);
                              }
                            }}
                            className="w-full px-3 py-2 text-sm text-left text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 rounded border-b border-gray-200 dark:border-gray-600 mb-1"
                          >
                            <XCircleIcon className="h-4 w-4" />
                            Remove Model
                          </button>
                          {/* Models list */}
                          {models.map((model) => {
                            const presetName = getPresetName(model.preset);
                            return (
                              <button
                                key={model._id}
                                onClick={async () => {
                                  try {
                                    setBulkOperationLoading(true);
                                    const response = await api.post('/accounts/bulk-update-model', {
                                      accountIds: selectedAccounts,
                                      modelId: model._id
                                    });
                                    toast.success(response.data.message || `Updated model assignment for ${selectedAccounts.length} account(s)`);
                                    // Update local state instead of refreshing
                                    // Ensure modelId is stored as string to match how it's used in getModelName()
                                    const modelIdString = typeof model._id === 'object' ? model._id.toString() : String(model._id);
                                    setAccounts(prevAccounts => 
                                      prevAccounts.map(acc => 
                                        selectedAccounts.includes(acc._id) 
                                          ? { ...acc, modelId: modelIdString }
                                          : acc
                                      )
                                    );
                                    setSelectedAccounts([]);
                                  } catch (error) {
                                    toast.error(error.response?.data?.error || 'Failed to update model');
                                    console.error('Error updating model:', error);
                                  } finally {
                                    setBulkOperationLoading(false);
                                  }
                                }}
                                className="w-full px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 rounded"
                              >
                                <UserIcon className="h-4 w-4" />
                                <span className="truncate">
                                  {model.displayName || model.name}{model.displayName ? ` (${model.name})` : ''} - {model.age}y, {model.gender}{presetName ? ` • ${presetName}` : ''}{user?.role === 'admin' && model.userId?.username ? ` (${model.userId.username})` : ''}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Update Proxy */}
                    <button
                      onClick={() => setShowBulkUpdateProxiesModal(true)}
                      disabled={bulkOperationLoading}
                      className={`px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-white shadow-lg ${
                        isDarkMode
                          ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 shadow-blue-500/30 hover:shadow-blue-500/40'
                          : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 shadow-blue-500/30 hover:shadow-blue-500/40'
                      }`}
                      title={`Update proxy for ${selectedAccounts.length} selected account(s)`}
                    >
                      Update Proxy
                    </button>
                    {/* Remove proxy from selected accounts */}
                    <button
                      onClick={handleBulkRemoveProxy}
                      disabled={bulkOperationLoading}
                      className="px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 hover:bg-amber-200 dark:hover:bg-amber-900/50"
                      title={`Remove proxy from ${selectedAccounts.length} selected account(s)`}
                    >
                      Remove Proxy
                    </button>
                    <button
                      onClick={handleBulkAddSigningKey}
                      disabled={bulkOperationLoading}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-emerald-800 dark:text-emerald-200 bg-emerald-100 dark:bg-emerald-900/40 border border-emerald-300 dark:border-emerald-600 hover:bg-emerald-200 dark:hover:bg-emerald-900/60"
                      title={`Recover the official Chat ECDSA signer from Juicebox (PIN 1111) for ${selectedAccounts.length} selected account(s). Does not register a new key.`}
                    >
                      <KeyIcon className="h-4 w-4 shrink-0" />
                      Recover Sign key ({selectedAccounts.length})
                    </button>
                    <button
                      onClick={handleBulkResetXchatPin}
                      disabled={bulkOperationLoading}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sky-800 dark:text-sky-200 bg-sky-100 dark:bg-sky-900/40 border border-sky-300 dark:border-sky-600 hover:bg-sky-200 dark:hover:bg-sky-900/60"
                      title={`Reset Chat PIN to 1111 and register ECDH + signer keys for ${selectedAccounts.length} selected account(s). Skips accounts that already have a signing key.`}
                    >
                      <LockClosedIcon className="h-4 w-4 shrink-0" />
                      Reset PIN + keys ({selectedAccounts.length})
                    </button>
                    <button
                      onClick={handleBulkClearXchatKey}
                      disabled={bulkOperationLoading}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-violet-800 dark:text-violet-200 bg-violet-100 dark:bg-violet-900/40 border border-violet-300 dark:border-violet-600 hover:bg-violet-200 dark:hover:bg-violet-900/60"
                      title={`Clear stored XChat E2EE private key for ${selectedAccounts.length} selected account(s). Recovery runs on next XChat start.`}
                    >
                      <LockOpenIcon className="h-4 w-4 shrink-0" />
                      Clear XChat key ({selectedAccounts.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowBulkResetTlsModal(true)}
                      disabled={bulkOperationLoading}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-teal-800 dark:text-teal-200 bg-teal-100 dark:bg-teal-900/40 border border-teal-300 dark:border-teal-600 hover:bg-teal-200 dark:hover:bg-teal-900/60"
                      title={`Clear JA3/JA4/JA4R TLS session data and set User-Agent for ${selectedAccounts.length} selected account(s)`}
                    >
                      <CpuChipIcon className="h-4 w-4 shrink-0" />
                      Reset TLS / UA ({selectedAccounts.length})
                    </button>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Bulk Operation Results */}
          {/* Only show bulk operation results when operation is completed (not when just queued) */}
          {bulkOperationResults && bulkStartProgress.status === 'completed' && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300">Bulk Operation Results</h3>
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    {bulkOperationResults.successCount || 0} successful, {bulkOperationResults.failCount || 0} failed
                  </p>
                </div>
                <button
                  onClick={clearBulkResults}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              </div>
                </div>
              )}

          {/* Export Filtered Accounts Button and Update Auth Button */}
          {filteredAccounts.length > 0 && (
            <div className="flex items-center justify-end gap-2 mb-4">
              <button
                onClick={handleExportAccounts}
                className="btn btn-secondary btn-sm"
                title={selectedAccounts.length > 0 
                  ? `Export ${selectedAccounts.length} selected account(s)` 
                  : `Export ${filteredAccounts.length} filtered account(s)`}
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-1 inline" />
                {selectedAccounts.length > 0 
                  ? `Export Selected (${selectedAccounts.length})` 
                  : `Export Filtered (${usePagination ? (accountSummary.totalAccounts || totalAccounts) : filteredAccounts.length})`}
              </button>
              <button
                onClick={() => setShowUpdateAuthModal(true)}
                className="btn btn-secondary btn-sm"
                title="Bulk update auth tokens for accounts"
              >
                Update Auth
              </button>
              <button
                onClick={() => setShowUpdateProxyModal(true)}
                className="btn btn-secondary btn-sm"
                title="Bulk update proxies for accounts (username:proxy per line)"
              >
                Update Proxy
              </button>
            </div>
          )}

          {/* Groups sidebar + main content */}
          <div className="flex gap-6 items-start">
            {/* Groups sidebar */}
            <div className="w-52 flex-shrink-0">
              <div className="card" style={{ overflow: 'visible' }}>
                <div className="card-body p-2">
                  <div className="space-y-1">
              <button
                onClick={() => {
                  setSelectedGroup('all');
                  setFilterStatus('all');
                  setFilterModelId('all');
                  setFilterVpsAgentId('all');
                }}
                className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-between ${
                  selectedGroup === 'all' && (!filterModelId || filterModelId === 'all')
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <span>All</span>
                <span className="text-xs text-gray-400 dark:text-gray-500">{accountSummary.globalTotalAccounts || accountSummary.totalAccounts}</span>
              </button>

              <ModelParentSidebar
                models={modelGroups}
                collapsed={collapsedModels}
                onToggle={(modelId) => setCollapsedModels((prev) => ({ ...prev, [modelId]: !prev[modelId] }))}
                selectedModelId={filterModelId}
                selectedGroup={selectedGroup}
                filterStatus={filterStatus}
                filterVpsAgentId={filterVpsAgentId}
                onSelectModel={(model) => {
                  setFilterModelId(model.modelId);
                  setFilterVpsAgentId('all');
                  setSelectedGroup('all');
                  setFilterStatus('all');
                }}
                onSelectModelStatus={(model, status) => {
                  setFilterModelId(model.modelId);
                  setFilterVpsAgentId('all');
                  setSelectedGroup('all');
                  setFilterStatus(status);
                }}
                onSelectGroup={(model, name) => {
                  setFilterModelId(model.modelId);
                  setFilterVpsAgentId('all');
                  setSelectedGroup(name);
                  setFilterStatus('all');
                }}
                onSelectGroupStatus={(model, name, status) => {
                  setFilterModelId(model.modelId);
                  setFilterVpsAgentId('all');
                  setSelectedGroup(name);
                  setFilterStatus(status);
                }}
                onSelectGroupVps={(model, name, status, vpsId) => {
                  setFilterModelId(model.modelId);
                  setFilterVpsAgentId(vpsId);
                  setSelectedGroup(name);
                  setFilterStatus(status);
                }}
                onStartModel={handleStartModel}
              />

              {groups.filter((g) => !groupsHiddenByModelParents(modelGroups).has(g)).map((group) => {
                const leafGroups = resolveLeafGroupsForGroupName(group);
                const groupCount = sumLeafMemberCounts(leafGroups);
                const meta = groupMetaByName[group];
                const hasPreset = !!meta?.automation;
                const presetLabel = meta?.automation?.label
                  || (meta?.automation?.modules || [])
                    .filter((m) => m.enabled !== false)
                    .map((m) => m.taskType)
                    .join(', ');
                const attachedAgent = meta?.attachedAgent;
                return (
                  <div key={group}>
                  <div className="relative group/tab">
                    <button
                      onClick={() => {
                        setSelectedGroup(group);
                        setFilterStatus('all');
                        setFilterModelId('all');
                        setFilterVpsAgentId('all');
                      }}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('application/x-group-name', group);
                        e.dataTransfer.effectAllowed = 'move';
                        setDraggingGroupName(group);
                        setDragOverGroupName(null);
                      }}
                      onDragEnd={() => {
                        setDraggingGroupName(null);
                        setDragOverGroupName(null);
                      }}
                      onDragOver={(e) => {
                        if (!draggingGroupName) return;
                        if (draggingGroupName === group) return;
                        e.preventDefault(); // Enable drop
                        setDragOverGroupName(group);
                      }}
                      onDrop={async (e) => {
                        e.preventDefault();
                        const sourceGroupName = e.dataTransfer.getData('application/x-group-name');
                        setDragOverGroupName(null);
                        setDraggingGroupName(null);

                        if (!sourceGroupName || sourceGroupName === group) return;

                        // If dropping onto an existing main group, just add as a subgroup.
                        if (isMainGroupName(group)) {
                          try {
                            await api.post('/accounts/groups/add-subgroup', {
                              mainGroupName: group,
                              subGroupName: sourceGroupName
                            });
                            toast.success(`Added "${sourceGroupName}" to "${group}"`);
                            await fetchGroups();
                          } catch (error) {
                            toast.error('Failed to add subgroup');
                            console.error('Error adding subgroup:', error);
                          }
                          return;
                        }

                        // Otherwise create a new main group using the two groups as subgroups.
                        const suggestedName = `${sourceGroupName} + ${group}`.trim();
                        setMainGroupModalDraft({
                          sourceGroup: sourceGroupName,
                          targetGroup: group,
                          suggestedName,
                          name: suggestedName
                        });
                        setShowMainGroupModal(true);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${
                        selectedGroup === group
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      } ${
                        dragOverGroupName === group
                          ? 'ring-2 ring-primary-300 dark:ring-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : ''
                      } ${draggingGroupName === group ? 'opacity-70' : ''}`}
                    >
                      {hasPreset && (
                        <span
                          title={`Auto-runs preset on upload${presetLabel ? `: ${presetLabel}` : ''}${meta?.automation?.runOnUpload === false ? ' (paused)' : ''}`}
                          className={`inline-block h-2 w-2 rounded-full ${
                            meta?.automation?.runOnUpload === false
                              ? 'bg-gray-400'
                              : 'bg-yellow-400 ring-1 ring-yellow-300'
                          }`}
                          aria-label="Group has an automation preset"
                        />
                      )}
                      {attachedAgent && (
                        <span
                          title={`Auto-assigns accounts to AI VA Agent: ${attachedAgent.name || attachedAgent.id}`}
                          className="inline-block h-2 w-2 rounded-full bg-blue-500 ring-1 ring-blue-300"
                          aria-label={`Auto-assigns to ${attachedAgent.name}`}
                        />
                      )}
                      <span className="truncate flex-1">{group}</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">({groupCount})</span>
                    </button>
                    {/* ⋮ kebab button — click to open dropdown */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenGroupMenu(prev => prev === group ? null : group);
                      }}
                      className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover/tab:opacity-100 transition-opacity hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 z-10"
                      title="Group options"
                    >
                      <EllipsisVerticalIcon className="h-4 w-4" />
                    </button>
                    {/* Dropdown — opens downward, z-50 escapes card */}
                    {openGroupMenu === group && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setOpenGroupMenu(null)} />
                        <div className="absolute left-0 top-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 min-w-[200px] py-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedGroup(group);
                              setFilterProfile('missing_dob');
                              setOpenGroupMenu(null);
                            }}
                            className="w-full px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                          >
                            <CalendarIcon className="h-4 w-4 text-red-500" />
                            Filter missing DoB
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedGroup(group);
                              setFilterProfile('missing_phone');
                              setOpenGroupMenu(null);
                            }}
                            className="w-full px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                          >
                            <DevicePhoneMobileIcon className="h-4 w-4 text-red-500" />
                            Filter missing Phone
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedGroup(group);
                              setFilterProfile('email_verified');
                              setOpenGroupMenu(null);
                            }}
                            className="w-full px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                          >
                            <EnvelopeIcon className="h-4 w-4 text-green-500" />
                            Filter email verified
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingGroup({ name: group, isNew: false, isMain: isMainGroupName(group) });
                              setNewGroupName(group);
                              setShowGroupModal(true);
                              setOpenGroupMenu(null);
                            }}
                            className="w-full px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                          >
                            <PencilIcon className="h-4 w-4" />
                            Rename
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const current = groupMetaByName[group] || {};
                              const a = current.automation || {};
                              setPresetModalGroup({
                                name: group,
                                automation: current.automation || null,
                                attachedAgent: current.attachedAgent || null
                              });
                              setPresetModalDraft({
                                modules: (a.modules || []).map((m) => ({
                                  taskType: m.taskType,
                                  enabled: m.enabled !== false,
                                  order: m.order ?? 0,
                                  settings: m.settings || {}
                                })),
                                bioSource: a.bioSource || 'manual',
                                bioModelId: a.bioModelId ? String(a.bioModelId) : '',
                                concurrency: a.concurrency || 15,
                                delayBetweenStepsMin: a.delayBetweenStepsMin ?? 3,
                                delayBetweenStepsMax: a.delayBetweenStepsMax ?? 8,
                                delayBetweenAccountsMin: a.delayBetweenAccountsMin ?? 5,
                                delayBetweenAccountsMax: a.delayBetweenAccountsMax ?? 15,
                                moveToGroupOnSuccess: !!a.moveToGroupOnSuccess,
                                moveToGroupName: a.moveToGroupName || '',
                                runOnUpload: a.runOnUpload !== false,
                                label: a.label || ''
                              });
                              setPresetModalOpen(true);
                              setOpenGroupMenu(null);
                            }}
                            className="w-full px-3 py-2 text-sm text-left text-yellow-700 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 flex items-center gap-2"
                          >
                            <SparklesIcon className="h-4 w-4" />
                            {hasPreset ? 'Edit preset' : 'Set preset'}
                          </button>
                          {user?.role === 'admin' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openAgentAssignModal(group);
                                setOpenGroupMenu(null);
                              }}
                              className="w-full px-3 py-2 text-sm text-left text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-2"
                            >
                              <CpuChipIcon className="h-4 w-4" />
                              {attachedAgent ? 'Auto-assign agent…' : 'Auto-assign to agent…'}
                            </button>
                          )}
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              setOpenGroupMenu(null);
                              const isMain = isMainGroupName(group);
                              if (window.confirm(
                                isMain
                                  ? `Are you sure you want to delete main group "${group}"? This will only remove the grouping (accounts stay in their leaf groups).`
                                  : `Are you sure you want to delete the group "${group}"? Accounts will be moved to ungrouped.`
                              )) {
                                try {
                                  if (isMain) {
                                    await api.post('/accounts/groups/delete-main', { name: group });
                                    toast.success(`Main group "${group}" deleted`);
                                  } else {
                                    await api.post('/accounts/delete-group', { groupName: group });
                                    toast.success(`Group "${group}" deleted`);
                                  }
                                  if (selectedGroup === group) setSelectedGroup('all');
                                  await fetchGroups();
                                  await fetchAccounts();
                                } catch (error) {
                                  toast.error('Failed to delete group');
                                  console.error('Error deleting group:', error);
                                }
                              }
                            }}
                            className="w-full px-3 py-2 text-sm text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                          >
                            <TrashIcon className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                    <GroupStatusSidebarFilters
                      selectedGroup={selectedGroup}
                      groupKey={group}
                      filterStatus={filterStatus}
                      counts={statusCountsForLeaves(leafGroups)}
                      onSelect={(name, status) => {
                        setSelectedGroup(name);
                        setFilterStatus(status);
                        setFilterModelId('all');
                        setFilterVpsAgentId('all');
                      }}
                    />
                  </div>
                );
              })}
              
              {/* Ungrouped tab - only show if there are ungrouped accounts */}
              {(accountSummary.ungroupedCount || 0) > 0 && (
                <div>
                <button
                  onClick={() => {
                    setSelectedGroup('ungrouped');
                    setFilterStatus('all');
                    setFilterModelId('all');
                    setFilterVpsAgentId('all');
                  }}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-between ${
                    selectedGroup === 'ungrouped'
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border border-dashed border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <span>Ungrouped</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500">({accountSummary.ungroupedCount})</span>
                </button>
                <GroupStatusSidebarFilters
                  selectedGroup={selectedGroup}
                  groupKey="ungrouped"
                  filterStatus={filterStatus}
                  counts={accountSummary.ungroupedStatusCounts}
                  onSelect={(name, status) => {
                    setSelectedGroup(name);
                    setFilterStatus(status);
                    setFilterModelId('all');
                    setFilterVpsAgentId('all');
                  }}
                />
                </div>
              )}
              
              {/* Add Group Button */}
              <button
                onClick={() => {
                  setEditingGroup({ name: '', isNew: true });
                  setNewGroupName('');
                  setShowGroupModal(true);
                }}
                className="w-full mt-1 px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-md hover:border-green-400 transition-colors flex items-center gap-1"
              >
                <FolderPlusIcon className="h-4 w-4" />
                Add Group
              </button>
                  </div>
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 px-1">
                    Tip: drag onto another group to merge; drop onto a main group to add as subgroup.
                  </div>
                </div>
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1 min-w-0">

          {/* Sub groups (folder view) */}
          {selectedGroup !== 'all' &&
            selectedGroup !== 'ungrouped' &&
            isMainGroupName(selectedGroup) && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <FolderIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Sub groups in {selectedGroup}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                  {(mainGroupsByName?.[selectedGroup]?.subGroups || []).length === 0 ? (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      No sub groups yet.
                    </div>
                  ) : (
                    (mainGroupsByName?.[selectedGroup]?.subGroups || []).map((subGroup) => {
                      const subLeafGroups = resolveLeafGroupsForGroupName(subGroup);
                      const subCount = sumLeafMemberCounts(subLeafGroups);
                      const isMain = isMainGroupName(subGroup);
                      return (
                        <div key={subGroup} className="relative group/subtab">
                          <button
                            onClick={() => setSelectedGroup(subGroup)}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('application/x-group-name', subGroup);
                              e.dataTransfer.effectAllowed = 'move';
                              setDraggingGroupName(subGroup);
                              setDragOverGroupName(null);
                            }}
                            onDragEnd={() => {
                              setDraggingGroupName(null);
                              setDragOverGroupName(null);
                            }}
                            onDragOver={(e) => {
                              if (!draggingGroupName) return;
                              if (draggingGroupName === subGroup) return;
                              e.preventDefault();
                              setDragOverGroupName(subGroup);
                            }}
                            onDrop={async (e) => {
                              e.preventDefault();
                              const sourceGroupName = e.dataTransfer.getData('application/x-group-name');
                              setDragOverGroupName(null);
                              setDraggingGroupName(null);

                              if (!sourceGroupName || sourceGroupName === subGroup) return;

                              // Dropping onto an existing main group => add as subgroup.
                              if (isMainGroupName(subGroup)) {
                                try {
                                  await api.post('/accounts/groups/add-subgroup', {
                                    mainGroupName: subGroup,
                                    subGroupName: sourceGroupName
                                  });
                                  toast.success(`Added "${sourceGroupName}" to "${subGroup}"`);
                                  await fetchGroups();
                                } catch (error) {
                                  toast.error('Failed to add subgroup');
                                  console.error('Error adding subgroup:', error);
                                }
                                return;
                              }

                              // Otherwise create a new main group using the two groups.
                              const suggestedName = `${sourceGroupName} + ${subGroup}`.trim();
                              setMainGroupModalDraft({
                                sourceGroup: sourceGroupName,
                                targetGroup: subGroup,
                                suggestedName,
                                name: suggestedName
                              });
                              setShowMainGroupModal(true);
                            }}
                            className={`px-3 py-1.5 text-sm font-medium rounded-xl transition-all flex items-center gap-2 ${
                              selectedGroup === subGroup
                                ? 'bg-primary-600 text-white shadow-sm'
                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                            } ${
                              dragOverGroupName === subGroup
                                ? 'ring-2 ring-primary-300 dark:ring-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                : ''
                            } ${draggingGroupName === subGroup ? 'opacity-70' : ''}`}
                          >
                            {subGroup} ({subCount})
                          </button>

                          {/* Edit/Delete dropdown on hover */}
                          <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-700 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 opacity-0 invisible group-hover/subtab:opacity-100 group-hover/subtab:visible transition-all z-10 min-w-[120px]">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingGroup({ name: subGroup, isNew: false, isMain: isMain });
                                setNewGroupName(subGroup);
                                setShowGroupModal(true);
                              }}
                              className="w-full px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2"
                            >
                              <PencilIcon className="h-4 w-4" />
                              Rename
                            </button>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (window.confirm(
                                  isMain
                                    ? `Are you sure you want to delete main group "${subGroup}"? This will only remove the grouping (accounts stay in their leaf groups).`
                                    : `Are you sure you want to delete the group "${subGroup}"? Accounts will be moved to ungrouped.`
                                )) {
                                  try {
                                    if (isMain) {
                                      await api.post('/accounts/groups/delete-main', { name: subGroup });
                                      toast.success(`Main group "${subGroup}" deleted`);
                                    } else {
                                      await api.post('/accounts/delete-group', { groupName: subGroup });
                                      toast.success(`Group "${subGroup}" deleted`);
                                    }

                                    if (selectedGroup === subGroup) setSelectedGroup('all');
                                    await fetchGroups();
                                    await fetchAccounts();
                                  } catch (error) {
                                    toast.error('Failed to delete group');
                                    console.error('Error deleting group:', error);
                                  }
                                }
                              }}
                              className="w-full px-3 py-2 text-sm text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                            >
                              <TrashIcon className="h-4 w-4" />
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

          {/* Accounts List */}
          <div className="space-y-4">
            {listRefreshing && accounts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-500 dark:text-gray-400">
                <div className="loading-spinner h-8 w-8 mb-3" />
                <p className="text-sm">Loading accounts…</p>
              </div>
            ) : (
              <div className={listRefreshing ? 'opacity-40 pointer-events-none transition-opacity duration-150' : ''}>
                {paginatedAccounts.map((account) => (
                  <AccountCard
                    key={account._id}
                    account={account}
                    clearStatusesKey={clearStatusesKey}
                    selected={selectedAccounts.some((id) => String(id) === String(account._id))}
                    models={models}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* Pagination Controls */}
          {(filteredAccounts.length > 0 || usePagination) && (
            <div className="mt-6 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700 dark:text-gray-300">Show:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                      if (usePagination || hasActiveServerFilters) {
                        fetchAccounts('soft', 1);
                      }
                    }}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="75">75</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                    <option value="300">300</option>
                    <option value="400">400</option>
                    <option value="500">500</option>
                  </select>
                  <span className="text-sm text-gray-500 dark:text-gray-400">per page</span>
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {usePagination ? ((currentPage - 1) * itemsPerPage + 1) : (startIndex + 1)} to {usePagination ? Math.min(currentPage * itemsPerPage, totalAccounts) : Math.min(endIndex, filteredAccounts.length)} of {usePagination ? totalAccounts : filteredAccounts.length} accounts
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, displayTotalPages) }, (_, i) => {
                    let pageNum;
                    if (displayTotalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= displayTotalPages - 2) {
                      pageNum = displayTotalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          currentPage === pageNum
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(displayTotalPages, prev + 1))}
                  disabled={currentPage === displayTotalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {!listRefreshing && filteredAccounts.length === 0 && accounts.length > 0 && (
            <div className="text-center py-12">
              <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-4 text-gray-500 dark:text-gray-400">No accounts match the selected filters</p>
              <button
                onClick={() => {
                  setFilterModelId('all');
                  setFilterVpsAgentId('all');
                  setFilterStatus('all');
                  setFilterAutomation('all');
                  setFilterProfile('all');
                  setFilterAgeOrder('low');
                  setSearchQuery('');
                  setSelectedGroup('all');
                }}
                className="mt-2 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                Clear filters
              </button>
            </div>
          )}

          {!listRefreshing && accounts.length === 0 && !loading && (
            hasActiveServerFilters || accountSummary.summaryScoped ? (
              <div className="text-center py-12">
                <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-4 text-gray-500 dark:text-gray-400">No accounts match the selected filters</p>
                <button
                  onClick={() => {
                    setFilterModelId('all');
                    setFilterVpsAgentId('all');
                    setFilterStatus('all');
                    setFilterAutomation('all');
                    setFilterProfile('all');
                    setFilterAgeOrder('low');
                    setSearchQuery('');
                    setSelectedGroup('all');
                  }}
                  className="mt-2 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  Clear filters
                </button>
              </div>
            ) : (
            <div className="text-center py-12">
              <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No accounts</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by adding your first X.com account.</p>
              {(user?.role === 'admin' || user?.features?.canAddAccounts) && (
                <div className="mt-6">
                  <button
                    onClick={() => setShowBulkModal(true)}
                    className={`inline-flex items-center px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40' 
                        : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                    }`}
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Account
                  </button>
                </div>
              )}
            </div>
            )
          )}
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      <UpdateAuthModal />
      <UpdateProxyModal />
      <BulkUpdateProxiesModal />
      <AddToVaModal />
      <BulkResetTlsModal />
      <StartAllBreakdownModal />
      
      <AccountModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleEditAccount}
        title="Edit Account"
      />

      {/* Bulk Add Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowBulkModal(false)}></div>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                      Bulk Add Accounts
                    </h3>
                    
                    <form onSubmit={handleBulkAddAccounts} className="space-y-6">
                      {/* Custom Format Builder */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Custom Format Builder
                          </label>
                          <div className="flex items-center gap-3">
                            <label className="text-xs text-gray-500 dark:text-gray-400">Delimiter:</label>
                            <select
                              value={customFormatDelimiter}
                              onChange={(e) => setCustomFormatDelimiter(e.target.value)}
                              className="form-select text-sm py-1 px-2"
                            >
                              <option value=":">:</option>
                              <option value="|">|</option>
                              <option value=",">,</option>
                              <option value=";">;</option>
                              <option value="\t">Tab</option>
                            </select>
                          </div>
                        </div>

                        {/* Drag and Drop Field Builder */}
                        <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50 mb-4">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Drag to reorder fields (click to toggle visibility)
                            </p>
                            <select
                              onChange={(e) => {
                                if (e.target.value) {
                                  addFieldToFormat(e.target.value);
                                  e.target.value = '';
                                }
                              }}
                              className="form-select text-xs py-1 px-2"
                              defaultValue=""
                            >
                              <option value="">+ Add Field</option>
                              {availableFields
                                .filter(f => !customFormatFields.find(cf => cf.id === f.id))
                                .map(field => (
                                  <option key={field.id} value={field.id}>
                                    {field.label}
                                  </option>
                                ))}
                            </select>
                          </div>

                          <div className="space-y-2 min-h-[120px]">
                            {customFormatFields.map((field, index) => (
                              <div
                                key={field.id}
                                draggable
                                onDragStart={(e) => handleDragStart(e, field.id)}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, index)}
                                className={`flex items-center gap-2 p-2 rounded border-2 transition-all cursor-move ${
                                  draggedField === field.id
                                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 opacity-50'
                                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-primary-400 dark:hover:border-primary-500'
                                }`}
                              >
                                <Bars3Icon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                <div className="flex-1 flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={field.enabled}
                                    onChange={() => toggleFieldEnabled(field.id)}
                                    className="form-checkbox"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <span className={`text-sm flex-1 ${field.enabled ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500 line-through'}`}>
                                    {field.label}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFieldFromFormat(field.id);
                                  }}
                                  className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                            {customFormatFields.length === 0 && (
                              <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
                                No fields added. Click "Add Field" to get started.
                              </div>
                            )}
                          </div>

                          {/* Format Preview */}
                          <div className="mt-4 p-3 bg-white dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Format Preview:</span>
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(getFormatPreview());
                                  toast.success('Format preview copied to clipboard');
                                }}
                                className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                              >
                                <ClipboardDocumentIcon className="h-3 w-3" />
                                Copy
                              </button>
                            </div>
                            <code className="text-xs text-gray-800 dark:text-gray-200 break-all">
                              {getFormatPreview()}
                            </code>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                              Proxy supports optional <span className="font-mono">proxy_auth</span> after password:{' '}
                              <span className="font-mono">host:port:user:pass:proxy_auth</span>. Keep Proxy last when delimiter is{' '}
                              <span className="font-mono">:</span>.
                            </p>
                          </div>
                        </div>

                        {/* Account Input */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Account Strings (one per line)
                            </label>
                            <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                              {getAccountCount()} account{getAccountCount() !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <textarea
                            value={customFormatAccounts}
                            onChange={(e) => setCustomFormatAccounts(e.target.value)}
                            className="form-textarea w-full h-32"
                            placeholder={`Enter accounts in the format shown above, one per line.\nExample:\n${getFormatPreview()}`}
                            required
                          />
                          <p className="text-sm text-gray-500 mt-1">
                            Enter accounts matching the format above (one account per line). Optional proxy_auth is saved on the account proxy.
                          </p>
                        </div>
                      </div>

                      {/* Add Proxies Separately Option */}
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="addProxiesSeparately"
                          checked={addProxiesSeparately}
                          onChange={(e) => setAddProxiesSeparately(e.target.checked)}
                          className="form-checkbox"
                        />
                        <label htmlFor="addProxiesSeparately" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                          Add proxies separately (not in account string)
                        </label>
                      </div>

                      {/* Auto assign proxies from Dashboard → Proxies (unused only, max per proxy) */}
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="autoAssignProxy"
                            checked={bulkFormData.autoAssignProxy || false}
                            onChange={(e) => setBulkFormData({ ...bulkFormData, autoAssignProxy: e.target.checked })}
                            className="form-checkbox"
                          />
                          <label htmlFor="autoAssignProxy" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            Auto assign proxies from Dashboard → Proxies
                          </label>
                        </div>
                        {bulkFormData.autoAssignProxy && (
                          <div className="ml-6 flex items-center gap-3 flex-wrap">
                            <label className="text-sm text-gray-600 dark:text-gray-400">Max accounts per proxy:</label>
                            <input
                              type="number"
                              min={1}
                              max={100}
                              value={bulkFormData.maxAccountsPerProxy ?? 4}
                              onChange={(e) => setBulkFormData({ ...bulkFormData, maxAccountsPerProxy: Math.max(1, parseInt(e.target.value, 10) || 4) })}
                              className="form-input w-24"
                            />
                            <span className="text-xs text-gray-500 dark:text-gray-400">Proxies at this cap are skipped (default 4)</span>
                          </div>
                        )}
                        {bulkFormData.autoAssignProxy && (
                          <div className="ml-6 flex items-start">
                            <input
                              type="checkbox"
                              id="autoAssignProxyRequireAuth"
                              checked={bulkFormData.autoAssignProxyRequireAuth || false}
                              onChange={(e) => setBulkFormData({ ...bulkFormData, autoAssignProxyRequireAuth: e.target.checked })}
                              className="form-checkbox mt-0.5"
                            />
                            <label htmlFor="autoAssignProxyRequireAuth" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                              Use only proxies with <span className="font-mono">proxy_auth</span>
                              <span className="block text-xs font-normal text-gray-500 dark:text-gray-400 mt-0.5">
                                Auto-assign will skip proxies without pool auth and skip any proxy that already reached the selected max accounts.
                              </span>
                            </label>
                          </div>
                        )}
                        {bulkFormData.autoAssignProxy && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 ml-6">
                            You do not need to add a proxy in the account string. Proxies are assigned from your list at /dashboard/proxies (only your own; admins see only their own).
                          </p>
                        )}
                        <div className="flex items-start">
                          <input
                            type="checkbox"
                            id="disableProxyPoolReplace"
                            checked={bulkFormData.disableProxyPoolReplace || false}
                            onChange={(e) => setBulkFormData({ ...bulkFormData, disableProxyPoolReplace: e.target.checked })}
                            className="form-checkbox mt-0.5"
                          />
                          <label htmlFor="disableProxyPoolReplace" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            Never auto-replace proxy from pool
                            <span className="block text-xs font-normal text-gray-500 dark:text-gray-400 mt-0.5">
                              Applies to every account in this upload. When enabled, connection failures will not swap this account to another dashboard proxy (login retry still retries the same proxy).
                            </span>
                          </label>
                        </div>
                      </div>

                      {/* Proxy Strings - Only show if "Add Proxies Separately" is checked and NOT auto-assign */}
                      {addProxiesSeparately && !bulkFormData.autoAssignProxy && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Proxy Strings (one per line) <span className="text-red-500">*</span>
                            </label>
                            <span className={`text-sm font-semibold ${
                              getProxyCount() === getAccountCount() 
                                ? 'text-green-600 dark:text-green-400' 
                                : 'text-red-600 dark:text-red-400'
                            }`}>
                              {getProxyCount()} proxy{getProxyCount() !== 1 ? 'ies' : ''}
                              {getProxyCount() !== getAccountCount() && (
                                <span className="ml-1">
                                  ({getProxyCount() < getAccountCount() ? '-' : '+'}{Math.abs(getProxyCount() - getAccountCount())})
                                </span>
                              )}
                            </span>
                          </div>
                          <textarea
                            value={bulkFormData.proxies}
                            onChange={(e) => setBulkFormData({...bulkFormData, proxies: e.target.value})}
                            className="form-textarea w-full h-24"
                            placeholder="IP:PORT:Username:Password:proxy_auth&#10;IP2:PORT2:Username2:Password2:proxy_auth"
                            required={addProxiesSeparately}
                          />
                          <p className="text-sm text-gray-500 mt-1">
                            Format: IP:PORT:Username:Password[:proxy_auth] (one proxy per line, matches account order — Account 1 uses Proxy 1, etc.). Optional 5th segment is stored as proxy_auth for pool tracking.
                            {getProxyCount() !== getAccountCount() && (
                              <span className="block text-red-600 dark:text-red-400 font-medium mt-1">
                                ⚠️ Proxy count must match account count ({getAccountCount()} accounts, {getProxyCount()} proxies)
                              </span>
                            )}
                          </p>
                        </div>
                      )}

                      {/* Proxy Protocol Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Proxy Protocol <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={bulkFormData.proxyProtocol || 'http'}
                          onChange={(e) => setBulkFormData({...bulkFormData, proxyProtocol: e.target.value})}
                          className="form-select w-full"
                          required
                        >
                          <option value="http">HTTP (Default)</option>
                          <option value="https">HTTPS</option>
                          <option value="socks5">SOCKS5</option>
                          <option value="socks4">SOCKS4</option>
                        </select>
                        <p className="text-sm text-gray-500 mt-1">
                          Select the proxy protocol. Note: SOCKS5/SOCKS4 may have limited support in Chrome/Puppeteer.
                        </p>
                      </div>

                      {/* User Agent */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          User Agent (applies to all accounts)
                        </label>
                        <input
                          type="text"
                          value={bulkFormData.userAgent}
                          onChange={(e) => setBulkFormData({...bulkFormData, userAgent: e.target.value})}
                          className="form-input w-full"
                          required
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          Browser user agent string for X.com login. This will be used for all accounts being added.
                        </p>
                      </div>

                      {/* Model Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Model Preset
                        </label>
                        <select
                          value={bulkFormData.modelId || ''}
                          onChange={(e) => setBulkFormData({...bulkFormData, modelId: e.target.value})}
                          className="form-select w-full"
                          required
                        >
                          <option value="">Select a model preset...</option>
                          {models.map((model) => {
                            const presetName = getPresetName(model.preset);
                            return (
                            <option key={model._id} value={model._id}>
                                {model.displayName || model.name}{model.displayName ? ` (${model.name})` : ''} - {model.age}y, {model.gender}{presetName ? ` • ${presetName}` : ''}{user?.role === 'admin' && model.userId?.username ? ` (${model.userId.username})` : ''}
                            </option>
                            );
                          })}
                        </select>
                        <p className="text-sm text-gray-500 mt-1">
                          Choose a model preset to inherit settings for all accounts
                        </p>
                      </div>

                      {/* Group Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Add to Group (Optional)
                        </label>
                        <div className="flex gap-2">
                          <select
                            value={bulkFormData.group || ''}
                            onChange={(e) => setBulkFormData({...bulkFormData, group: e.target.value})}
                            className="form-select flex-1"
                          >
                            <option value="">No group (ungrouped)</option>
                            {leafGroups.map((group) => (
                              <option key={group} value={group}>
                                {group}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => {
                              const groupName = window.prompt('Enter new group name:');
                              if (groupName && groupName.trim()) {
                                const newGroup = groupName.trim();
                                setLeafGroups(prev => [...new Set([...prev, newGroup])].sort());
                                setGroups(prev => [...new Set([...prev, newGroup])].sort());
                                setBulkFormData({...bulkFormData, group: newGroup});
                                toast.success(`Group "${newGroup}" will be created`);
                              }
                            }}
                            className="px-3 py-2 text-sm font-medium rounded-md bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/40 flex items-center gap-1"
                          >
                            <FolderPlusIcon className="h-4 w-4" />
                            New
                          </button>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Organize accounts into a group for easier management
                        </p>
                      </div>

                      {/* Scraper Tag Option - Only visible if user has canUseScraper feature */}
                      {user?.features?.canUseScraper && (
                        <div className="border-t pt-4 space-y-4">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="addScraperTag"
                              checked={bulkFormData.addScraperTag || false}
                              onChange={(e) => setBulkFormData({...bulkFormData, addScraperTag: e.target.checked})}
                              className="form-checkbox"
                            />
                            <label htmlFor="addScraperTag" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                              Add 'scraper' tag to all accounts
                            </label>
                          </div>
                          <p className="text-sm text-gray-500 mt-1 ml-6">
                            Tag all accounts with 'scraper' tag for use in scraping followers
                          </p>
                          
                          {(user?.role === 'admin' || user?.features?.canUseActiveCheckers) && (
                          <>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="addActiveCheckerTag"
                              checked={bulkFormData.addActiveCheckerTag || false}
                              onChange={(e) => setBulkFormData({...bulkFormData, addActiveCheckerTag: e.target.checked})}
                              className="form-checkbox"
                            />
                            <label htmlFor="addActiveCheckerTag" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                              Add 'active_checker' tag to all accounts
                            </label>
                          </div>
                          <p className="text-sm text-gray-500 mt-1 ml-6">
                            Tag all accounts with 'active_checker' tag for checking user activity in parallel
                          </p>
                          </>
                          )}
                          {(user?.role === 'admin' || user?.features?.canUseFollowBackCheckers) && (
                          <>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="addFollowBackCheckerTag"
                              checked={bulkFormData.addFollowBackCheckerTag || false}
                              onChange={(e) => setBulkFormData({...bulkFormData, addFollowBackCheckerTag: e.target.checked})}
                              className="form-checkbox"
                            />
                            <label htmlFor="addFollowBackCheckerTag" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                              Add 'follow_back_checker' tag to all accounts
                            </label>
                          </div>
                          <p className="text-sm text-gray-500 mt-1 ml-6">
                            Tag all accounts for follow-back automation scanning pool
                          </p>
                          </>
                          )}
                          {user?.role === 'admin' && (
                          <>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="addViewCheckerTag"
                              checked={bulkFormData.addViewCheckerTag || false}
                              onChange={(e) => setBulkFormData({...bulkFormData, addViewCheckerTag: e.target.checked})}
                              className="form-checkbox"
                            />
                            <label htmlFor="addViewCheckerTag" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                              Add 'view_checker' tag to all accounts
                            </label>
                          </div>
                          <p className="text-sm text-gray-500 mt-1 ml-6">
                            Tag all accounts for auto-comment view checking pool
                          </p>
                          </>
                          )}
                        </div>
                      )}

                      {/* Post-Upload Tasks - admin only */}
                      {user?.role === 'admin' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Post-Upload Tasks
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                          Select tasks to automatically execute after accounts are successfully uploaded. Tasks run sequentially with appropriate delays.
                        </p>
                        <div className="space-y-2 bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                          {/* Order matches execution: Privacy+DoB init → Bio Update → Extract Profile */}
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="task_privacy_check"
                              checked={bulkFormData.postUploadTasks?.privacy_check || false}
                              onChange={(e) => setBulkFormData({...bulkFormData, postUploadTasks: {...bulkFormData.postUploadTasks, privacy_check: e.target.checked}})}
                              className="form-checkbox"
                            />
                            <label htmlFor="task_privacy_check" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                              Privacy Check
                            </label>
                            <span className="ml-2 text-xs text-gray-400">Checks and unprotects private accounts</span>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="task_dob_phone_check"
                              checked={bulkFormData.postUploadTasks?.dob_phone_check || false}
                              onChange={(e) => setBulkFormData({...bulkFormData, postUploadTasks: {...bulkFormData.postUploadTasks, dob_phone_check: e.target.checked}})}
                              className="form-checkbox"
                            />
                            <label htmlFor="task_dob_phone_check" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                              DoB & Phone Check
                            </label>
                            <span className="ml-2 text-xs text-gray-400">Extracts date of birth and phone number</span>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="task_update_bio"
                              checked={bulkFormData.postUploadTasks?.update_bio || false}
                              onChange={(e) => setBulkFormData({...bulkFormData, postUploadTasks: {...bulkFormData.postUploadTasks, update_bio: e.target.checked}})}
                              className="form-checkbox"
                            />
                            <label htmlFor="task_update_bio" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                              Bio Update
                            </label>
                            <span className="ml-2 text-xs text-gray-400">Updates profile picture, banner, bio & name from model config</span>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="task_extract_profile"
                              checked={bulkFormData.postUploadTasks?.extract_profile || false}
                              onChange={(e) => setBulkFormData({...bulkFormData, postUploadTasks: {...bulkFormData.postUploadTasks, extract_profile: e.target.checked}})}
                              className="form-checkbox"
                            />
                            <label htmlFor="task_extract_profile" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                              Extract Profile
                            </label>
                            <span className="ml-2 text-xs text-gray-400">Extracts profile info (avatar, bio, etc.)</span>
                          </div>
                          {bulkFormData.postUploadTasks?.update_bio && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 ml-6">
                              Make sure you have configured Bio Update details in the selected model&apos;s &quot;Bio Update&quot; tab.
                            </p>
                          )}
                        </div>
                      </div>
                      )}

                      {/* Results Display */}
                      {(bulkAddRunning || bulkAddProgress.total > 0 || bulkAddProgress.items.length > 0) && (
                        <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-md font-medium text-gray-900 dark:text-gray-100">Login Test Progress</h4>
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-gray-600 dark:text-gray-300">
                                {bulkAddProgress.index}/{bulkAddProgress.total || '?'}
                                <span className="ml-2 text-xs opacity-90">
                                  ({bulkAddProgress.successCount || 0} ok, {bulkAddProgress.skippedCount || 0} skip, {bulkAddProgress.failCount || 0} fail)
                                </span>
                              </span>
                              {bulkAddProgress.currentAccount && bulkAddRunning && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[10rem]" title={bulkAddProgress.currentAccount}>
                                  @{bulkAddProgress.currentAccount}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={handleCancelBulkAdd}
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                                title="Clear progress"
                              >
                                <XCircleIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                          <progress className="w-full" value={bulkAddProgress.index} max={Math.max(1, bulkAddProgress.total || 1)} />
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            Duplicates are skipped automatically and counted under “skip” (not listed individually).
                          </p>
                          <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                            {bulkAddProgress.items.map((it, i) => (
                              <div key={i} className={`text-xs p-2 rounded ${
                                it.success 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' 
                                  : it.skipped
                                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                              }`}>
                                <div>
                                  <strong>{it.username || it.user || 'account'}:</strong> {
                                    it.success 
                                      ? 'Logged-in' 
                                      : it.skipped
                                      ? 'Skipped (duplicate)'
                                      : (it.error || 'login failed')
                                  }
                                  {!it.success && !it.skipped && it.missingCookies && (
                                    <div className="mt-1 text-xs opacity-90">
                                      Missing: {Array.isArray(it.missingCookies) ? it.missingCookies.join(', ') : it.missingCookies}
                                    </div>
                                  )}
                                  {!it.success && !it.skipped && it.availableCookies && (
                                    <div className="mt-1 text-xs opacity-75">
                                      Available: {it.availableCookies}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {bulkResults && (
                        <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-md font-medium text-gray-900 dark:text-gray-100">Results</h4>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 rounded text-sm ${
                                bulkResults.summary?.successRate >= 80 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' 
                                  : bulkResults.summary?.successRate >= 50
                                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                              }`}>
                                {bulkResults.summary?.successRate || 0}% Success Rate
                              </span>
                              {bulkResults.summary?.skipped > 0 && (
                                <span className="px-2 py-1 rounded text-sm bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
                                  {bulkResults.summary.skipped} Skipped
                                </span>
                              )}
                              {bulkResults.summary?.failed > 0 && (
                                <button
                                  type="button"
                                  onClick={handleRetryFailedLogins}
                                  disabled={bulkProcessing}
                                  className="btn btn-secondary btn-sm"
                                >
                                  <ArrowPathIcon className="h-4 w-4 mr-1" />
                                  Retry Failed ({bulkResults.summary.failed})
                                </button>
                              )}
                            </div>
                          </div>
                          
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {bulkResults.results.map((result, index) => {
                              const isSuccess = result.success || result.loginResult?.success;
                              const isSkipped = result.skipped;
                              const username = result.username || result.account?.username || (result.accountString ? result.accountString.split(':')[0] : 'unknown');
                              const hasError = result.error || (result.loginResult?.message && !isSuccess);
                              const message = result.error || result.loginResult?.message;
                              const is100PercentSuccess = bulkResults.summary?.successRate === 100;
                              
                              return (
                              <div key={index} className="flex items-center justify-between p-2 border rounded">
                                <div className="flex items-center space-x-3">
                                  {isSuccess ? (
                                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                                  ) : isSkipped ? (
                                    <XCircleIcon className="h-5 w-5 text-yellow-500" />
                                  ) : (
                                    <XCircleIcon className="h-5 w-5 text-red-500" />
                                  )}
                                  <div>
                                    <span className="font-medium text-gray-900 dark:text-gray-100">
                                      {username}
                                    </span>
                                    {(!is100PercentSuccess || hasError) && message && (
                                      <p className={`text-sm ${
                                        isSkipped
                                          ? 'text-yellow-600 dark:text-yellow-400'
                                          : (message?.includes('CRITICAL') || message?.includes('proxy'))
                                          ? 'text-red-600 font-medium dark:text-red-400' 
                                          : 'text-gray-500 dark:text-gray-400'
                                      }`}>
                                        {isSkipped ? 'Skipped (duplicate)' : message}
                                      </p>
                                    )}
                                    {!is100PercentSuccess && result.account?.proxy?.enabled && (
                                      <p className="text-xs text-blue-600">
                                        Using proxy: {result.account.proxy.proxyId}
                                      </p>
                                    )}
                                    {result.account?.sessionError && (
                                      <p className="text-xs text-red-600 font-medium">
                                        Session Error: {result.account.sessionError}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="text-sm text-gray-500">
                                  {result.loginResult?.method && (
                                    <span className="px-2 py-1 bg-gray-100 rounded">
                                      {result.loginResult.method}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex justify-end space-x-3 pt-4">
                        <button
                          type="button"
                          onClick={() => {
                            setShowBulkModal(false);
                            resetBulkForm();
                          }}
                          className="btn btn-secondary"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={bulkProcessing}
                          className={`inline-flex items-center px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                            isDarkMode 
                              ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40' 
                              : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                          } ${bulkProcessing ? 'opacity-60 cursor-not-allowed' : ''}`}
                        >
                          {bulkProcessing ? (
                            <>
                              <div className="loading-spinner h-4 w-4 mr-2"></div>
                              Processing...
                            </>
                          ) : (
                            <>
                              <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                              Add Accounts
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Proxy Validation Modal */}
      {showProxyValidationModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowProxyValidationModal(false)}></div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 sm:mx-0 sm:h-10 sm:w-10">
                    <XCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                      Proxy Count Mismatch
                    </h3>
                    <div className="mt-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        The number of proxy strings must match the number of account strings.
                      </p>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Accounts:</span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{proxyValidationData.accountCount}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Proxies:</span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{proxyValidationData.proxyCount}</span>
                        </div>
                        <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-red-600 dark:text-red-400">Missing Proxies:</span>
                            <span className="text-sm font-bold text-red-600 dark:text-red-400">{proxyValidationData.missing} account{proxyValidationData.missing !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-red-600 dark:text-red-400 font-medium mt-4">
                        Please add {proxyValidationData.missing} more proxy string{proxyValidationData.missing !== 1 ? 's' : ''} to match the account count.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setShowProxyValidationModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Group Modal - Create/Edit Group */}
      {showGroupModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowGroupModal(false)}></div>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 dark:bg-primary-900/20 sm:mx-0 sm:h-10 sm:w-10">
                    <FolderPlusIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                      {editingGroup?.isNew
                        ? 'Create New Group'
                        : editingGroup?.isMain
                          ? 'Rename Main Group'
                          : 'Rename Group'}
                    </h3>
                    <div className="mt-4">
                      <label htmlFor="groupName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Group Name
                      </label>
                      <input
                        type="text"
                        id="groupName"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        placeholder="Enter group name..."
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        autoFocus
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  disabled={!newGroupName.trim()}
                  onClick={async () => {
                    if (!newGroupName.trim()) return;
                    
                    try {
                      if (editingGroup?.isNew) {
                        // Creating new leaf group locally for convenience.
                        // Groups are persisted implicitly when accounts get assigned to them,
                        // but we keep the UI responsive.
                        const created = newGroupName.trim();
                        setLeafGroups(prev => [...new Set([...prev, created])].sort());
                        setGroups(prev => [...new Set([...prev, created])].sort());
                        toast.success(`Group "${created}" created`);
                      } else {
                        if (editingGroup?.isMain) {
                          // Rename main group
                          await api.post('/accounts/groups/rename-main', {
                            oldName: editingGroup.name,
                            newName: newGroupName.trim()
                          });
                          toast.success(`Main group renamed to "${newGroupName.trim()}"`);
                          if (selectedGroup === editingGroup.name) {
                            setSelectedGroup(newGroupName.trim());
                          }
                          await fetchGroups();
                          await fetchAccounts();
                        } else {
                          // Renaming existing leaf group
                          await api.post('/accounts/rename-group', {
                            oldName: editingGroup.name,
                            newName: newGroupName.trim()
                          });
                          toast.success(`Group renamed to "${newGroupName.trim()}"`);
                          if (selectedGroup === editingGroup.name) {
                            setSelectedGroup(newGroupName.trim());
                          }
                          await fetchGroups();
                          await fetchAccounts();
                        }
                      }
                      setShowGroupModal(false);
                      setEditingGroup(null);
                      setNewGroupName('');
                    } catch (error) {
                      toast.error(
                        editingGroup?.isNew
                          ? 'Failed to create group'
                          : editingGroup?.isMain
                            ? 'Failed to rename main group'
                            : 'Failed to rename group'
                      );
                      console.error('Error with group operation:', error);
                    }
                  }}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingGroup?.isNew
                    ? 'Create Group'
                    : editingGroup?.isMain
                      ? 'Rename Main'
                      : 'Rename'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowGroupModal(false);
                    setEditingGroup(null);
                    setNewGroupName('');
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preset Modal — configure or clear the group's automation preset.
          Drives the yellow "auto-runs preset" badge on the group tab and
          fires AccountUpdateFlow on every bulk-upload that lands new
          accounts in this group. Read-only view of the attached AI VA
          Agent (when any) — agent attach itself lives in the VA Agents
          dashboard. */}
      {presetModalOpen && presetModalGroup && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => {
                setPresetModalOpen(false);
                setPresetModalGroup(null);
              }}
            />

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <SparklesIcon className="h-5 w-5 text-yellow-500" />
                  Automation preset — <span className="font-mono">{presetModalGroup.name}</span>
                </h3>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Auto-fires an Update Accounts flow on every new account that lands in this group via bulk upload.
                </p>

                {presetModalGroup.attachedAgent && (
                  <div className="mt-3 px-3 py-2 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 text-xs text-blue-700 dark:text-blue-300">
                    Auto-assigns accounts to AI VA Agent:{' '}
                    <span className="font-semibold">{presetModalGroup.attachedAgent.name || presetModalGroup.attachedAgent.id}</span>
                    <div className="mt-1 text-blue-600/80 dark:text-blue-400/80">
                      Change via group tab menu → Auto-assign to agent…
                    </div>
                  </div>
                )}

                {/* Modules checklist */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Modules to run on every new account
                  </label>
                  <div className="space-y-2">
                    {[
                      { id: 'privacy_check', label: 'Privacy check' },
                      { id: 'dob_phone_check', label: 'Username | DoB | Phone' },
                      { id: 'fetch_username', label: 'Fetch username' },
                      { id: 'update_bio', label: 'Update bio (name, description, profile pic, banner)' },
                      { id: 'extract_profile', label: 'Extract profile snapshot' }
                    ].map((mod) => {
                      const existing = presetModalDraft.modules.find((m) => m.taskType === mod.id);
                      const checked = !!existing && existing.enabled !== false;
                      return (
                        <label key={mod.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setPresetModalDraft((prev) => {
                                const without = prev.modules.filter((m) => m.taskType !== mod.id);
                                if (e.target.checked) {
                                  const prior = prev.modules.find((m) => m.taskType === mod.id);
                                  const nextOrder = without.length;
                                  return {
                                    ...prev,
                                    modules: [...without, {
                                      taskType: mod.id,
                                      enabled: true,
                                      order: nextOrder,
                                      settings: prior?.settings || {}
                                    }]
                                  };
                                }
                                return { ...prev, modules: without };
                              });
                            }}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          {mod.label}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {presetModalDraft.modules.some((m) => m.taskType === 'dob_phone_check' && m.enabled !== false) && (
                  <div className="mt-4 p-3 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Username name prefixes
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      {USERNAME_PREFIX_HINT} Leave empty to only verify password and check/add DoB &amp; phone.
                    </p>
                    <textarea
                      value={
                        presetModalDraft.modules.find((m) => m.taskType === 'dob_phone_check')?.settings?.usernameNamePrefix || ''
                      }
                      onChange={(e) => {
                        const value = clampUsernamePrefixInput(e.target.value);
                        setPresetModalDraft((prev) => ({
                          ...prev,
                          modules: prev.modules.map((m) => (
                            m.taskType === 'dob_phone_check'
                              ? { ...m, settings: { ...(m.settings || {}), usernameNamePrefix: value } }
                              : m
                          ))
                        }));
                      }}
                      placeholder={'amy\namyb\namybl\namyblis\namybliss\namy_bliss'}
                      rows={6}
                      spellCheck={false}
                      className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-sm font-mono"
                    />
                    {exampleUsernamesFromPrefix(
                      presetModalDraft.modules.find((m) => m.taskType === 'dob_phone_check')?.settings?.usernameNamePrefix || ''
                    ).length > 0 && (
                      <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                        Examples:{' '}
                        {exampleUsernamesFromPrefix(
                          presetModalDraft.modules.find((m) => m.taskType === 'dob_phone_check')?.settings?.usernameNamePrefix
                        ).map((ex, i) => (
                          <span key={ex + i} className="font-mono text-blue-600 dark:text-blue-400 mr-2">@{ex}</span>
                        ))}
                      </p>
                    )}
                  </div>
                )}

                {/* Bio source — only shown when update_bio is enabled */}
                {presetModalDraft.modules.some((m) => m.taskType === 'update_bio' && m.enabled !== false) && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Bio data source
                    </label>
                    <div className="flex items-center gap-3 text-sm">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={presetModalDraft.bioSource === 'model'}
                          onChange={() => setPresetModalDraft((prev) => ({ ...prev, bioSource: 'model' }))}
                        />
                        From Model preset
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={presetModalDraft.bioSource === 'manual'}
                          onChange={() => setPresetModalDraft((prev) => ({ ...prev, bioSource: 'manual' }))}
                        />
                        Manual (configured in Update Accounts later)
                      </label>
                    </div>
                    {presetModalDraft.bioSource === 'model' && (
                      <>
                        <select
                          value={presetModalDraft.bioModelId}
                          onChange={(e) => setPresetModalDraft((prev) => ({ ...prev, bioModelId: e.target.value }))}
                          className="mt-2 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-sm"
                        >
                          <option value="">— pick a Model —</option>
                          {(models || []).map((m) => (
                            <option key={m._id} value={m._id}>{m.name}</option>
                          ))}
                        </select>
                        {!presetModalDraft.bioModelId && (
                          <p className="mt-1 text-xs text-red-500">
                            Pick a Model preset or switch to Manual — "Update bio" cannot run with no source.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Execution settings */}
                <div className="mt-4 p-3 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40 space-y-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Module order is randomized per account. Extract profile always runs after bio update.
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Concurrency (parallel accounts)
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={50}
                      value={presetModalDraft.concurrency}
                      onChange={(e) => setPresetModalDraft((prev) => ({
                        ...prev,
                        concurrency: Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 15))
                      }))}
                      className="block w-24 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Delay between steps (sec)
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={0}
                          value={presetModalDraft.delayBetweenStepsMin}
                          onChange={(e) => setPresetModalDraft((prev) => ({
                            ...prev,
                            delayBetweenStepsMin: parseFloat(e.target.value) || 0
                          }))}
                          className="block w-20 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-sm"
                        />
                        <span className="text-gray-400">–</span>
                        <input
                          type="number"
                          min={0}
                          value={presetModalDraft.delayBetweenStepsMax}
                          onChange={(e) => setPresetModalDraft((prev) => ({
                            ...prev,
                            delayBetweenStepsMax: parseFloat(e.target.value) || 0
                          }))}
                          className="block w-20 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Delay between accounts (sec)
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={0}
                          value={presetModalDraft.delayBetweenAccountsMin}
                          onChange={(e) => setPresetModalDraft((prev) => ({
                            ...prev,
                            delayBetweenAccountsMin: parseFloat(e.target.value) || 0
                          }))}
                          className="block w-20 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-sm"
                        />
                        <span className="text-gray-400">–</span>
                        <input
                          type="number"
                          min={0}
                          value={presetModalDraft.delayBetweenAccountsMax}
                          onChange={(e) => setPresetModalDraft((prev) => ({
                            ...prev,
                            delayBetweenAccountsMax: parseFloat(e.target.value) || 0
                          }))}
                          className="block w-20 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Move successful accounts to another group */}
                <div className="mt-4 p-3 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40 space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={presetModalDraft.moveToGroupOnSuccess}
                      onChange={(e) => setPresetModalDraft((prev) => ({
                        ...prev,
                        moveToGroupOnSuccess: e.target.checked,
                        moveToGroupName: e.target.checked ? prev.moveToGroupName : ''
                      }))}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    Move successful accounts to another group after preset completes
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Only accounts that fully complete all steps are moved. The destination group&apos;s auto-assign rules apply (e.g. VA agent → GoLogin import).
                  </p>
                  {presetModalDraft.moveToGroupOnSuccess && (
                    <select
                      value={presetModalDraft.moveToGroupName}
                      onChange={(e) => setPresetModalDraft((prev) => ({ ...prev, moveToGroupName: e.target.value }))}
                      className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-sm"
                    >
                      <option value="">— select target group —</option>
                      {leafGroups
                        .filter((g) => g !== presetModalGroup.name)
                        .map((g) => (
                          <option key={g} value={g}>{g}</option>
                        ))}
                    </select>
                  )}
                  {presetModalDraft.moveToGroupOnSuccess && !presetModalDraft.moveToGroupName && (
                    <p className="text-xs text-red-500">Select a target group or disable this option.</p>
                  )}
                </div>

                {/* Run-on-upload toggle + label */}
                <div className="mt-4 space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={presetModalDraft.runOnUpload}
                      onChange={(e) => setPresetModalDraft((prev) => ({ ...prev, runOnUpload: e.target.checked }))}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    Auto-fire on bulk upload
                  </label>
                  <input
                    type="text"
                    value={presetModalDraft.label}
                    onChange={(e) => setPresetModalDraft((prev) => ({ ...prev, label: e.target.value }))}
                    placeholder="Optional label (e.g. 'Standard onboard')"
                    maxLength={60}
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-sm"
                  />
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  disabled={
                    presetModalSubmitting
                    || presetModalDraft.modules.length === 0
                    || (
                      presetModalDraft.modules.some((m) => m.taskType === 'update_bio' && m.enabled !== false)
                      && presetModalDraft.bioSource === 'model'
                      && !presetModalDraft.bioModelId
                    )
                    || (
                      presetModalDraft.moveToGroupOnSuccess
                      && !presetModalDraft.moveToGroupName
                    )
                  }
                  onClick={async () => {
                    setPresetModalSubmitting(true);
                    try {
                      const automation = {
                        modules: presetModalDraft.modules,
                        bioSource: presetModalDraft.bioSource,
                        bioModelId: presetModalDraft.bioModelId || null,
                        randomizeOrder: true,
                        concurrency: presetModalDraft.concurrency,
                        delayBetweenStepsMin: presetModalDraft.delayBetweenStepsMin,
                        delayBetweenStepsMax: presetModalDraft.delayBetweenStepsMax,
                        delayBetweenAccountsMin: presetModalDraft.delayBetweenAccountsMin,
                        delayBetweenAccountsMax: presetModalDraft.delayBetweenAccountsMax,
                        moveToGroupOnSuccess: presetModalDraft.moveToGroupOnSuccess,
                        moveToGroupName: presetModalDraft.moveToGroupOnSuccess
                          ? presetModalDraft.moveToGroupName
                          : '',
                        runOnUpload: presetModalDraft.runOnUpload,
                        label: presetModalDraft.label
                      };
                      await api.patch(
                        `/accounts/groups/${encodeURIComponent(presetModalGroup.name)}/preset`,
                        { automation }
                      );
                      toast.success(`Preset saved on "${presetModalGroup.name}"`);
                      setPresetModalOpen(false);
                      setPresetModalGroup(null);
                      await fetchGroups();
                    } catch (err) {
                      toast.error(err.response?.data?.error || 'Failed to save preset');
                    } finally {
                      setPresetModalSubmitting(false);
                    }
                  }}
                  className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm disabled:opacity-50"
                >
                  Save preset
                </button>
                {presetModalGroup.automation && (
                  <button
                    type="button"
                    disabled={presetModalSubmitting}
                    onClick={async () => {
                      if (!window.confirm(`Clear the preset on "${presetModalGroup.name}"? Future uploads to this group will NOT auto-run anything.`)) return;
                      setPresetModalSubmitting(true);
                      try {
                        await api.patch(
                          `/accounts/groups/${encodeURIComponent(presetModalGroup.name)}/preset`,
                          { automation: null }
                        );
                        toast.success(`Preset cleared on "${presetModalGroup.name}"`);
                        setPresetModalOpen(false);
                        setPresetModalGroup(null);
                        await fetchGroups();
                      } catch (err) {
                        toast.error(err.response?.data?.error || 'Failed to clear preset');
                      } finally {
                        setPresetModalSubmitting(false);
                      }
                    }}
                    className="inline-flex justify-center rounded-md border border-red-300 dark:border-red-700 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 sm:text-sm disabled:opacity-50"
                  >
                    Clear
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setPresetModalOpen(false);
                    setPresetModalGroup(null);
                  }}
                  disabled={presetModalSubmitting}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auto-assign group → AI VA Agent (admin) */}
      {agentAssignModalOpen && agentAssignModalGroup && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => {
                if (agentAssignSubmitting) return;
                setAgentAssignModalOpen(false);
                setAgentAssignModalGroup(null);
              }}
            />

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <CpuChipIcon className="h-5 w-5 text-blue-500" />
                  Auto-assign — <span className="font-mono">{agentAssignModalGroup.name}</span>
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Attaches every account already in this group to the AI VA Agent and enables
                  auto-assign for all future bulk uploads that land here.
                </p>

                {agentAssignModalGroup.attachedAgent && (
                  <div className="mt-4 px-3 py-2 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 text-sm text-blue-800 dark:text-blue-200">
                    Currently auto-assigns to:{' '}
                    <span className="font-semibold">
                      {agentAssignModalGroup.attachedAgent.name || agentAssignModalGroup.attachedAgent.id}
                    </span>
                  </div>
                )}

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    AI VA Agent
                  </label>
                  {agentAssignLoadingAgents ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Loading agents…</p>
                  ) : (
                    <select
                      value={agentAssignSelectedId}
                      onChange={(e) => setAgentAssignSelectedId(e.target.value)}
                      className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-sm"
                      disabled={agentAssignSubmitting}
                    >
                      <option value="">— select an AI VA Agent —</option>
                      {aiAgents.map((agent) => (
                        <option key={agent._id} value={agent._id}>
                          {agent.name}
                          {agent.status ? ` (${agent.status})` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {!agentAssignLoadingAgents && aiAgents.length === 0 && (
                    <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                      No AI VA Agents found — create one under Admin → VAs → AI VA first.
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse sm:flex-wrap gap-2">
                <button
                  type="button"
                  disabled={agentAssignSubmitting || agentAssignLoadingAgents || !agentAssignSelectedId}
                  onClick={handleAttachGroupToAgent}
                  className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm disabled:opacity-50"
                >
                  {agentAssignSubmitting ? 'Saving…' : (agentAssignModalGroup.attachedAgent ? 'Update auto-assign' : 'Enable auto-assign')}
                </button>
                {agentAssignModalGroup.attachedAgent && (
                  <button
                    type="button"
                    disabled={agentAssignSubmitting}
                    onClick={handleDetachGroupFromAgent}
                    className="inline-flex justify-center rounded-md border border-red-300 dark:border-red-700 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 sm:text-sm disabled:opacity-50"
                  >
                    Stop auto-assign
                  </button>
                )}
                <button
                  type="button"
                  disabled={agentAssignSubmitting}
                  onClick={() => {
                    setAgentAssignModalOpen(false);
                    setAgentAssignModalGroup(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Group Modal - Create */}
      {showMainGroupModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => {
                setShowMainGroupModal(false);
                setMainGroupModalDraft({
                  sourceGroup: null,
                  targetGroup: null,
                  suggestedName: '',
                  name: ''
                });
              }}
            />

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 dark:bg-primary-900/20 sm:mx-0 sm:h-10 sm:w-10">
                    <FolderIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                  </div>

                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                      Create Main Group
                    </h3>

                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                      This main group will contain:
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {" "}
                        {mainGroupModalDraft?.sourceGroup}{" "}
                      </span>
                      {" "}
                      and
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {" "}
                        {mainGroupModalDraft?.targetGroup}
                      </span>
                    </p>

                    <div className="mt-4">
                      <label htmlFor="mainGroupName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Main Group Name
                      </label>
                      <input
                        type="text"
                        id="mainGroupName"
                        value={mainGroupModalDraft?.name || ''}
                        onChange={(e) =>
                          setMainGroupModalDraft(prev => ({
                            ...prev,
                            name: e.target.value
                          }))
                        }
                        placeholder="Enter main group name..."
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        autoFocus
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  disabled={!mainGroupModalDraft?.name?.trim()}
                  onClick={async () => {
                    const proposedName = mainGroupModalDraft?.name?.trim();
                    const sourceGroup = mainGroupModalDraft?.sourceGroup;
                    const targetGroup = mainGroupModalDraft?.targetGroup;

                    if (!proposedName || !sourceGroup || !targetGroup) return;

                    try {
                      await api.post('/accounts/groups/create-main', {
                        name: proposedName,
                        subGroups: [sourceGroup, targetGroup]
                      });
                      toast.success(`Main group "${proposedName}" created`);
                      await fetchGroups();
                      setSelectedGroup(proposedName);
                      setShowMainGroupModal(false);
                      setMainGroupModalDraft({
                        sourceGroup: null,
                        targetGroup: null,
                        suggestedName: '',
                        name: ''
                      });
                    } catch (error) {
                      toast.error('Failed to create main group');
                      console.error('Error creating main group:', error);
                    }
                  }}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Main Group
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowMainGroupModal(false);
                    setMainGroupModalDraft({
                      sourceGroup: null,
                      targetGroup: null,
                      suggestedName: '',
                      name: ''
                    });
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accounts;
